import { BackupRestore } from './components/admin/BackupRestore';
import { SystemAdmin } from './components/admin/SystemAdmin';
import { GoogleGenAI, Type } from "@google/genai";
import { fetchGooglePlaceData } from './services/googlePlacesService';
import { withRetry } from './services/apiUtils';
import { searchCircuitByAI, generatePackContent, analyzeActivitiesForClient, fetchAIDataForSite, getAiClient } from './services/geminiService';
import { generateCircuit, getDistance, getTravelTime } from './services/circuitEngine';
import { matrixService } from './services/matrixService';
import { storageService } from './services/storageService';
import { auth, googleProvider, createUserWithEmailAndPassword, signInWithEmailAndPassword, signInWithPopup, onAuthStateChanged, signOut } from './firebase';
import React, { useState, useEffect, useRef, useCallback, useMemo, Component, ErrorInfo, ReactNode } from 'react';

class ErrorBoundary extends Component<{ children: ReactNode, fallback: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode, fallback: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}
import { io, Socket } from 'socket.io-client';
import { 
  APIProvider, 
  Map, 
  AdvancedMarker, 
  Pin, 
  useMap, 
  useMapsLibrary,
  InfoWindow,
  useAdvancedMarkerRef
} from '@vis.gl/react-google-maps';
import { motion, AnimatePresence } from 'motion/react';

const SourceBadge = ({ source }: { source?: 'google' | 'ai' | 'manual' | 'photon' | 'ionos' }) => {
  if (!source) return null;
  const colors = {
    google: 'bg-blue-100 text-blue-700 border-blue-200',
    ai: 'bg-purple-100 text-purple-700 border-purple-200',
    manual: 'bg-amber-100 text-amber-700 border-amber-200',
    photon: 'bg-emerald-100 text-emerald-700 border-emerald-200'
  };
  const labels = {
    google: 'G',
    ai: 'AI',
    manual: 'M',
    photon: 'P'
  };
  return (
    <span className={cn("text-[8px] font-bold px-1 rounded border ml-1 uppercase inline-flex items-center justify-center min-w-[14px]", colors[source])} title={`Source: ${source}`}>
      {labels[source]}
    </span>
  );
};

const CheckeredFlag = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M4 2v20h2V2H4zm2 0h14v12H6V2zm2 2v2h2V4H8zm4 0v2h2V4h-2zm4 0v2h2V4h-2zM8 6v2h2V6H8zm4 0v2h2V6h-2zm4 0v2h2V6h-2zM8 8v2h2V8H8zm4 0v2h2V8h-2zm4 0v2h2V8h-2zM8 10v2h2v-2H8zm4 0v2h2v-2h-2zm4 0v2h2v-2h-2z" />
  </svg>
);

const NavButton = ({ 
  label, 
  onClick, 
  type = 'primary', 
  disabled = false, 
  icon: Icon,
  className
}: { 
  label: string, 
  onClick: () => void, 
  type?: 'primary' | 'secondary', 
  disabled?: boolean,
  icon?: any,
  className?: string
}) => {
  const DefaultIcon = type === 'primary' ? ChevronRight : ChevronLeft;
  const FinalIcon = Icon || DefaultIcon;
  
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "px-6 md:px-8 py-4 md:py-4 rounded-2xl font-bold transition-all flex items-center justify-center gap-2 w-full md:w-auto min-w-[160px] text-base md:text-lg",
        type === 'primary' 
          ? "bg-emerald-600 text-white hover:bg-emerald-700 shadow-lg shadow-emerald-600/20 disabled:opacity-50" 
          : "bg-white text-zinc-600 border border-zinc-200 hover:bg-zinc-50",
        className
      )}
    >
      {FinalIcon && <FinalIcon className="w-5 h-5" />}
      {label}
    </button>
  );
};

export const AISearchInput = ({ 
  initialPrompt, 
  initialLimit, 
  sector, 
  isSearching, 
  onSearch 
}: { 
  initialPrompt: string, 
  initialLimit: string, 
  sector: string, 
  isSearching: boolean, 
  onSearch: (prompt: string, limit: string) => void 
}) => {
  const [prompt, setPrompt] = useState(initialPrompt);
  const [limit, setLimit] = useState(initialLimit);

  useEffect(() => {
    setPrompt(initialPrompt);
  }, [initialPrompt]);

  useEffect(() => {
    setLimit(initialLimit);
  }, [initialLimit]);

  return (
    <div className="flex flex-col md:flex-row gap-3 w-full">
      <div className="w-full md:w-24 shrink-0">
        <label className="text-[10px] font-bold text-emerald-700 uppercase tracking-widest mb-1 block">Nb Résultats</label>
        <input 
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={limit || ''}
          onChange={(e) => {
            const val = e.target.value;
            if (val === '' || /^\d+$/.test(val)) {
              setLimit(val);
            }
          }}
          className="w-full p-3 bg-white border border-emerald-200 rounded-xl text-black focus:ring-2 focus:ring-emerald-500 outline-none transition-all text-sm placeholder:text-zinc-500"
        />
      </div>
      <input 
        type="text"
        value={prompt || ''}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder={`Décrivez votre circuit pour le secteur "${sector}"...`}
        className="flex-1 min-w-0 w-full p-3 bg-white border border-emerald-200 rounded-xl text-black placeholder:text-zinc-500 focus:ring-2 focus:ring-emerald-500 outline-none transition-all text-sm"
      />
      <button 
        onClick={() => onSearch(prompt, limit)}
        disabled={isSearching || !prompt.trim()}
        className="px-6 py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2 text-sm shrink-0"
      >
        {isSearching ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
        CHERCHER
      </button>
    </div>
  );
};
import { SiteEditModal } from './components/SiteEditModal';
import { 
  Car, 
  MapPin, 
  Navigation, 
  Clock, 
  Hourglass,
  Home,
  Phone, 
  Star, 
  ChevronRight, 
  ChevronLeft,
  ChevronDown,
  Compass,
  CheckCircle2,
  CheckCircle,
  AlertCircle,
  X,
  Settings as SettingsIcon,
  User as UserIcon,
  UserCheck,
  LogIn,
  FileText,
  Calendar,
  ShieldCheck,
  Check,
  Palette,
  Upload,
  Download,
  Image as ImageIcon,
  Accessibility,
  Ban,
  Route,
  Landmark,
  Trees,
  Mountain,
  Footprints,
  UtensilsCrossed,
  Sparkles,
  MousePointer2,
  Waves,
  Globe,
  Coffee,
  Bath,
  Tent,
  Apple,
  Flag,
  Play,
  Search,
  Mail,
  CreditCard,
  Zap,
  ShoppingBag,
  Package,
  Copy,
  MessageSquare,
  Mic,
  Send,
  Camera,
  Edit2,
  Pencil,
  RefreshCw,
  Trash2,
  Coins,
  ArrowRight,
  Users,
  Baby,
  Plus,
  Minus,
  Euro,
  Loader2,
  Timer,
  Info,
  ExternalLink,
  Share2,
  AlertTriangle,
  MoreVertical,
  LayoutDashboard,
  Gauge,
  Heart,
  Moon,
  PawPrint,
  Dog,
  Printer,
  Map as MapIcon,
  Utensils,
  Table,
  LogOut,
  Lock,
  Unlock,
} from 'lucide-react';
import { User, TouristSite, Settings, Booking, Location, Tour, Message, TourPack, TourQuestionnaire, AnalyzedActivity, AiSearchResult } from './types';
import { PhotonAutocompleteInput } from './components/PhotonAutocompleteInput';
import { geocodePhoton } from './services/photonService';
import { TouristCircuitTunnel, calculateLocalCost } from './components/TouristCircuitTunnel';
import { cn, safeSetItem } from './lib/utils';

const deg2rad = (deg: number) => deg * (Math.PI / 180);

const getDistanceLegacy = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  return getDistance({ lat: lat1, lng: lon1 }, { lat: lat2, lng: lon2 });
};

const HERO_IMAGES = [
  {
    id: 'cevennes-local',
    name: 'Cévennes Accueil',
    url: '/accueil-vtc.png'
  }
];

const translations = {
  fr: {
    home: "Accueil",
    booking: "Réservation",
    profile: "Profil",
    orders: "Commandes",
    admin: "Admin",
    driver: "Chauffeur",
    heroTitle: "Votre Chauffeur Privé en Cévennes",
    heroSubtitle: "Découvrez la beauté sauvage des Cévennes avec un service VTC premium. Confort, sécurité et authenticité pour tous vos déplacements.",
    bookNow: "Réserver maintenant",
    viewSites: "Sites touristiques",
    pickup: "Lieu de départ",
    destination: "Lieu d'arrivée",
    date: "Date",
    time: "Heure",
    passengers: "Passagers",
    price: "Prix",
    validate: "Valider",
    cancel: "Annuler",
    chat: "Chat en direct",
    call: "Appeler",
    status: "Statut",
    nightTariff: "Tarif de nuit",
    discount: "Remise",
    completed: "Terminé",
    inProgress: "En cours",
    approaching: "Arrive",
    arrived: "Sur place",
    accepted: "Accepté",
    pending: "En attente",
    immediateRide: "Course Immédiate",
    taxiVtcPro: "Taxi & VTC Pro",
    planTrip: "Planifiez votre trajet",
    touristSites: "Sites touristiques",
    exploreCevennes: "Explorez les Cévennes",
    customTour: "Circuit sur mesure",
    yourAdventure: "Votre aventure commence ici",
    safety: "Sécurité",
    safetySub: "Chauffeur certifié & discret",
    punctuality: "Ponctualité",
    punctualitySub: "Service 24/7 sur réservation",
    local: "Local",
    localSub: "Expert passionné du terroir",
    comfort: "Confort",
    comfortSub: "Véhicule entretenu",
    touristCircuits: "Circuits Touristiques",
    touristCircuitsSub: "Musées, Gastronomie, Activités & Panoramas",
    backToHome: "Retour à l'accueil",
    yourRide: "Votre Course",
    enterAddresses: "Saisissez vos adresses pour commander votre chauffeur.",
    rideStatus: "Statut de la course",
    waitingForDriver: "En attente du chauffeur...",
    driverOnWay: "Chauffeur en route !",
    driverApproaching: "Le chauffeur approche...",
    driverArrived: "Le chauffeur est arrivé !",
    rideInProgress: "Course en cours...",
    rideCompleted: "Course terminée",
    estimatedPrice: "Prix Estimé",
    pickupPoint: "Point de départ",
    destinationPoint: "Destination",
    geolocateMe: "Me géolocaliser",
    whereToPickup: "Où vous récupère-t-on ?",
    whereTo: "Où allez-vous ?",
    cancelBooking: "Annuler la réservation",
    acceptRide: "Accepter la course",
    startRide: "Démarrer",
    endRide: "Terminer la course"
  },
  en: {
    home: "Home",
    booking: "Booking",
    profile: "Profile",
    orders: "Orders",
    admin: "Admin",
    driver: "Driver",
    heroTitle: "Your Private Driver in Cévennes",
    heroSubtitle: "Your private transport in Cévennes. Simple, fast and professional.",
    bookNow: "Book Now",
    viewSites: "Tourist Sites",
    pickup: "Pickup location",
    destination: "Destination",
    date: "Date",
    time: "Time",
    passengers: "Passengers",
    price: "Price",
    validate: "Validate",
    cancel: "Cancel",
    chat: "Live Chat",
    call: "Call",
    status: "Status",
    nightTariff: "Night Tariff",
    discount: "Discount",
    completed: "Completed",
    inProgress: "In progress",
    approaching: "Approaching",
    arrived: "Arrived",
    accepted: "Accepted",
    pending: "Pending",
    immediateRide: "Immediate Ride",
    taxiVtcPro: "Taxi & VTC Pro",
    planTrip: "Plan your trip",
    touristSites: "Tourist Sites",
    exploreCevennes: "Explore the Cévennes",
    customTour: "Custom Tour",
    yourAdventure: "Your adventure starts here",
    safety: "Safety",
    safetySub: "Certified & discreet driver",
    punctuality: "Punctuality",
    punctualitySub: "24/7 service on reservation",
    local: "Local",
    localSub: "Passionate local expert",
    comfort: "Comfort",
    comfortSub: "Maintained vehicle",
    touristCircuits: "Tourist Circuits",
    touristCircuitsSub: "Museums, Gastronomy, Activities & Panoramas",
    backToHome: "Back to home",
    yourRide: "Your Ride",
    enterAddresses: "Enter your addresses to order your driver.",
    rideStatus: "Ride status",
    waitingForDriver: "Waiting for driver...",
    driverOnWay: "Driver on the way!",
    driverApproaching: "Driver is approaching...",
    driverArrived: "Driver has arrived!",
    rideInProgress: "Ride in progress...",
    rideCompleted: "Ride completed",
    estimatedPrice: "Estimated Price",
    pickupPoint: "Pickup point",
    destinationPoint: "Destination",
    geolocateMe: "Geolocate me",
    whereToPickup: "Where to pick you up?",
    whereTo: "Where are you going?",
    cancelBooking: "Cancel booking",
    acceptRide: "Accept ride",
    startRide: "Start",
    endRide: "End ride"
  },
  es: {
    home: "Inicio",
    booking: "Reserva",
    profile: "Perfil",
    orders: "Pedidos",
    admin: "Admin",
    driver: "Conductor",
    heroTitle: "Su Conductor Privado en Cévennes",
    heroSubtitle: "Su transporte privado en Cévennes. Simple, rápido y profesional.",
    bookNow: "Reservar ahora",
    viewSites: "Sitios Turísticos",
    pickup: "Lugar de salida",
    destination: "Lugar de llegada",
    date: "Fecha",
    time: "Hora",
    passengers: "Pasajeros",
    price: "Precio",
    validate: "Validar",
    cancel: "Cancelar",
    chat: "Chat en vivo",
    call: "Llamar",
    status: "Estado",
    nightTariff: "Tarifa nocturna",
    discount: "Descuento",
    completed: "Completado",
    inProgress: "En curso",
    approaching: "Llegando",
    arrived: "En el lugar",
    accepted: "Aceptado",
    pending: "Pendiente",
    immediateRide: "Viaje Inmediato",
    taxiVtcPro: "Taxi & VTC Pro",
    planTrip: "Planifique su viaje",
    touristSites: "Sitios Turísticos",
    exploreCevennes: "Explore las Cévennes",
    customTour: "Circuito a medida",
    yourAdventure: "Su aventura comienza aquí",
    safety: "Seguridad",
    safetySub: "Conductor certificado y discreto",
    punctuality: "Puntualidad",
    punctualitySub: "Servicio 24/7 bajo reserva",
    local: "Local",
    localSub: "Experto local apasionado",
    comfort: "Confort",
    comfortSub: "Vehículo mantenido",
    touristCircuits: "Circuitos Turísticos",
    touristCircuitsSub: "Museos, Gastronomía, Actividades y Panoramas",
    backToHome: "Volver al inicio",
    yourRide: "Su Viaje",
    enterAddresses: "Ingrese sus direcciones para pedir su conductor.",
    rideStatus: "Estado del viaje",
    waitingForDriver: "Esperando al conductor...",
    driverOnWay: "¡Conductor en camino!",
    driverApproaching: "El conductor se está acercando...",
    driverArrived: "¡El conductor ha llegado!",
    rideInProgress: "Viaje en curso...",
    rideCompleted: "Viaje completado",
    estimatedPrice: "Precio Estimado",
    pickupPoint: "Punto de partida",
    destinationPoint: "Destino",
    geolocateMe: "Geolocalizarme",
    whereToPickup: "¿Dónde lo recogemos?",
    whereTo: "¿A dónde va?",
    cancelBooking: "Cancelar reserva",
    acceptRide: "Aceptar viaje",
    startRide: "Iniciar",
    endRide: "Terminar viaje"
  },
  it: {
    home: "Home",
    booking: "Prenotazione",
    profile: "Profilo",
    orders: "Ordini",
    admin: "Admin",
    driver: "Autista",
    heroTitle: "Il Tuo Autista Privato nelle Cévennes",
    heroSubtitle: "Il tuo trasporto privato nelle Cévennes. Semplice, veloce e professionale.",
    bookNow: "Prenota ora",
    viewSites: "Siti Turistici",
    pickup: "Luogo di partenza",
    destination: "Luogo di arrivo",
    date: "Data",
    time: "Ora",
    passengers: "Passeggeri",
    price: "Prezzo",
    validate: "Conferma",
    cancel: "Annulla",
    chat: "Chat dal vivo",
    call: "Chiama",
    status: "Stato",
    nightTariff: "Tariffa notturna",
    discount: "Sconto",
    completed: "Completato",
    inProgress: "In corso",
    approaching: "In arrivo",
    arrived: "Sul posto",
    accepted: "Accettato",
    pending: "In attesa",
    immediateRide: "Corsa Immediata",
    taxiVtcPro: "Taxi & VTC Pro",
    planTrip: "Pianifica il tuo viaggio",
    touristSites: "Siti Turistici",
    exploreCevennes: "Esplora le Cévennes",
    customTour: "Tour su misura",
    yourAdventure: "La tua avventura inizia qui",
    safety: "Sicurezza",
    safetySub: "Autista certificato e discreto",
    punctuality: "Puntualità",
    punctualitySub: "Servizio 24/7 su prenotazione",
    local: "Locale",
    localSub: "Esperto locale appassionato",
    comfort: "Comfort",
    comfortSub: "Veicolo mantenuto",
    touristCircuits: "Circuiti Turistici",
    touristCircuitsSub: "Musei, Gastronomia, Attività e Panorami",
    backToHome: "Torna alla home",
    yourRide: "La Tua Corsa",
    enterAddresses: "Inserisci i tuoi indirizzi per ordinare il tuo autista.",
    rideStatus: "Stato della corsa",
    waitingForDriver: "In attesa dell'autista...",
    driverOnWay: "Autista in arrivo!",
    driverApproaching: "L'autista si sta avvicinando...",
    driverArrived: "L'autista è arrivato!",
    rideInProgress: "Corsa in corso...",
    rideCompleted: "Corsa completata",
    estimatedPrice: "Prezzo Stimato",
    pickupPoint: "Punto di partenza",
    destinationPoint: "Destinazione",
    geolocateMe: "Geolocalizzami",
    whereToPickup: "Dove ti veniamo a prendere?",
    whereTo: "Dove stai andando?",
    cancelBooking: "Annulla prenotazione",
    acceptRide: "Accetta corsa",
    startRide: "Inizia",
    endRide: "Termina corsa"
  },
  de: {
    home: "Startseite",
    booking: "Buchung",
    profile: "Profil",
    orders: "Bestellungen",
    admin: "Admin",
    driver: "Fahrer",
    heroTitle: "Ihr privater Fahrer in den Cevennen",
    heroSubtitle: "Ihr privater Transport in den Cevennen. Einfach, schnell und professionell.",
    bookNow: "Jetzt buchen",
    viewSites: "Sehenswürdigkeiten",
    pickup: "Abholort",
    destination: "Zielort",
    date: "Datum",
    time: "Uhrzeit",
    passengers: "Fahrgäste",
    price: "Preis",
    validate: "Bestätigen",
    cancel: "Abbrechen",
    chat: "Live-Chat",
    call: "Anrufen",
    status: "Status",
    nightTariff: "Nachttarif",
    discount: "Rabatt",
    completed: "Abgeschlossen",
    inProgress: "In Arbeit",
    approaching: "Nähert sich",
    arrived: "Angekommen",
    accepted: "Akzeptiert",
    pending: "Ausstehend",
    immediateRide: "Sofortige Fahrt",
    taxiVtcPro: "Taxi & VTC Pro",
    planTrip: "Planen Sie Ihre Reise",
    touristSites: "Sehenswürdigkeiten",
    exploreCevennes: "Erkunden Sie die Cevennen",
    customTour: "Maßgeschneiderte Tour",
    yourAdventure: "Ihr Abenteuer beginnt hier",
    safety: "Sicherheit",
    safetySub: "Zertifizierter & diskreter Fahrer",
    punctuality: "Pünktlichkeit",
    punctualitySub: "24/7 Service auf Reservierung",
    local: "Lokal",
    localSub: "Leidenschaftlicher lokaler Experte",
    comfort: "Komfort",
    comfortSub: "Gepflegtes Fahrzeug",
    touristCircuits: "Touristische Routen",
    touristCircuitsSub: "Museen, Gastronomie, Aktivitäten & Panoramen",
    backToHome: "Zurück zur Startseite",
    yourRide: "Ihre Fahrt",
    enterAddresses: "Geben Sie Ihre Adressen ein, um Ihren Fahrer zu bestellen.",
    rideStatus: "Fahrtstatus",
    waitingForDriver: "Warten auf den Fahrer...",
    driverOnWay: "Fahrer ist unterwegs!",
    driverApproaching: "Fahrer nähert sich...",
    driverArrived: "Fahrer ist angekommen!",
    rideInProgress: "Fahrt läuft...",
    rideCompleted: "Fahrt abgeschlossen",
    estimatedPrice: "Geschätzter Preis",
    pickupPoint: "Abholpunkt",
    destinationPoint: "Ziel",
    geolocateMe: "Mich orten",
    whereToPickup: "Wo sollen wir Sie abholen?",
    whereTo: "Wohin fahren Sie?",
    cancelBooking: "Buchung stornieren",
    acceptRide: "Fahrt akzeptieren",
    startRide: "Starten",
    endRide: "Fahrt beenden"
  },
  ru: {
    home: "Главная",
    booking: "Бронирование",
    profile: "Профиль",
    orders: "Заказы",
    admin: "Админ",
    driver: "Водитель",
    heroTitle: "Ваш частный водитель в Севеннах",
    heroSubtitle: "Ваш частный транспорт в Севеннах. Просто, быстро и профессионально.",
    bookNow: "Забронировать",
    viewSites: "Туристические места",
    pickup: "Место отправления",
    destination: "Место назначения",
    date: "Дата",
    time: "Время",
    passengers: "Пассажиры",
    price: "Цена",
    validate: "Подтвердить",
    cancel: "Отмена",
    chat: "Чат",
    call: "Позвонить",
    status: "Статус",
    nightTariff: "Ночной тариф",
    discount: "Скидка",
    completed: "Завершено",
    inProgress: "В процессе",
    approaching: "Подъезжает",
    arrived: "На месте",
    accepted: "Принято",
    pending: "В ожидании",
    immediateRide: "Срочная поездка",
    taxiVtcPro: "Такси и VTC Про",
    planTrip: "Спланируйте поездку",
    touristSites: "Туристические места",
    exploreCevennes: "Исследуйте Севенны",
    customTour: "Индивидуальный тур",
    yourAdventure: "Ваше приключение начинается здесь",
    safety: "Безопасность",
    safetySub: "Сертифицированный и скромный водитель",
    punctuality: "Пунктуальность",
    punctualitySub: "Круглосуточное обслуживание по бронированию",
    local: "Местный",
    localSub: "Страстный местный эксперт",
    comfort: "Комфорт",
    comfortSub: "Ухоженный автомобиль",
    touristCircuits: "Туристические маршруты",
    touristCircuitsSub: "Музеи, гастрономия, развлечения и панорамы",
    backToHome: "Вернуться на главную",
    yourRide: "Ваша поездка",
    enterAddresses: "Введите адреса, чтобы заказать водителя.",
    rideStatus: "Статус поездки",
    waitingForDriver: "Ожидание водителя...",
    driverOnWay: "Водитель в пути!",
    driverApproaching: "Водитель приближается...",
    driverArrived: "Водитель приехал!",
    rideInProgress: "Поездка в процессе...",
    rideCompleted: "Поездка завершена",
    estimatedPrice: "Оценочная стоимость",
    pickupPoint: "Точка отправления",
    destinationPoint: "Пункт назначения",
    geolocateMe: "Определить мое местоположение",
    whereToPickup: "Откуда вас забрать?",
    whereTo: "Куда вы едете?",
    cancelBooking: "Отменить бронирование",
    acceptRide: "Принять поездку",
    startRide: "Начать",
    endRide: "Завершить поездку"
  }
};

// --- Constants ---
const API_KEY = "AIzaSyB21zCCShXfmJzovHx4MIvKVgvAsUl-Jm8";
const hasValidKey = Boolean(API_KEY) && (API_KEY as string) !== 'YOUR_API_KEY';

const DEFAULT_CENTER = { lat: 43.923, lng: 3.847 }; // Cazilhac 34190

// --- Components ---

const ActivityDetailModal = ({ activity, onClose }: { activity: any, onClose: () => void }) => {
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[110] flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-[40px] shadow-2xl max-w-2xl w-full overflow-hidden flex flex-col max-h-[90vh]"
      >
        <div className="relative h-64 md:h-80 bg-zinc-100">
          {activity.photos && activity.photos.length > 0 ? (
            <div className="flex h-full overflow-x-auto snap-x snap-mandatory scrollbar-hide">
              {activity.photos.map((photo: string, i: number) => photo && (
                <img 
                  key={i}
                  src={photo} 
                  alt={`${activity.name} ${i + 1}`} 
                  className="w-full h-full object-cover flex-shrink-0 snap-center"
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&q=80&w=800'; // Fallback nature image
                  }}
                />
              ))}
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-zinc-300">
              <Camera className="w-12 h-12" />
            </div>
          )}
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 p-2 bg-black/20 hover:bg-black/40 backdrop-blur-md rounded-full text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="absolute bottom-4 left-4 right-4 flex justify-between items-end">
            <div className="bg-black/20 backdrop-blur-md px-4 py-2 rounded-2xl text-white flex flex-col gap-1">
              <h3 className="text-xl font-bold">{activity.name}</h3>
              <div className="flex items-center gap-2 flex-wrap">
                {activity.googleRating && (
                  <span className="bg-white/20 text-white text-[10px] px-2 py-1 rounded-lg font-bold flex items-center gap-1">
                    <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                    {activity.googleRating}/5 ({activity.googleReviewsCount || activity.userRatingsTotal || 0} avis)
                  </span>
                )}
                
                <div className="flex items-center gap-1.5 ml-1">
                  {activity.isHandicapAccessible && (
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-500/20 border border-blue-500/30" title="Accessible PMR">
                      <Accessibility className="w-3.5 h-3.5 text-blue-400" />
                    </div>
                  )}
                  
                  {activity.dogsNotAllowed && (
                    <div className="relative flex items-center justify-center w-6 h-6 rounded-full bg-red-500/20 border border-red-500/30" title="Chiens interdits">
                      <PawPrint className="w-3.5 h-3.5 text-red-400" />
                      <Ban className="w-5 h-5 text-red-500 absolute opacity-50" strokeWidth={1} />
                    </div>
                  )}

                  {activity.notSuitableForChildren && (
                    <div className="relative flex items-center justify-center w-6 h-6 rounded-full bg-red-500/20 border border-red-500/30" title="Inadapté aux enfants">
                      <Baby className="w-3.5 h-3.5 text-red-400" />
                      <Ban className="w-5 h-5 text-red-500 absolute opacity-50" strokeWidth={1} />
                    </div>
                  )}

                  {activity.isWindingRoad && (
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-amber-500/20 border border-amber-500/30" title="Route sinueuse">
                      <Route className="w-3.5 h-3.5 text-amber-400" />
                    </div>
                  )}
                </div>
              </div>
            </div>
            {activity.isGastronomy && (
              <div className="bg-amber-500 text-white px-3 py-1.5 rounded-xl font-bold flex items-center gap-2 shadow-lg">
                <span className="text-sm">{activity.forks === 0 ? 'Snack' : 'Restaurant'}</span>
              </div>
            )}
          </div>
        </div>

        <div className="p-8 overflow-y-auto space-y-6">
          <div className="space-y-4">
            <div className="flex justify-between items-start">
              <h4 className="text-lg font-bold text-zinc-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-500" />
                Description complète
              </h4>
            </div>
            {activity.categories && activity.categories.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {activity.categories.map((cat: string) => (
                  <span key={cat} className="bg-emerald-50 text-emerald-700 border border-emerald-100 px-2 py-1 rounded-lg text-xs font-bold uppercase tracking-wider">
                    {cat}
                  </span>
                ))}
              </div>
            )}
            <p className="text-zinc-600 leading-relaxed whitespace-pre-wrap">
              {activity.fullDescription || activity.description}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {!activity.isFree && (
              <div className="bg-zinc-50 p-6 rounded-3xl border border-zinc-100 space-y-3">
                <h4 className="font-bold text-zinc-900 flex items-center gap-2">
                  <Coins className="w-4 h-4 text-amber-500" />
                  Tarifs détaillés
                </h4>
                <p className="text-zinc-600 text-sm">
                  {activity.priceDetails || "Tarifs non communiqués"}
                </p>
                <div className="pt-2 border-t border-zinc-200">
                  <p className="text-xs text-zinc-400">Estimation totale pour votre groupe :</p>
                  <p className="text-lg font-bold text-emerald-600">{activity.estimatedCost}€</p>
                </div>
              </div>
            )}

            <div className="bg-zinc-50 p-6 rounded-3xl border border-zinc-100 space-y-3">
              <h4 className="font-bold text-zinc-900 flex items-center gap-2">
                <Info className="w-4 h-4 text-emerald-500" />
                Informations
              </h4>
              <div className="space-y-2">
                {activity.address && (
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-zinc-400 shrink-0 mt-0.5" />
                    <span className="text-zinc-600">{activity.address}</span>
                  </div>
                )}
                {activity.openingHours && (
                  <div className="flex items-start gap-2 text-sm">
                    <Clock className="w-4 h-4 text-zinc-400 shrink-0 mt-0.5" />
                    <span className="text-zinc-600">{activity.openingHours}</span>
                  </div>
                )}
                {activity.phone && (
                  <div className="flex items-start gap-2 text-sm">
                    <Phone className="w-4 h-4 text-zinc-400 shrink-0 mt-0.5" />
                    <span className="text-zinc-600">{activity.phone}</span>
                  </div>
                )}
                {activity.website && (
                  <div className="flex items-center gap-2 text-sm">
                    <Globe className="w-4 h-4 text-zinc-400 shrink-0" />
                    <a href={activity.website} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline truncate">
                      Site web
                    </a>
                  </div>
                )}
                {activity.conditions && (
                  <div className="flex items-start gap-2 text-sm">
                    <FileText className="w-4 h-4 text-zinc-400 shrink-0 mt-0.5" />
                    <span className="text-zinc-600"><strong>Conditions :</strong> {activity.conditions}</span>
                  </div>
                )}
                {activity.specialConditions && activity.specialConditions !== activity.conditions && (
                  <div className="flex items-start gap-2 text-sm">
                    <AlertTriangle className="w-4 h-4 text-zinc-400 shrink-0 mt-0.5" />
                    <span className="text-zinc-600"><strong>Conditions spécifiques :</strong> {activity.specialConditions}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <button
            onClick={onClose}
            className="w-full py-4 bg-zinc-900 text-white rounded-2xl font-bold hover:bg-zinc-800 transition-all"
          >
            Fermer la fiche
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const ConflictModal = ({ 
  serverItem, 
  localItem, 
  onResolve 
}: { 
  serverItem: any, 
  localItem: any, 
  onResolve: (choice: 'server' | 'local') => void 
}) => {
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-[32px] shadow-2xl max-w-lg w-full p-8 space-y-6"
      >
        <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto">
          <AlertCircle className="w-8 h-8 text-amber-600" />
        </div>
        
        <div className="text-center space-y-2">
          <h3 className="text-2xl font-bold text-zinc-900">Conflit de Synchronisation</h3>
          <p className="text-zinc-500">
            La fiche <strong>"{serverItem.name}"</strong> a été modifiée sur le serveur depuis votre dernière synchronisation.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-zinc-50 rounded-2xl border border-zinc-100 space-y-2">
            <p className="text-[10px] font-bold text-zinc-400 uppercase">Version Serveur</p>
            <p className="text-xs text-zinc-600 italic">
              Modifiée le : {new Date(serverItem.updated_at).toLocaleString()}
            </p>
            <button 
              onClick={() => onResolve('server')}
              className="w-full py-2 bg-zinc-200 hover:bg-zinc-300 text-zinc-700 rounded-xl text-sm font-bold transition-colors"
            >
              Récupérer
            </button>
          </div>
          
          <div className="p-4 bg-emerald-50 rounded-2xl border border-emerald-100 space-y-2">
            <p className="text-[10px] font-bold text-emerald-400 uppercase">Votre Version</p>
            <p className="text-xs text-emerald-600 italic">
              Modifiée le : {new Date(localItem.updated_at || Date.now()).toLocaleString()}
            </p>
            <button 
              onClick={() => onResolve('local')}
              className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-bold transition-colors"
            >
              Écraser
            </button>
          </div>
        </div>

        <p className="text-[10px] text-center text-zinc-400">
          L'écrasement remplacera les données du serveur par les vôtres. La récupération annulera vos modifications locales pour cette fiche.
        </p>
      </motion.div>
    </div>
  );
};

const SplashScreen = () => (
  <div className="flex flex-col items-center justify-center h-screen bg-zinc-900 p-6 font-sans relative overflow-hidden">
    <div className="absolute inset-0 z-0 bg-zinc-950">
      <div className="absolute inset-0 bg-gradient-to-b from-zinc-900/80 via-zinc-900/40 to-zinc-900" />
    </div>
    
    <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none z-1">
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500 rounded-full blur-[120px]" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-emerald-500 rounded-full blur-[120px]" />
    </div>
    
    <motion.div 
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="max-w-md w-full glass-card rounded-[48px] p-12 relative z-10 text-center"
    >
      <div className="w-24 h-24 bg-emerald-500 rounded-[32px] flex items-center justify-center mb-10 mx-auto shadow-2xl shadow-emerald-200">
        <MapPin className="text-white w-12 h-12" />
      </div>
      <h2 className="text-3xl font-display font-bold text-zinc-900 mb-4">Configuration Requise</h2>
      <p className="text-zinc-500 mb-10 leading-relaxed">
        Pour activer l'expérience immersive Céven'VTC, vous devez configurer votre clé API Google Maps.
      </p>
      
      <div className="space-y-6 text-left">
        {[
          { step: 1, text: "Obtenez une clé sur la Console Google Cloud" },
          { step: 2, text: "Ouvrez les Paramètres → Secrets" },
          { step: 3, text: "Ajoutez GOOGLE_MAPS_PLATFORM_KEY" }
        ].map((item) => (
          <div key={item.step} className="flex items-center gap-5">
            <div className="w-10 h-10 rounded-2xl bg-emerald-50 flex items-center justify-center flex-shrink-0 font-bold text-emerald-600 shadow-sm">
              {item.step}
            </div>
            <p className="text-zinc-700 font-medium text-sm">{item.text}</p>
          </div>
        ))}
      </div>
      
      <div className="mt-12 pt-8 border-t border-zinc-100">
        <p className="text-[10px] text-zinc-400 uppercase tracking-widest font-bold italic">
          Redémarrage automatique après configuration
        </p>
      </div>
    </motion.div>
  </div>
);

const Chat = ({ booking, user, onSendMessage }: { booking: Booking, user: User, onSendMessage: (msg: Message) => void }) => {
  const [text, setText] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [booking.messages]);

  const handleSend = () => {
    if (!text.trim()) return;
    const msg: Message = {
      id: Math.random().toString(36).substr(2, 9),
      senderId: user.id,
      senderName: `${user.firstName} ${user.lastName}`,
      text: text.trim(),
      timestamp: Date.now()
    };
    onSendMessage(msg);
    setText('');
  };

  const startVoiceInput = () => {
    setError(null);
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setError("La reconnaissance vocale n'est pas supportée.");
      return;
    }
    
    try {
      const recognition = new SpeechRecognition();
      recognition.lang = 'fr-FR';
      
      recognition.onstart = () => {
        setIsListening(true);
      };
      
      recognition.onend = () => {
        setIsListening(false);
      };
      
      recognition.onerror = (event: any) => {
        setIsListening(false);
        if (event.error === 'not-allowed') {
          setError("Microphone bloqué. Veuillez l'autoriser dans les paramètres de votre téléphone ou de votre navigateur.");
        } else {
          setError("Erreur de reconnaissance vocale.");
        }
        setTimeout(() => setError(null), 5000);
      };

      recognition.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        setText(prev => prev + ' ' + transcript);
      };
      recognition.start();
    } catch (err) {
      console.error('Error starting speech recognition:', err);
      setIsListening(false);
    }
  };

  return (
    <div className="flex flex-col h-[400px] bg-white rounded-3xl border border-zinc-100 shadow-xl overflow-hidden">
      <div className="p-4 border-b border-zinc-50 bg-zinc-50/50 flex items-center justify-between">
        <h3 className="font-bold text-zinc-900 flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-emerald-600" /> Chat en direct
        </h3>
        <div className="flex gap-2">
          <button 
            onClick={() => window.location.href = `tel:${booking.driver?.phone || '0600000000'}`}
            className="p-2 bg-emerald-50 text-emerald-600 rounded-xl hover:bg-emerald-100 transition-colors"
            title="Appeler le chauffeur"
          >
            <Phone className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4 scroll-smooth">
        {booking.messages?.map((msg) => (
          <div key={msg.id} className={cn("flex flex-col", msg.senderId === user.id ? "items-end" : "items-start")}>
            <div className={cn(
              "max-w-[80%] p-3 rounded-2xl text-sm shadow-sm",
              msg.senderId === user.id ? "bg-emerald-600 text-white rounded-tr-none" : "bg-zinc-100 text-zinc-900 rounded-tl-none"
            )}>
              {msg.text}
            </div>
            <span className="text-[10px] text-zinc-400 mt-1 px-1">
              {msg.senderName} • {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        ))}
        {(!booking.messages || booking.messages.length === 0) && (
          <div className="h-full flex items-center justify-center text-zinc-400 text-sm italic">
            Aucun message pour le moment
          </div>
        )}
      </div>
      <div className="p-4 bg-zinc-50 border-t border-zinc-100 flex flex-col gap-2">
        <div className="flex gap-2 w-full">
          <button 
            onClick={() => {}}
            className="p-2 text-zinc-400 hover:text-emerald-600 transition-colors"
            title="Envoyer une photo (Bientôt disponible)"
          >
            <Camera className="w-5 h-5" />
          </button>
          <button 
            onClick={startVoiceInput}
            className={cn(
              "p-2 transition-colors rounded-xl",
              isListening ? "bg-red-500 text-white animate-pulse" : "text-zinc-400 hover:text-emerald-600 hover:bg-emerald-50"
            )}
            title="Dictée vocale"
          >
            <Mic className="w-5 h-5" />
          </button>
          <input 
            type="text" 
            value={text || ''}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Votre message..."
            className="flex-1 bg-white border border-zinc-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
          />
          <button 
            onClick={handleSend}
            disabled={!text.trim()}
            className="p-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
        {error && (
          <span className="text-xs text-red-500 px-2 animate-in fade-in slide-in-from-top-1">{error}</span>
        )}
      </div>
    </div>
  );
};

const RouteDisplay = ({ 
  origin, 
  destination, 
  waypoints = [],
  strokeColor = '#10b981', 
  strokeWeight = 8, 
  strokeOpacity = 0.8,
  padding = { top: 80, right: 80, bottom: 320, left: 80 }
}: { 
  origin: Location, 
  destination: Location, 
  waypoints?: Location[],
  strokeColor?: string, 
  strokeWeight?: number, 
  strokeOpacity?: number,
  padding?: google.maps.Padding
}) => {
  const map = useMap();
  const routesLib = useMapsLibrary('routes');
  const polylinesRef = useRef<google.maps.Polyline[]>([]);

  useEffect(() => {
    if (!routesLib || !map || !origin?.lat || !origin?.lng || !destination?.lat || !destination?.lng) return;
    
    polylinesRef.current.forEach(p => p.setMap(null));

    const request: any = {
      origin: { lat: origin.lat, lng: origin.lng },
      destination: { lat: destination.lat, lng: destination.lng },
      travelMode: 'DRIVING',
      fields: ['path', 'viewport'],
    };

    if (waypoints && waypoints.length > 0) {
      request.intermediates = waypoints.map(wp => ({ location: { lat: wp.lat, lng: wp.lng } }));
    }

    (routesLib as any).Route.computeRoutes(request).then(({ routes }: any) => {
      if (routes?.[0]) {
        // Create a multi-layered glow effect for a "neon" look
        const outerGlow = routes[0].createPolylines({
          strokeColor,
          strokeWeight: strokeWeight + 10,
          strokeOpacity: 0.1
        });

        const innerGlow = routes[0].createPolylines({
          strokeColor,
          strokeWeight: strokeWeight + 4,
          strokeOpacity: 0.2
        });
        
        // Create the main path line
        const mainPolylines = routes[0].createPolylines({
          strokeColor,
          strokeWeight,
          strokeOpacity
        });

        const allPolylines = [...outerGlow, ...innerGlow, ...mainPolylines];
        allPolylines.forEach(p => p.setMap(map));
        polylinesRef.current = allPolylines;
        
        if (routes[0].viewport) {
          map.fitBounds(routes[0].viewport, padding);
        }
      }
    });

    return () => polylinesRef.current.forEach(p => p.setMap(null));
  }, [routesLib, map, origin, destination, strokeColor, strokeWeight, strokeOpacity, padding]);

  return null;
};

const MapAutoFit = ({ points }: { points: (Location | null | undefined)[] }) => {
  const map = useMap();
  
  useEffect(() => {
    if (!map || points.length === 0) return;
    
    const validPoints = points.filter((p): p is Location => !!p && typeof p.lat === 'number' && typeof p.lng === 'number');
    if (validPoints.length === 0) return;

    const bounds = new google.maps.LatLngBounds();
    validPoints.forEach(p => bounds.extend(p));
    
    map.fitBounds(bounds, { top: 100, right: 80, bottom: 320, left: 80 });
  }, [map, points]);

  return null;
};

export const TOUR_SECTORS = [
  "Vis et Larzac",
  "Anduze et Cévennes",
  "Gorges de l'Hérault",
  "Littoral et Camargue",
  "Vestiges Romains",
  "Massif de l'Aigoual"
];

const SITE_CATEGORIES = [
  { id: 'patrimoine', label: 'PATRIMOINE', icon: Landmark },
  { id: 'nature', label: 'BALADES EN NATURE', icon: Trees },
  { id: 'activites', label: 'ACTIVITÉ / SPORT', icon: Sparkles },
  { id: 'randonnee', label: 'RANDONNÉE', icon: Footprints },
  { id: 'points_de_vue', label: 'POINTS DE VUE', icon: Mountain },
  { id: 'terroir', label: 'PRODUITS DU TERROIR', icon: UtensilsCrossed },
  { id: 'restauration', label: 'RESTAURATION', icon: UtensilsCrossed },
  { id: 'hebergement', label: 'HÉBERGEMENT', icon: Home },
  { id: 'insolite', label: 'INSOLITE', icon: Star },
  { id: 'baignade', label: 'BAIGNADE', icon: Waves },
  { id: 'autre', label: 'AUTRE', icon: Coffee },
];

const getDefaultTourQuestionnaire = (): TourQuestionnaire => ({
  date: new Date().toISOString().split('T')[0],
  pickupLocation: null,
  accommodationLocation: null,
  departureTime: '09:00',
  hasAnimal: null,
  animalRace: '',
  animalSize: null,
  animalCount: 0,
  passengers: {
    seniors: 0,
    adults: 0,
    children6_12: 0,
    children3_6: 0,
    babies: 0,
  },
  preferences: {
    patrimoine: null,
    nature: null,
    activites: null,
    randonnee: null,
    points_de_vue: null,
    terroir: null,
    insolite: null,
    baignade: null,
    grotte: null,
    detente: null,
  },
  catering: {
    type: null,
  },
  accommodationPreference: null,
  sector: null,
  selectedActivities: [],
});

function DebouncedInput({ value, onChange, ...props }: any) {
  const [localValue, setLocalValue] = useState(value);
  
  useEffect(() => {
    const strValue = value === undefined || value === null ? '' : String(value);
    if (strValue !== localValue) {
      if (props.type === 'number') {
        const numValue = parseFloat(strValue);
        const numLocal = parseFloat(localValue);
        if (numValue !== numLocal && !(Number.isNaN(numValue) && Number.isNaN(numLocal)) && !(Number.isNaN(numValue) && localValue === '')) {
           setLocalValue(strValue);
        }
      } else {
        setLocalValue(strValue);
      }
    }
  }, [value, props.type]);

  return (
    <input
      {...props}
      value={localValue}
      onChange={(e) => {
        setLocalValue(e.target.value);
        onChange(e);
      }}
      onBlur={props.onBlur}
    />
  );
}

function DebouncedTextarea({ value, onChange, ...props }: any) {
  const [localValue, setLocalValue] = useState(value);
  
  useEffect(() => {
    const strValue = value === undefined || value === null ? '' : String(value);
    if (strValue !== localValue) {
      setLocalValue(strValue);
    }
  }, [value]);

  return (
    <textarea
      {...props}
      value={localValue}
      onChange={(e) => {
        setLocalValue(e.target.value);
        onChange(e);
      }}
      onBlur={props.onBlur}
    />
  );
}


function SiteAdminCard({ 
  site, 
  adminPackViewMode, 
  updatingSiteIds, 
  serverSiteIds,
  handleUpdateSingleSite, 
  setSiteToDelete, 
  onSave 
}: { 
  site: TouristSite; 
  adminPackViewMode: 'grid' | 'detail'; 
  updatingSiteIds: Set<string>; 
  serverSiteIds: Set<string>;
  handleUpdateSingleSite: (site: TouristSite) => void; 
  setSiteToDelete: (id: string) => void; 
  onSave: (site: TouristSite) => void; 
}) {
  const [localSite, setLocalSite] = useState<TouristSite>(site);
  const [isLocked, setIsLocked] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const { clientWidth } = scrollContainerRef.current;
      scrollContainerRef.current.scrollBy({ left: direction === 'left' ? -clientWidth : clientWidth, behavior: 'smooth' });
    }
  };

  const images = (localSite.images && localSite.images.length > 0 
    ? localSite.images.slice(0, 8) 
    : (localSite.image ? [localSite.image] : []))
    .filter(img => img && !img.includes('maps.googleapis.com/maps/api/place/js/PhotoService.GetPhoto'));

  useEffect(() => {
    setLocalSite(site);
  }, [site]);

  return (
                                  <div 
                                    key={localSite.id} 
                                    className={cn(
                                      "p-3 sm:p-4 rounded-xl border transition-all",
                                      adminPackViewMode === 'grid' ? "flex flex-col gap-3 sm:gap-4" : "flex flex-row gap-6",
                                      localSite.isVisibleClient 
                                        ? "bg-white border-purple-300 shadow-sm" 
                                        : "bg-white/50 border-purple-100 opacity-80"
                                    )}
                                  >
                                    <div className={cn("flex flex-col gap-3 w-full", adminPackViewMode === 'detail' && "flex-row gap-6")}>
                                      <div className={cn("relative group shrink-0", adminPackViewMode === 'grid' ? "w-full h-32 sm:h-40" : "w-64 h-64")}>
                                        {images.length > 0 ? (
                                          <div className="relative w-full h-full">
                                            <div className="w-full h-full rounded-lg overflow-hidden relative">
                                              <div 
                                                ref={scrollContainerRef}
                                                className="flex w-full h-full overflow-x-auto snap-x snap-mandatory scrollbar-hide"
                                                style={{ scrollSnapType: 'x mandatory' }}
                                              >
                                                {images.map((img, idx) => (
                                                  <div key={idx} className="relative w-full h-full flex-shrink-0 snap-center">
                                                    <img 
                                                      src={img} 
                                                      alt={`${localSite.name} ${idx + 1}`} 
                                                      className="w-full h-full object-cover" 
                                                      referrerPolicy="no-referrer" 
                                                      loading={idx === 0 ? "eager" : "lazy"}
                                                    />
                                                    {!isLocked && (
                                                      <label 
                                                        className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
                                                        title="Remplacer la photo"
                                                      >
                                                        <Upload className="w-6 h-6 text-white" />
                                                        <input 
                                                          type="file" 
                                                          accept="image/*" 
                                                          className="hidden" 
                                                          onChange={(e) => {
                                                            const file = e.target.files?.[0];
                                                            if (file) {
                                                              const reader = new FileReader();
                                                              reader.onloadend = () => {
                                                                const newImages = [...images];
                                                                newImages[idx] = reader.result as string;
                                                                setLocalSite(prev => ({ ...prev, images: newImages, image: newImages[0] }));
                                                              };
                                                              reader.readAsDataURL(file);
                                                            }
                                                          }} 
                                                        />
                                                      </label>
                                                    )}
                                                  </div>
                                                ))}
                                                {!isLocked && images.length < 8 && (
                                                  <div className="relative w-full h-full flex-shrink-0 snap-center bg-zinc-100 flex items-center justify-center">
                                                    <label 
                                                      className="absolute inset-0 flex flex-col items-center justify-center cursor-pointer hover:bg-zinc-200 transition-colors text-zinc-500"
                                                      title="Ajouter une photo"
                                                    >
                                                      <Plus className="w-8 h-8 mb-2" />
                                                      <span className="text-xs font-medium">Ajouter</span>
                                                      <input 
                                                        type="file" 
                                                        accept="image/*" 
                                                        className="hidden" 
                                                        onChange={(e) => {
                                                          const file = e.target.files?.[0];
                                                          if (file) {
                                                            const reader = new FileReader();
                                                            reader.onloadend = () => {
                                                              const newImages = [...images, reader.result as string];
                                                              setLocalSite(prev => ({ ...prev, images: newImages, image: newImages[0] }));
                                                              setTimeout(() => scroll('right'), 100);
                                                            };
                                                            reader.readAsDataURL(file);
                                                          }
                                                        }} 
                                                      />
                                                    </label>
                                                  </div>
                                                )}
                                              </div>
                                              {(images.length > 1 || (!isLocked && images.length < 8)) && (
                                                <>
                                                  <button 
                                                    onClick={(e) => { e.preventDefault(); scroll('left'); }}
                                                    className="absolute left-1 top-1/2 -translate-y-1/2 p-1 bg-black/50 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hidden sm:block z-10"
                                                  >
                                                    <ChevronLeft className="w-4 h-4" />
                                                  </button>
                                                  <button 
                                                    onClick={(e) => { e.preventDefault(); scroll('right'); }}
                                                    className="absolute right-1 top-1/2 -translate-y-1/2 p-1 bg-black/50 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hidden sm:block z-10"
                                                  >
                                                    <ChevronRight className="w-4 h-4" />
                                                  </button>
                                                </>
                                              )}
                                            </div>
                                            {!isLocked && (
                                              <button 
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  setLocalSite(prev => ({ ...prev, image: '', images: [] }));
                                                }}
                                                className="absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded-full shadow-lg hover:bg-red-600 transition-colors z-20"
                                                title="Supprimer toutes les photos"
                                              >
                                                <X className="w-3.5 h-3.5" />
                                              </button>
                                            )}
                                          </div>
                                        ) : (
                                          <div className="relative w-full h-full rounded-lg bg-purple-100 flex items-center justify-center text-purple-400">
                                            {(localSite.categories || []).includes('patrimoine') && <Landmark className="w-10 h-10" />}
                                            {(localSite.categories || []).includes('nature') && <Trees className="w-10 h-10" />}
                                            {(localSite.categories || []).includes('activites') && <Sparkles className="w-10 h-10" />}
                                            {(localSite.categories || []).includes('randonnee') && <Footprints className="w-10 h-10" />}
                                            {(localSite.categories || []).includes('points_de_vue') && <Mountain className="w-10 h-10" />}
                                            {(localSite.categories || []).includes('terroir') && <Coffee className="w-10 h-10" />}
                                            {(localSite.categories || []).includes('restauration') && <UtensilsCrossed className="w-10 h-10" />}
                                            {(localSite.categories || []).includes('insolite') && <Star className="w-10 h-10" />}
                                            {!(localSite.categories || []).some(c => ['patrimoine', 'nature', 'activites', 'randonnee', 'points_de_vue', 'terroir', 'restauration', 'insolite'].includes(c)) && <MapPin className="w-10 h-10" />}
                                            
                                            {!isLocked && (
                                              <label 
                                                className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity rounded-lg cursor-pointer"
                                                title="Ajouter une photo"
                                              >
                                                <Upload className="w-6 h-6 text-white" />
                                                <input 
                                                  type="file" 
                                                  accept="image/*" 
                                                  className="hidden" 
                                                  onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file) {
                                                      const reader = new FileReader();
                                                      reader.onloadend = () => {
                                                        setLocalSite(prev => ({ ...prev, image: reader.result as string, images: [reader.result as string] }));
                                                      };
                                                      reader.readAsDataURL(file);
                                                    }
                                                  }} 
                                                />
                                              </label>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                      
                                      <div className="flex flex-col flex-1 min-w-0 space-y-3 w-full">
                                        {serverSiteIds.has(localSite.id) && (
                                          <div className="bg-amber-50 border border-amber-200 p-2 rounded-lg mb-2 flex items-start gap-2">
                                            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                                            <div className="text-[10px] text-amber-800 font-medium leading-tight">
                                              <span className="font-bold uppercase">Règle de Publication Strict :</span> Ce site existe déjà sur le serveur. 
                                              Les modifications ne seront pas sauvegardées. 
                                              Utilisez l'admin du serveur pour modifier.
                                            </div>
                                          </div>
                                        )}
                                        <div className="flex justify-between items-start gap-2 w-full">
                                          <div className="flex-1 min-w-0">
                                            <DebouncedTextarea disabled={isLocked} 
                                              value={localSite.name || ''}
                                              rows={2}
                                              onChange={(e) => {
                                                setLocalSite(prev => ({ ...prev, name: e.target.value, sources: { ...prev.sources, name: 'manual' as 'manual' } }));
                                              }}
                                              className="font-bold text-zinc-900 text-sm w-full bg-transparent border-b border-transparent hover:border-zinc-300 focus:border-purple-500 focus:outline-none px-1 py-0.5 -ml-1 resize-none overflow-hidden"
                                              placeholder="Nom du site"
                                            />
                                            {localSite.isHandicapAccessible && (
                                              <span className="bg-blue-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold inline-flex items-center gap-1 shadow-sm mt-1" title="Accessible aux personnes à mobilité réduite">
                                                ♿ PMR
                                              </span>
                                            )}
                                            {!localSite.isFree && (!localSite.adultPrice && !localSite.child6_12Price && !localSite.child3_6Price && !localSite.babyPrice) && (
                                              <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 bg-orange-100 text-orange-700 text-xs font-bold rounded-lg border border-orange-200">
                                                <AlertTriangle className="w-3.5 h-3.5" />
                                                Mettre à jour les tarifs
                                              </div>
                                            )}
                                          </div>
                                          <div className="flex items-center gap-1 shrink-0 mt-1">
                                            <button 
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleUpdateSingleSite(site);
                                              }}
                                              disabled={updatingSiteIds.has(localSite.id)}
                                              className="p-1.5 text-zinc-400 hover:text-blue-500 transition-colors disabled:opacity-50"
                                              title="Actualiser les données Google & IA"
                                            >
                                              <RefreshCw className={cn("w-4 h-4", updatingSiteIds.has(localSite.id) && "animate-spin")} />
                                            </button>
                                            
                                            <div className="w-px h-4 bg-zinc-200 mx-2" />

                                            <button 
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setSiteToDelete(localSite.id);
                                              }}
                                              disabled={serverSiteIds.has(localSite.id)}
                                              className={cn(
                                                "p-1.5 transition-colors",
                                                serverSiteIds.has(localSite.id) ? "text-zinc-200 cursor-not-allowed" : "text-zinc-400 hover:text-red-500"
                                              )}
                                              title={serverSiteIds.has(localSite.id) ? "Suppression interdite (Existant)" : "Retirer du secteur"}
                                            >
                                              <Trash2 className="w-4 h-4" />
                                            </button>
                                          </div>
                                        </div>

                                        <div className="grid grid-cols-1 gap-1.5">
                                          <div className="flex items-center gap-2">
                                        <button
                                          onClick={() => setIsLocked(!isLocked)}
                                          className={cn(
                                            "p-2 rounded-lg transition-colors flex items-center gap-2 text-xs font-bold",
                                            isLocked ? "bg-zinc-100 text-zinc-500 hover:bg-zinc-200" : "bg-emerald-100 text-emerald-600 hover:bg-emerald-200"
                                          )}
                                          title={isLocked ? "Déverrouiller pour modifier" : "Verrouiller"}
                                        >
                                          {isLocked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                                          <span className="hidden sm:inline">{isLocked ? "VERROUILLÉ" : "DÉVERROUILLÉ"}</span>
                                        </button>
                                            <Phone className="w-3 h-3 text-emerald-600 shrink-0" />
                                            <DebouncedInput disabled={isLocked} 
                                              type="text"
                                              value={localSite.phone || ''}
                                              onChange={(e) => {
                                                setLocalSite(prev => ({ ...prev, phone: e.target.value }));
                                              }}
                                              className="text-[10px] text-emerald-600 font-bold bg-transparent border-b border-transparent hover:border-zinc-300 focus:border-emerald-500 focus:outline-none px-1 py-0.5 w-full"
                                              placeholder="Téléphone"
                                            />
                                          </div>
                                          {((localSite.categories || []).includes('restauration') || localSite.isCatering || localSite.cateringType === 'restaurant' || localSite.cateringType === 'snack') && (
                                            <div className="flex items-center gap-2">
                                              <UtensilsCrossed className="w-3 h-3 text-blue-600 shrink-0" />
                                              <select disabled={isLocked}
                                                value={localSite.forks?.toString() || '1'}
                                                onChange={(e) => {
                                                  const val = parseInt(e.target.value);
                                                  setLocalSite(prev => ({ ...prev,  
                                                    forks: val as 0 | 1 | 2 | 3,
                                                    cateringType: (val === 0 ? 'snack' : 'restaurant') as "none" | "pique-nique" | "snack" | "restaurant",
                                                    isCatering: true
                                                   }));
                                                }}
                                                className="text-[10px] text-blue-600 font-bold bg-transparent border-b border-transparent hover:border-zinc-300 focus:border-blue-500 focus:outline-none px-1 py-0.5 w-full"
                                              >
                                                <option value="0">0 (Snack)</option>
                                                <option value="1">1 fourchette</option>
                                                <option value="2">2 fourchettes</option>
                                                <option value="3">3 fourchettes</option>
                                              </select>
                                            </div>
                                          )}
                                          <div className="flex items-center gap-2">
                                            <Hourglass className="w-3 h-3 text-amber-600 shrink-0" />
                                            <DebouncedInput disabled={isLocked} 
                                              type="number"
                                              value={localSite.durationMinutes ?? ''}
                                              onChange={(e) => {
                                                const val = e.target.value === '' ? undefined : parseInt(e.target.value);
                                                setLocalSite(prev => ({ ...prev, durationMinutes: val }));
                                              }}
                                              className="text-[10px] text-amber-600 font-bold bg-transparent border-b border-transparent hover:border-zinc-300 focus:border-amber-500 focus:outline-none px-1 py-0.5 w-full"
                                              placeholder="Durée (min)"
                                            />
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <Globe className="w-3 h-3 text-blue-600 shrink-0" />
                                            <DebouncedInput disabled={isLocked} 
                                              type="text"
                                              value={localSite.website || ''}
                                              onChange={(e) => {
                                                setLocalSite(prev => ({ ...prev, website: e.target.value }));
                                              }}
                                              className="text-[10px] text-blue-600 font-bold bg-transparent border-b border-transparent hover:border-zinc-300 focus:border-blue-500 focus:outline-none px-1 py-0.5 w-full"
                                              placeholder="Site web (https://...)"
                                            />
                                          </div>
                                        </div>
                                        
                                        <div className="flex flex-wrap gap-1.5 sm:gap-2">
                                          <div className="flex flex-wrap gap-2 p-2 bg-zinc-50 border border-zinc-200 rounded-lg">
                                            {['patrimoine', 'nature', 'activites', 'randonnee', 'grotte', 'baignade', 'points_de_vue', 'terroir', 'restauration', 'hebergement', 'insolite', 'detente', 'autre'].map(cat => (
                                              <label key={cat} className="flex items-center gap-1 text-[10px] font-bold text-zinc-700 cursor-pointer">
                                                <input disabled={isLocked} type="checkbox"
                                                  checked={(localSite.categories || []).includes(cat)}
                                                  onChange={(e) => {
                                                    const currentCategories = localSite.categories || [];
                                                    const updatedCategories = e.target.checked
                                                      ? [...currentCategories, cat]
                                                      : currentCategories.filter(c => c !== cat);
                                                    
                                                    const isRestauration = updatedCategories.includes('restauration');
                                                    const isAccommodation = updatedCategories.includes('hebergement');
                                                    
                                                    setLocalSite(prev => ({ ...prev,  
                                                      categories: updatedCategories,
                                                      isFree: isRestauration ? false : prev.isFree,
                                                      cateringType: isRestauration && !prev.cateringType ? 'restaurant' : (!isRestauration ? undefined : prev.cateringType),
                                                      forks: isRestauration && prev.forks === undefined ? 1 : (!isRestauration ? undefined : prev.forks),
                                                      isCatering: isRestauration,
                                                      isAccommodation: isAccommodation
                                                     }));
                                                  }}
                                                  className="rounded border-zinc-300 text-purple-600 focus:ring-purple-500"
                                                />
                                                {cat.charAt(0).toUpperCase() + cat.slice(1).replace('_', ' ')}
                                              </label>
                                            ))}
                                          </div>
                                          
                                          <label className="flex items-center gap-1.5 text-xs font-bold text-zinc-600 cursor-pointer bg-zinc-50 px-2 py-1 rounded-lg border border-zinc-200">
                                            <input disabled={isLocked} type="checkbox"
                                              checked={localSite.isFree || false}
                                              onChange={(e) => {
                                                setLocalSite(prev => ({ ...prev, isFree: e.target.checked }));
                                              }}
                                              className="rounded border-zinc-300 text-purple-600 focus:ring-purple-500"
                                            />
                                            GRATUIT
                                          </label>
                                          
                                          <label className="flex items-center gap-1.5 text-xs font-bold text-zinc-600 cursor-pointer bg-zinc-50 px-2 py-1 rounded-lg border border-zinc-200">
                                            <input disabled={isLocked} type="checkbox"
                                              checked={localSite.dogsNotAllowed || false}
                                              onChange={(e) => {
                                                setLocalSite(prev => ({ ...prev, dogsNotAllowed: e.target.checked }));
                                              }}
                                              className="rounded border-zinc-300 text-purple-600 focus:ring-purple-500"
                                            />
                                            Chiens NON acceptés
                                          </label>
                                          
                                          <label className="flex items-center gap-1.5 text-xs font-bold text-zinc-600 cursor-pointer bg-zinc-50 px-2 py-1 rounded-lg border border-zinc-200">
                                            <input disabled={isLocked} type="checkbox"
                                              checked={localSite.isHandicapAccessible || false}
                                              onChange={(e) => {
                                                setLocalSite(prev => ({ ...prev, isHandicapAccessible: e.target.checked }));
                                              }}
                                              className="rounded border-zinc-300 text-purple-600 focus:ring-purple-500"
                                            />
                                            Accessibilité Handicapés
                                          </label>

                                          <label className="flex items-center gap-1.5 text-xs font-bold text-zinc-600 cursor-pointer bg-zinc-50 px-2 py-1 rounded-lg border border-zinc-200">
                                            <input disabled={isLocked} type="checkbox"
                                              checked={localSite.isWindingRoad || false}
                                              onChange={(e) => {
                                                setLocalSite(prev => ({ ...prev, isWindingRoad: e.target.checked }));
                                              }}
                                              className="rounded border-zinc-300 text-purple-600 focus:ring-purple-500"
                                            />
                                            Route Sinueuse ⚠️
                                          </label>

                                          <label className="flex items-center gap-1.5 text-xs font-bold text-zinc-600 cursor-pointer bg-zinc-50 px-2 py-1 rounded-lg border border-zinc-200">
                                            <input disabled={isLocked} type="checkbox"
                                              checked={localSite.notSuitableForChildren || false}
                                              onChange={(e) => {
                                                setLocalSite(prev => ({ ...prev, notSuitableForChildren: e.target.checked }));
                                              }}
                                              className="rounded border-zinc-300 text-purple-600 focus:ring-purple-500"
                                            />
                                            Inadapté Enfants 🚫
                                          </label>
                                        </div>

                                        <div className="grid grid-cols-1 min-[400px]:grid-cols-2 md:grid-cols-4 gap-2">
                                      <div>
                                        <label className="block text-[10px] font-bold text-zinc-500 uppercase mb-1">
                                          Adulte <SourceBadge source={localSite.sources?.adultPrice} />
                                        </label>
                                        <DebouncedInput disabled={isLocked} 
                                          type="number"
                                          value={localSite.adultPrice ?? ''}
                                          onChange={(e) => {
                                            const val = e.target.value === '' ? undefined : parseFloat(e.target.value);
                                            setLocalSite(prev => ({ ...prev,  
                                              adultPrice: val,
                                              isFree: val !== undefined && val > 0 ? false : prev.isFree,
                                              sources: { ...(prev.sources || {}), adultPrice: 'manual' } as any
                                             }));
                                          }}
                                          className="w-full p-2 text-sm bg-white border border-zinc-200 rounded-lg disabled:opacity-50 disabled:bg-zinc-100 text-zinc-900"
                                          placeholder="Prix €"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-[10px] font-bold text-zinc-500 uppercase mb-1">
                                          6-12 ans <SourceBadge source={localSite.sources?.child6_12Price} />
                                        </label>
                                        <DebouncedInput disabled={isLocked} 
                                          type="number"
                                          value={localSite.child6_12Price ?? ''}
                                          onChange={(e) => {
                                            const val = e.target.value === '' ? undefined : parseFloat(e.target.value);
                                            setLocalSite(prev => ({ ...prev,  
                                              child6_12Price: val,
                                              isFree: val > 0 ? false : prev.isFree,
                                              sources: { ...(prev.sources || {}), child6_12Price: 'manual' } as any
                                             }));
                                          }}
                                          className="w-full p-2 text-sm bg-white border border-zinc-200 rounded-lg disabled:opacity-50 disabled:bg-zinc-100 text-zinc-900"
                                          placeholder="Prix €"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-[10px] font-bold text-zinc-500 uppercase mb-1">
                                          3-6 ans <SourceBadge source={localSite.sources?.child3_6Price} />
                                        </label>
                                        <DebouncedInput disabled={isLocked} 
                                          type="number"
                                          value={localSite.child3_6Price ?? ''}
                                          onChange={(e) => {
                                            const val = e.target.value === '' ? undefined : parseFloat(e.target.value);
                                            setLocalSite(prev => ({ ...prev,  
                                              child3_6Price: val,
                                              isFree: val > 0 ? false : prev.isFree,
                                              sources: { ...(prev.sources || {}), child3_6Price: 'manual' } as any
                                             }));
                                          }}
                                          className="w-full p-2 text-sm bg-white border border-zinc-200 rounded-lg disabled:opacity-50 disabled:bg-zinc-100 text-zinc-900"
                                          placeholder="Prix €"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-[10px] font-bold text-zinc-500 uppercase mb-1">
                                          -3 ans <SourceBadge source={localSite.sources?.babyPrice} />
                                        </label>
                                        <DebouncedInput disabled={isLocked} 
                                          type="number"
                                          value={localSite.babyPrice ?? ''}
                                          onChange={(e) => {
                                            const val = e.target.value === '' ? undefined : parseFloat(e.target.value);
                                            setLocalSite(prev => ({ ...prev,  
                                              babyPrice: val,
                                              isFree: val > 0 ? false : prev.isFree,
                                              sources: { ...(prev.sources || {}), babyPrice: 'manual' } as any
                                             }));
                                          }}
                                          className="w-full p-2 text-sm bg-white border border-zinc-200 rounded-lg disabled:opacity-50 disabled:bg-zinc-100 text-zinc-900"
                                          placeholder="Prix €"
                                        />
                                      </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                                      <div>
                                        <label className="block text-[10px] font-bold text-zinc-500 uppercase mb-1">
                                          Description Courte <SourceBadge source={localSite.sources?.description} />
                                        </label>
                                        <DebouncedTextarea disabled={isLocked} 
                                          value={localSite.description || ''}
                                          onChange={(e) => {
                                            setLocalSite(prev => ({ ...prev,  
                                              description: e.target.value,
                                              sources: { ...(prev.sources || {}), description: 'manual' } as any
                                             }));
                                          }}
                                          className="w-full p-2 text-xs bg-white border border-zinc-200 rounded-lg h-16 text-zinc-900"
                                          placeholder="Description courte..."
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-[10px] font-bold text-zinc-500 uppercase mb-1">
                                          Description Détaillée <SourceBadge source={localSite.sources?.fullDescription} />
                                        </label>
                                        <DebouncedTextarea disabled={isLocked} 
                                          value={localSite.fullDescription || ''}
                                          onChange={(e) => {
                                            setLocalSite(prev => ({ ...prev,  
                                              fullDescription: e.target.value,
                                              sources: { ...(prev.sources || {}), fullDescription: 'manual' } as any
                                             }));
                                          }}
                                          className={cn("w-full p-2 text-sm bg-white border border-zinc-200 rounded-lg text-zinc-900", adminPackViewMode === 'detail' ? 'h-96' : 'h-16')}
                                          placeholder="Description détaillée..."
                                        />
                                      </div>
                                      <div className="col-span-1 sm:col-span-2">
                                        <label className="block text-[10px] font-bold text-zinc-500 uppercase mb-1">
                                          Adresse Exacte <SourceBadge source={localSite.sources?.address} />
                                        </label>
                                        <PhotonAutocompleteInput 
                                          disabled={isLocked}
                                          placeholder="Adresse..."
                                          defaultValue={localSite.address || ''}
                                          onPlaceSelect={(loc) => {
                                            setLocalSite(prev => ({ ...prev,  
                                              address: loc.address, 
                                              gps: `${loc.lat}, ${loc.lng}`,
                                              sources: { 
                                                ...(prev.sources || {}), 
                                                address: 'manual' as const,
                                                gps: 'manual' as const
                                              }
                                             }));
                                          }}
                                          inputClassName={cn("p-2 text-sm bg-white border border-zinc-200 rounded-lg text-zinc-900 w-full", adminPackViewMode === 'detail' ? 'h-12' : 'h-10')}
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-[10px] font-bold text-zinc-500 uppercase mb-1">
                                          Coordonnées GPS <SourceBadge source={localSite.sources?.gps} />
                                        </label>
                                        <input disabled={isLocked} type="text"
                                          value={localSite.gps || ''}
                                          onChange={(e) => {
                                            setLocalSite(prev => ({ ...prev,  
                                              gps: e.target.value,
                                              sources: { ...(prev.sources || {}), gps: 'manual' } as any
                                             }));
                                          }}
                                          className={cn("w-full p-2 text-sm bg-white border border-zinc-200 rounded-lg text-zinc-900", adminPackViewMode === 'detail' ? 'h-12' : 'h-10')}
                                          placeholder="Lat, Lng"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-[10px] font-bold text-zinc-500 uppercase mb-1">
                                          Téléphone <SourceBadge source={localSite.sources?.phone} />
                                        </label>
                                        <input disabled={isLocked} type="text"
                                          value={localSite.phone || ''}
                                          onChange={(e) => {
                                            setLocalSite(prev => ({ ...prev,  
                                              phone: e.target.value,
                                              sources: { ...(prev.sources || {}), phone: 'manual' } as any
                                             }));
                                          }}
                                          className={cn("w-full p-2 text-sm bg-white border border-zinc-200 rounded-lg text-zinc-900", adminPackViewMode === 'detail' ? 'h-12' : 'h-10')}
                                          placeholder="+33..."
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-[10px] font-bold text-zinc-500 uppercase mb-1">
                                          Site Internet <SourceBadge source={localSite.sources?.website} />
                                        </label>
                                        <input disabled={isLocked} type="text"
                                          value={localSite.website || ''}
                                          onChange={(e) => {
                                            setLocalSite(prev => ({ ...prev,  
                                              website: e.target.value,
                                              sources: { ...(prev.sources || {}), website: 'manual' } as any
                                             }));
                                          }}
                                          className={cn("w-full p-2 text-sm bg-white border border-zinc-200 rounded-lg text-zinc-900", adminPackViewMode === 'detail' ? 'h-12' : 'h-10')}
                                          placeholder="https://..."
                                        />
                                        <label className="block text-[10px] font-bold text-zinc-500 uppercase mb-1 mt-2">
                                          Horaires & Saisons <SourceBadge source={localSite.sources?.openingHours} />
                                        </label>
                                        <input disabled={isLocked} type="text"
                                          value={localSite.openingHours || ''}
                                          onChange={(e) => {
                                            setLocalSite(prev => ({ ...prev,  
                                              openingHours: e.target.value,
                                              sources: { ...(prev.sources || {}), openingHours: 'manual' } as any
                                             }));
                                          }}
                                          className={cn("w-full p-2 text-sm bg-white border border-zinc-200 rounded-lg text-zinc-900", adminPackViewMode === 'detail' ? 'h-12' : 'h-10')}
                                          placeholder="Horaires..."
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-[10px] font-bold text-zinc-500 uppercase mb-1">
                                          Conditions Spécifiques <SourceBadge source={localSite.sources?.conditions} />
                                        </label>
                                        <textarea 
                                          value={localSite.conditions || ''}
                                          onChange={(e) => {
                                            setLocalSite(prev => ({ ...prev,  
                                              conditions: e.target.value,
                                              sources: { ...(prev.sources || {}), conditions: 'manual' } as any
                                             }));
                                          }}
                                          className={cn("w-full p-2 text-sm bg-white border border-zinc-200 rounded-lg text-zinc-900", adminPackViewMode === 'detail' ? 'h-32' : 'h-16')}
                                          placeholder="Conditions..."
                                        />
                                      </div>
                                      <div className="flex flex-col min-[400px]:flex-row gap-2">
                                        <div className="flex-1">
                                          <label className="block text-[10px] font-bold text-zinc-500 uppercase mb-1">
                                            Note Google <SourceBadge source={localSite.sources?.googleRating} />
                                          </label>
                                          <DebouncedInput disabled={isLocked} 
                                            type="number"
                                            step="0.1"
                                            value={Number.isNaN(localSite.googleRating) ? '' : (localSite.googleRating ?? '')}
                                            onChange={(e) => {
                                              const val = e.target.value === '' ? undefined : parseFloat(e.target.value);
                                              setLocalSite(prev => ({ ...prev,  
                                                googleRating: val,
                                                sources: { ...(prev.sources || {}), googleRating: 'manual' } as any
                                               }));
                                            }}
                                            className="w-full p-2 text-xs bg-white border border-zinc-200 rounded-lg text-zinc-900"
                                            placeholder="Ex: 4.5"
                                          />
                                        </div>
                                        <div className="flex-1">
                                          <label className="block text-[10px] font-bold text-zinc-500 uppercase mb-1">
                                            Avis Google <SourceBadge source={localSite.sources?.googleReviewsCount} />
                                          </label>
                                          <DebouncedInput disabled={isLocked} 
                                            type="number"
                                            value={Number.isNaN(localSite.googleReviewsCount) ? '' : (localSite.googleReviewsCount ?? '')}
                                            onChange={(e) => {
                                              const val = e.target.value === '' ? undefined : parseInt(e.target.value, 10);
                                              setLocalSite(prev => ({ ...prev,  
                                                googleReviewsCount: val,
                                                sources: { ...(prev.sources || {}), googleReviewsCount: 'manual' } as any
                                               }));
                                            }}
                                            className="w-full p-2 text-xs bg-white border border-zinc-200 rounded-lg text-zinc-900"
                                            placeholder="Ex: 120"
                                          />
                                        </div>
                                        <div className="flex-1">
                                          <label className="block text-[10px] font-bold text-zinc-500 uppercase mb-1">
                                            Durée (min) <SourceBadge source={localSite.sources?.durationMinutes} />
                                          </label>
                                          <DebouncedInput disabled={isLocked} 
                                            type="number"
                                            value={localSite.durationMinutes ?? ''}
                                            onChange={(e) => {
                                              const val = e.target.value === '' ? undefined : parseInt(e.target.value, 10);
                                              setLocalSite(prev => ({ ...prev,  
                                                durationMinutes: val,
                                                sources: { ...(prev.sources || {}), durationMinutes: 'manual' } as any
                                               }));
                                            }}
                                            className="w-full p-2 text-xs bg-white border border-zinc-200 rounded-lg text-zinc-900"
                                            placeholder="Ex: 60"
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  <div className="w-full space-y-3">
                                      <div className="pt-3 border-t border-purple-100 flex justify-between items-center">
                                        <label className="flex items-center gap-2 text-sm font-bold text-amber-600 cursor-pointer" title="Utiliser cette photo pour illustrer le pack">
                                          <input disabled={isLocked} type="checkbox"
                                            checked={localSite.isEmblematic || false}
                                            onChange={(e) => {
                                              setLocalSite(prev => ({ ...prev, isEmblematic: e.target.checked }));
                                            }}
                                            className="w-4 h-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                                          />
                                          SITE EMBLÉMATIQUE
                                        </label>
                                        <label className="flex items-center gap-2 text-sm font-bold text-purple-700 cursor-pointer">
                                          <input disabled={isLocked} type="checkbox"
                                            checked={localSite.isVisibleClient || false}
                                            onChange={(e) => {
                                              setLocalSite(prev => ({ ...prev, isVisibleClient: e.target.checked }));
                                            }}
                                            className="w-4 h-4 rounded border-purple-300 text-purple-600 focus:ring-purple-500"
                                          />
                                          VISIBLE CLIENT
                                        </label>
                                      </div>

                                      <div className="pt-3 border-t border-purple-100">
                                        <button
                                          disabled={isLocked || serverSiteIds.has(localSite.id)}
                                          onClick={() => {
                                            onSave(localSite);
                                            setIsLocked(true);
                                          }}
                                          className={cn(
                                            "w-full py-2 rounded-lg font-bold text-xs transition-colors flex items-center justify-center gap-2",
                                            (isLocked || serverSiteIds.has(localSite.id)) 
                                              ? "bg-zinc-100 text-zinc-400 cursor-not-allowed border border-zinc-200" 
                                              : "bg-purple-600 text-white hover:bg-purple-700 shadow-md shadow-purple-600/20"
                                          )}
                                        >
                                          <CheckCircle className="w-4 h-4" />
                                          {serverSiteIds.has(localSite.id) ? "MODIFICATION INTERDITE (EXISTANT)" : "ENREGISTRER LES MODIFICATIONS"}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
  );
}


export default function App() {



  const [lang, setLang] = useState<'fr' | 'en' | 'es' | 'it' | 'de' | 'ru'>(() => {
    const saved = localStorage.getItem('vtc_lang');
    if (saved && ['fr', 'en', 'es', 'it', 'de', 'ru'].includes(saved)) return saved as any;
    return 'fr';
  });
  const [isLangOpen, setIsLangOpen] = useState(false);

  useEffect(() => {
    const savedLang = localStorage.getItem('vtc_lang');
    if (!savedLang) {
      const browserLang = navigator.language.split('-')[0];
      const supportedLangs = ['fr', 'en', 'es', 'it', 'de', 'ru'];
      if (supportedLangs.includes(browserLang)) {
        setLang(browserLang as any);
        safeSetItem('vtc_lang', browserLang);
      } else {
        setLang('en');
        safeSetItem('vtc_lang', 'en');
      }
    }
  }, []);

  const handleSetLang = (newLang: 'fr' | 'en' | 'es' | 'it' | 'de' | 'ru') => {
    setLang(newLang);
    safeSetItem('vtc_lang', newLang);
    setIsLangOpen(false);
  };

  const flags = {
    fr: '🇫🇷',
    en: '🇬🇧',
    es: '🇪🇸',
    it: '🇮🇹',
    de: '🇩🇪',
    ru: '🇷🇺'
  };

  const t = (key: keyof typeof translations['fr']) => {
    return translations[lang][key] || translations['fr'][key];
  };

  const [socket, setSocket] = useState<Socket | null>(null);
  const [view, setView] = useState<'home' | 'vtc' | 'tours' | 'status' | 'admin' | 'custom_tour' | 'profile' | 'profile_edit' | 'orders' | 'contact' | 'legal' | 'cgv' | 'driver_dashboard' | 'tour_confirmation'>(() => {
    const hash = window.location.hash.replace('#', '');
    const validViews = ['home', 'vtc', 'tours', 'status', 'admin', 'custom_tour', 'profile', 'profile_edit', 'orders', 'contact', 'legal', 'cgv', 'driver_dashboard', 'tour_confirmation'];
    if (hash && validViews.includes(hash)) {
      return hash as any;
    }
    const saved = localStorage.getItem('vtc_view');
    return (saved as any) || 'home';
  });
  /**
   * Admin Tab State
   * Determines which section of the Admin panel is currently visible.
   * Persisted in localStorage under 'vtc_admin_tab'.
   */
  const [adminTab, setAdminTab] = useState<'settings' | 'sites' | 'customers' | 'orders' | 'design' | 'packs' | 'backup' | 'system'>(() => {
    const saved = localStorage.getItem('vtc_admin_tab');
    return (saved as any) || 'settings';
  });
  const [adminPackViewMode, setAdminPackViewMode] = useState<'grid' | 'detail'>('grid');
  const [selectedCustomer, setSelectedCustomer] = useState<User | null>(null);
  const [customerSearchLastName, setCustomerSearchLastName] = useState('');
  const [customerSearchFirstName, setCustomerSearchFirstName] = useState('');

  const isInitialMount = useRef(true);

  useEffect(() => {
    const currentHash = window.location.hash.replace('#', '');
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (currentHash !== view) {
        window.history.replaceState({ view }, '', `#${view}`);
      } else {
        window.history.replaceState({ view }, '', window.location.href);
      }
    } else if (currentHash !== view) {
      window.history.pushState({ view }, '', `#${view}`);
    }
    safeSetItem('vtc_view', view);
  }, [view]);

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (event.state && event.state.view) {
        setView(event.state.view);
      } else {
        const hashView = window.location.hash.replace('#', '');
        const validViews = ['home', 'vtc', 'tours', 'status', 'admin', 'custom_tour', 'profile', 'profile_edit', 'orders', 'contact', 'legal', 'cgv', 'driver_dashboard', 'tour_confirmation'];
        if (hashView && validViews.includes(hashView)) {
          setView(hashView as any);
        } else {
          setView('home');
        }
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    safeSetItem('vtc_admin_tab', adminTab);
  }, [adminTab]);
  const [tourStep, setTourStep] = useState<number>(() => {
    const saved = localStorage.getItem('vtc_tour_step_v2');
    return saved ? parseInt(saved) : 1;
  });
  const [selectedPack, setSelectedPack] = useState<TourPack | null>(null);
  const [tourQuestionnaire, setTourQuestionnaire] = useState<TourQuestionnaire>(() => {
    const saved = localStorage.getItem('vtc_tour_questionnaire');
    const savedTimestamp = localStorage.getItem('vtc_tour_questionnaire_timestamp');
    const defaultData = getDefaultTourQuestionnaire();

    if (saved && savedTimestamp) {
      const now = Date.now();
      const twoHours = 2 * 60 * 60 * 1000;
      if (now - parseInt(savedTimestamp) < twoHours) {
        try {
          const parsed = JSON.parse(saved);
          return {
            ...defaultData,
            ...parsed,
            passengers: {
              ...defaultData.passengers,
              ...(parsed.passengers || {})
            },
            preferences: {
              ...defaultData.preferences,
              ...(parsed.preferences || {})
            },
            catering: {
              ...defaultData.catering,
              ...(parsed.catering || {})
            }
          };
        } catch (e) {
          return defaultData;
        }
      }
    }
    return defaultData;
  });

  useEffect(() => {
    safeSetItem('vtc_tour_questionnaire', JSON.stringify(tourQuestionnaire));
    safeSetItem('vtc_tour_questionnaire_timestamp', Date.now().toString());
  }, [tourQuestionnaire]);

  const [analyzedActivities, setAnalyzedActivities] = useState<AnalyzedActivity[]>(() => {
    const saved = localStorage.getItem('vtc_analyzed_activities');
    try {
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      console.error("Error parsing analyzedActivities", e);
      return [];
    }
  });
  const [isAnalyzingActivities, setIsAnalyzingActivities] = useState(false);
  const [selectedAnalyzedActivity, setSelectedAnalyzedActivity] = useState<AnalyzedActivity | null>(null);
  const [selectedSectorForDetail, setSelectedSectorForDetail] = useState<string | null>(null);
  const [interestClickOrder, setInterestClickOrder] = useState<string[]>([]);
  const [selectedAnalyzedActivityIds, setSelectedAnalyzedActivityIds] = useState<string[]>(() => {
    const saved = localStorage.getItem('vtc_selected_analyzed_activity_ids');
    try {
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      console.error("Error parsing selectedAnalyzedActivityIds", e);
      return [];
    }
  });
  const [prioritizedActivityIds, setPrioritizedActivityIds] = useState<string[]>(() => {
    const saved = localStorage.getItem('vtc_prioritized_activity_ids');
    try {
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      console.error("Error parsing prioritizedActivityIds", e);
      return [];
    }
  });
  const [overflowOption, setOverflowOption] = useState<'multi_day' | 'priority' | null>(() => {
    const saved = localStorage.getItem('vtc_overflow_option');
    try {
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      console.error("Error parsing overflowOption", e);
      return null;
    }
  });

  const [cartOrderNumber, setCartOrderNumber] = useState<string | null>(() => {
    const saved = localStorage.getItem('vtc_cart_order_number');
    return saved ? saved : null;
  });
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [showDistanceMatrix, setShowDistanceMatrix] = useState(false);
  const [isUpdatingMatrix, setIsUpdatingMatrix] = useState(false);
  const [matrixUpdateProgress, setMatrixUpdateProgress] = useState({ current: 0, total: 0 });

  const updateCartTimestamp = () => {
    safeSetItem('vtc_cart_last_updated', Date.now().toString());
  };

  useEffect(() => {
    if (cartOrderNumber) {
      safeSetItem('vtc_cart_order_number', cartOrderNumber);
    } else {
      localStorage.removeItem('vtc_cart_order_number');
    }
  }, [cartOrderNumber]);

  useEffect(() => {
    safeSetItem('vtc_tour_step_v2', tourStep.toString());
    updateCartTimestamp();
  }, [tourStep]);

  useEffect(() => {
    safeSetItem('vtc_tour_questionnaire', JSON.stringify(tourQuestionnaire));
    updateCartTimestamp();
  }, [tourQuestionnaire]);

  useEffect(() => {
    safeSetItem('vtc_analyzed_activities', JSON.stringify(analyzedActivities));
    updateCartTimestamp();
  }, [analyzedActivities]);

  useEffect(() => {
    safeSetItem('vtc_selected_analyzed_activity_ids', JSON.stringify(selectedAnalyzedActivityIds));
    updateCartTimestamp();
  }, [selectedAnalyzedActivityIds]);

  useEffect(() => {
    safeSetItem('vtc_prioritized_activity_ids', JSON.stringify(prioritizedActivityIds));
    updateCartTimestamp();
  }, [prioritizedActivityIds]);

  useEffect(() => {
    safeSetItem('vtc_overflow_option', JSON.stringify(overflowOption));
    updateCartTimestamp();
  }, [overflowOption]);


  const isPassengerSectionLocked = 
    tourQuestionnaire.hasAnimal === null || 
    (tourQuestionnaire.hasAnimal === true && (!tourQuestionnaire.animalSize || !tourQuestionnaire.animalRace));

  const resetPassengers = () => ({
    adults: 0,
    children6_12: 0,
    children3_6: 0,
    babies: 0
  });

  const handleAnimalPresenceChange = (hasAnimal: boolean) => {
    setTourQuestionnaire(prev => ({
      ...prev,
      hasAnimal, 
      animalRace: '',
      animalSize: 'moyen'
    }));
  };

  const handleAnimalDetailChange = (updates: Partial<TourQuestionnaire>) => {
    setTourQuestionnaire(prev => ({
      ...prev,
      ...updates
    }));
  };

  const totalDurationMinutes = useMemo(() => {
    const selected = analyzedActivities.filter(a => selectedAnalyzedActivityIds.includes(a.id));
    // Estimate 90 mins per activity + 30 mins travel
    return selected.reduce((acc, act) => acc + (act.durationMinutes || 90) + 30, 0);
  }, [selectedAnalyzedActivityIds, analyzedActivities]);

  const isTimeOverflowing = totalDurationMinutes > 540; // 9 hours


  const [viewingWebsite, setViewingWebsite] = useState<string | null>(null);
  /**
   * Admin Selected Sector
   * Tracks the currently selected sector in the Admin panel for site management.
   */
  const [adminSelectedSector, setAdminSelectedSector] = useState(TOUR_SECTORS[0]);
  const [adminSearchQuery, setAdminSearchQuery] = useState('');
  const [updateProgress, setUpdateProgress] = useState<{ current: number, total: number } | null>(null);
  const [aiSearchSector, setAiSearchSector] = useState(TOUR_SECTORS[0]);

  /**
   * Handle Update All Sites
   * Updates all sites in a specific sector by fetching data from Google Places.
   * Updates the UI incrementally to show progress.
   * @param sector - The sector name to update.
   */
  const handleUpdateDistanceMatrix = async (sector: string) => {
    setIsUpdatingMatrix(true);
    
    const sectorSites = appData.sites.filter(s => s.sector === sector && s.location && s.location.lat !== 0 && s.location.lng !== 0);
    const newMatrices = { ...(appData.settings.distanceMatrices || {}) };
    
    if (sectorSites.length < 2) {
      alert("Pas assez de sites avec des coordonnées GPS valides dans ce secteur pour calculer une matrice.");
      setIsUpdatingMatrix(false);
      return;
    }

    // OSRM public API allows up to 100 coordinates per request.
    if (sectorSites.length > 100) {
      alert("Trop de sites pour une seule requête OSRM (limite 100). Veuillez procéder par plus petits secteurs.");
      setIsUpdatingMatrix(false);
      return;
    }

    setMatrixUpdateProgress({ current: 0, total: 1 });

    try {
      // Format for OSRM: lng,lat;lng,lat...
      const coordinatesString = sectorSites.map(site => `${site.location.lng},${site.location.lat}`).join(';');
      
      // Call OSRM Table service
      const response = await fetch(`https://router.project-osrm.org/table/v1/driving/${coordinatesString}?annotations=duration,distance`);
      
      if (!response.ok) {
        throw new Error(`OSRM API error: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.code !== 'Ok') {
        throw new Error(`OSRM returned code: ${data.code}`);
      }

      // data.durations and data.distances are 2D arrays [origins][destinations]
      for (let i = 0; i < sectorSites.length; i++) {
        for (let j = i + 1; j < sectorSites.length; j++) {
          const siteA = sectorSites[i];
          const siteB = sectorSites[j];
          
          const durationSeconds = data.durations[i][j];
          const distanceMeters = data.distances[i][j];
          
          if (durationSeconds !== null && distanceMeters !== null) {
            const pairKey = [siteA.id, siteB.id].sort().join('_');
            newMatrices[pairKey] = {
              distance: distanceMeters / 1000, // Convert to km
              duration: Math.round(durationSeconds / 60), // Convert to minutes
              lastUpdate: Date.now()
            };
          }
        }
      }
      
      setMatrixUpdateProgress({ current: 1, total: 1 });
      
      const newData = {
        ...appData,
        settings: {
          ...appData.settings,
          distanceMatrices: newMatrices
        }
      };
      
      const savedData = await saveAppData(newData);
      setAppData(savedData);
      // Forcer la sauvegarde immédiate via updateSettings pour être sûr
      await updateSettings(savedData.settings);
    } catch (err) {
      console.error("Erreur lors de la mise à jour de la matrice via OSRM:", err);
      alert("Une erreur est survenue lors du calcul via OSRM. Veuillez réessayer.");
    }
    
    setIsUpdatingMatrix(false);
  };

  const handleUpdateAllSites = async (sector: string) => {
    if (isUpdatingAllSites) return;
    setIsUpdatingAllSites(true);
    setUpdateProgress({ current: 0, total: 0 });
    
    try {
      const sitesToUpdate = appData.sites.filter(s => s.sector === sector);
      setUpdateProgress({ current: 0, total: sitesToUpdate.length });
      
      let currentAppData = { ...appData };
      
      for (let i = 0; i < sitesToUpdate.length; i++) {
        const site = sitesToUpdate[i];
        setUpdateProgress({ current: i + 1, total: sitesToUpdate.length });
        
        // Add a small delay between sites to avoid hitting rate limits
        if (i > 0) {
          await new Promise(resolve => setTimeout(resolve, 2000)); // Réduit de 12s à 2s pour plus de fluidité
        }
        
        const updatedSite = await handleUpdateSingleSite(site, true); // skipSave = true
        if (updatedSite) {
          currentAppData = {
            ...currentAppData,
            sites: currentAppData.sites.map(s => s.id === site.id ? updatedSite : s)
          };
          setAppData(currentAppData);
        }
      }
      
      // Sauvegarde finale une seule fois
      const savedData = await saveAppData(currentAppData);
      setAppData(savedData);
      alert("Mise à jour globale terminée.");
    } catch (error) {
      console.error("Error updating all sites:", error);
      alert("Erreur lors de la mise à jour globale.");
    } finally {
      setIsUpdatingAllSites(false);
      setUpdateProgress(null);
    }
  };

  /**
   * Handle Update Single Site
   * Updates a single site by fetching data from Google Places and AI.
   * @param site - The site object to update.
   * @param skipSave - If true, doesn't call saveAppData (used for batch updates).
   */
  const handleUpdateSingleSite = async (site: TouristSite, skipSave: boolean = false): Promise<TouristSite | null> => {
    if (updatingSiteIds.has(site.id)) return null;
    
    setUpdatingSiteIds(prev => new Set(prev).add(site.id));
    try {
      const googleResults = await fetchGooglePlaceData(site.name, site.address || '', site.phone || '');
      const googleData = googleResults.find(r => r.name.toLowerCase().includes(site.name.toLowerCase())) || googleResults[0];
      
      const mergedSources = { ...(site.sources || {}) };
      const updatedSite = { ...site };
      
      if (!updatedSite.location) updatedSite.location = { lat: 0, lng: 0 };

      // 1. Apply Google Data
      if (googleData) {
        if (googleData.rating !== null && googleData.rating !== undefined) {
          updatedSite.googleRating = googleData.rating;
          mergedSources.googleRating = 'google';
        }
        if (googleData.userRatingsTotal !== null && googleData.userRatingsTotal !== undefined) {
          updatedSite.googleReviewsCount = googleData.userRatingsTotal;
          mergedSources.googleReviewsCount = 'google';
        }
        
        if (googleData.address) {
          updatedSite.address = googleData.address;
          if (updatedSite.location) updatedSite.location.address = googleData.address;
          mergedSources.address = 'google';
        }
        if (googleData.location) {
          updatedSite.gps = `${googleData.location.lat},${googleData.location.lng}`;
          if (updatedSite.location) {
            updatedSite.location.lat = googleData.location.lat;
            updatedSite.location.lng = googleData.location.lng;
          }
          mergedSources.gps = 'google';
        }
        if (googleData.phoneNumber) {
          updatedSite.phone = googleData.phoneNumber;
          mergedSources.phone = 'google';
        }
        if (googleData.website) {
          updatedSite.website = googleData.website;
          mergedSources.website = 'google';
        }
        if (googleData.priceLevel !== null && googleData.priceLevel !== undefined) {
          updatedSite.forks = Math.min(3, Math.max(1, googleData.priceLevel)) as any;
          updatedSite.cateringType = 'restaurant';
        }
        if (googleData.photoUrl && site.sources?.image !== 'manual') {
          console.log(`[ADMIN] Début du transfert d'images pour ${site.name}...`);
          try {
            // Premier upload avec clean: true pour vider le dossier
            const localUrl = await storageService.uploadImageToIonos(googleData.photoUrl, site.id, site.name, true);
            updatedSite.image = localUrl;
            
            if (googleData.photoUrls && googleData.photoUrls.length > 0) {
              // Uploads suivants sans clean
              const localUrls = await Promise.all(googleData.photoUrls.map(url => 
                storageService.uploadImageToIonos(url, site.id, site.name, false)
              ));
              updatedSite.images = localUrls;
            } else {
              updatedSite.images = [localUrl];
            }
            mergedSources.image = 'ionos';
          } catch (e) {
            console.error("Error uploading to Ionos, skipping Google URL to avoid 403 errors", e);
            // On ne fallback plus sur l'URL Google qui cause des 403 Forbidden dans le navigateur
            // mergedSources.image reste inchangé ou devient undefined
          }
        }
        if (googleData.isHandicapAccessible !== null && googleData.isHandicapAccessible !== undefined) {
          updatedSite.isHandicapAccessible = googleData.isHandicapAccessible;
        }
        if (googleData.openingHours) {
          updatedSite.openingHours = googleData.openingHours;
          mergedSources.openingHours = 'google';
        }
        if (googleData.summary) {
          updatedSite.description = googleData.summary;
          mergedSources.description = 'google';
        }
      }
      
      // 2. Apply AI Data
      const aiData = await fetchAIDataForSite(site.name, site.address || '', site.categories || []);
      if (aiData) {
        updatedSite.description = aiData.description;
        mergedSources.description = 'ai';
        updatedSite.fullDescription = aiData.fullDescription;
        mergedSources.fullDescription = 'ai';
        
        if (!updatedSite.googleRating && aiData.googleRating) {
          updatedSite.googleRating = aiData.googleRating;
          mergedSources.googleRating = 'ai';
        }
        if (!updatedSite.googleReviewsCount && aiData.googleReviewsCount) {
          updatedSite.googleReviewsCount = aiData.googleReviewsCount;
          mergedSources.googleReviewsCount = 'ai';
        }

        if (site.sources?.image !== 'manual' && aiData.image && mergedSources.image !== 'google') {
          updatedSite.image = aiData.image;
          updatedSite.images = [aiData.image];
          mergedSources.image = 'ai';
        }
        
        if (site.sources?.adultPrice !== 'manual' && aiData.adultPrice !== undefined) {
          updatedSite.adultPrice = aiData.adultPrice;
          mergedSources.adultPrice = 'ai';
        }
        if (site.sources?.child6_12Price !== 'manual' && aiData.child6_12Price !== undefined) {
          updatedSite.child6_12Price = aiData.child6_12Price;
          mergedSources.child6_12Price = 'ai';
        }
        if (site.sources?.child3_6Price !== 'manual' && aiData.child3_6Price !== undefined) {
          updatedSite.child3_6Price = aiData.child3_6Price;
          mergedSources.child3_6Price = 'ai';
        }
        if (site.sources?.babyPrice !== 'manual' && aiData.babyPrice !== undefined) {
          updatedSite.babyPrice = aiData.babyPrice;
          mergedSources.babyPrice = 'ai';
        }
        
        if (updatedSite.categories && (updatedSite.categories.includes('restaurations') || updatedSite.categories.includes('restauration'))) {
          if (aiData.forks !== undefined) {
            updatedSite.forks = aiData.forks;
            updatedSite.cateringType = aiData.forks === 0 ? 'snack' : 'restaurant';
          } else {
            updatedSite.cateringType = 'restaurant';
          }
        }

        if (aiData.durationMinutes !== undefined) {
          updatedSite.durationMinutes = aiData.durationMinutes;
          mergedSources.durationMinutes = 'ai';
        }

        if (updatedSite.adultPrice || updatedSite.child6_12Price || updatedSite.child3_6Price || updatedSite.babyPrice || updatedSite.cateringType === 'restaurant' || updatedSite.cateringType === 'snack') {
          updatedSite.isFree = false;
        }
        
        updatedSite.isPaid = aiData.isPaid;
        
        if (!updatedSite.openingHours && aiData.openingHours) {
          updatedSite.openingHours = aiData.openingHours;
          mergedSources.openingHours = 'ai';
        }
        
        updatedSite.dogsNotAllowed = aiData.dogsNotAllowed;
        
        if (aiData.isHandicapAccessible !== undefined && updatedSite.isHandicapAccessible === undefined) {
          updatedSite.isHandicapAccessible = aiData.isHandicapAccessible;
        }
        
        if (site.sources?.conditions !== 'manual' && aiData.accessibility) {
          updatedSite.conditions = aiData.accessibility;
          mergedSources.conditions = 'ai';
        }
        
        if (!updatedSite.phone && aiData.phone) {
          updatedSite.phone = aiData.phone;
          mergedSources.phone = 'ai';
        }
        if (!updatedSite.website && aiData.website) {
          updatedSite.website = aiData.website;
          mergedSources.website = 'ai';
        }

        if (!updatedSite.address && aiData.address) {
          updatedSite.address = aiData.address;
          updatedSite.location.address = aiData.address;
          mergedSources.address = 'ai';
        }
        if (!updatedSite.gps && aiData.gps) {
          updatedSite.gps = aiData.gps;
          const [lat, lng] = aiData.gps.split(',').map((s: string) => parseFloat(s.trim()));
          if (!isNaN(lat) && !isNaN(lng)) {
            updatedSite.location.lat = lat;
            updatedSite.location.lng = lng;
          }
          mergedSources.gps = 'ai';
        }
      }

      updatedSite.sources = mergedSources;
      updatedSite.updated_at = Date.now();
      
      if (!skipSave) {
        const updatedSites = appData.sites.map(s => s.id === site.id ? updatedSite : s);
        const finalData = { ...appData, sites: updatedSites };
        const savedData = await saveAppData(finalData);
        setAppData(savedData);
      }
      
      return updatedSite;
    } catch (error) {
      console.error("Error updating site:", error);
      const errorMsg = error instanceof Error ? error.message : String(error);
      const isQuota = errorMsg.includes('429') || errorMsg.includes('RESOURCE_EXHAUSTED');
      if (!skipSave) {
        try {
          alert(isQuota 
            ? "Quota API épuisé pour ce site. Veuillez patienter." 
            : `Erreur lors de la mise à jour du site: ${errorMsg}`);
        } catch (e) {
          console.warn("Alert blocked", e);
        }
      }
      return null;
    } finally {
      setUpdatingSiteIds(prev => {
        const next = new Set(prev);
        next.delete(site.id);
        return next;
      });
    }
  };

  const [aiSearchLimit, setAiSearchLimit] = useState("40");
  /**
   * Admin Search City
   * Stores the city name for the Admin site search functionality.
   */
  const [adminSearchCity, setAdminSearchCity] = useState('');
  /**
   * Admin Search Radius
   * Stores the radius (in km) for the Admin site search functionality.
   */
  const [adminSearchRadius, setAdminSearchRadius] = useState(20);
  const [selectedSuggestedSites, setSelectedSuggestedSites] = useState<string[]>([]);
  /**
   * Admin Site Category
   * Tracks the currently selected category for filtering sites in the Admin panel.
   */
  const [adminSiteCategory, setAdminSiteCategory] = useState<string>('patrimoine');
  const handlePhotonUpdate = async (site: TouristSite) => {
    const photonData = await geocodePhoton(site.name, site.address || '');
    if (photonData) {
      setEditingSite({
        ...site,
        name: photonData.name || site.name,
        address: photonData.address || site.address,
        gps: `${photonData.location.lat},${photonData.location.lng}`,
        location: {
          lat: photonData.location.lat,
          lng: photonData.location.lng,
          address: photonData.address
        }
      });
    }
  };

  const handleAIUpdate = async (site: TouristSite, retries = 3) => {
    try {
      const ai = getAiClient();
      const response = await ai.models.generateContent({
        model: "gemini-3-flash-preview",
        contents: `Mets à jour les informations pour le site suivant : ${JSON.stringify(site)}.`,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              description: { type: Type.STRING },
              fullDescription: { type: Type.STRING },
              categories: { type: Type.ARRAY, items: { type: Type.STRING } },
              adultPrice: { type: Type.NUMBER },
              child6_12Price: { type: Type.NUMBER },
              child3_6Price: { type: Type.NUMBER },
              babyPrice: { type: Type.NUMBER }
            },
            required: ["description", "fullDescription", "categories"]
          }
        }
      });
      const data = JSON.parse(response.text);
      setEditingSite({
        ...site,
        description: data.description,
        fullDescription: data.fullDescription,
        categories: data.categories,
        adultPrice: data.adultPrice,
        child6_12Price: data.child6_12Price,
        child3_6Price: data.child3_6Price,
        babyPrice: data.babyPrice
      });
    } catch (error) {
      if (retries > 0) {
        console.warn(`AI update failed for ${site.name}, retrying... (${retries} retries left)`, error);
        await new Promise(resolve => setTimeout(resolve, 3000));
        return handleAIUpdate(site, retries - 1);
      }
      console.error(`AI update failed for ${site.name} after all retries`, error);
      const errorMsg = error instanceof Error ? error.message : String(error);
      alert(`Erreur lors de la mise à jour par l'IA: ${errorMsg}`);
    }
  };

  const [editingSite, setEditingSite] = useState<TouristSite | null>(null);
  const [conflictData, setConflictData] = useState<{ serverItem: any, localItem: any, onResolve: (choice: 'server' | 'local') => void } | null>(null);
  const [isUploadingSiteImage, setIsUploadingSiteImage] = useState(false);
  const [siteImagePreview, setSiteImagePreview] = useState<string | null>(null);
  const [pendingImageSiteId, setPendingImageSiteId] = useState<string | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadDebugMessage, setUploadDebugMessage] = useState<string | null>(null);
  
  /**
   * AI Pack Prompt
   * Stores the prompt used for AI-generated tour packs.
   */
  const [aiPackPrompt, setAiPackPrompt] = useState('');
  const [isAiGeneratingPack, setIsAiGeneratingPack] = useState(false);
  const [isAiGeneratingAllPacks, setIsAiGeneratingAllPacks] = useState(false);
  /**
   * AI Circuit Search Prompt
   * Stores the prompt used for AI-based circuit searches.
   */
  const [aiCircuitSearchPrompt, setAiCircuitSearchPrompt] = useState('merci de faire une recherche sur ce secteur ne pas oublier les marchés locaux pour les produits du terroir, les snacks, les sandwicheries, et les restaurants qui préparant les produits du terroir et spécialités locales');
  const [isAiSearchingCircuit, setIsAiSearchingCircuit] = useState(false);
  const [aiSearchResult, setAiSearchResult] = useState<AiSearchResult | null>(null);
  const [isManualSiteModalOpen, setIsManualSiteModalOpen] = useState(false);
  const [manualSiteName, setManualSiteName] = useState('');
  /**
   * Is Updating All Sites
   * Flag indicating if the Admin is currently updating all sites in a sector.
   */
  const [isUpdatingAllSites, setIsUpdatingAllSites] = useState(false);
  /**
   * Updating Site IDs
   * Set of site IDs currently being updated.
   */
  const [updatingSiteIds, setUpdatingSiteIds] = useState<Set<string>>(new Set());
  /**
   * Site To Delete
   * Stores the ID of the site currently marked for deletion.
   */
  const [siteToDelete, setSiteToDelete] = useState<string | null>(null);

  const [user, setUser] = useState<User | null>(() => {
    const saved = localStorage.getItem('vtc_user');
    return saved ? JSON.parse(saved) : null;
  });
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('signup');
  const [authMessage, setAuthMessage] = useState<{ text: string, type: 'success' | 'error' | 'info' } | null>(null);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [manualUserForm, setManualUserForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    password: '',
    confirmPassword: ''
  });
  const [editUserForm, setEditUserForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    address: ''
  });

  useEffect(() => {
    if (view === 'profile_edit' && user) {
      setEditUserForm({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || '',
        address: user.address || ''
      });
    }
  }, [view, user]);
  const [customTourParams, setCustomTourParams] = useState(() => {
    const saved = localStorage.getItem('vtc_custom_tour_params');
    return saved ? JSON.parse(saved) : {
      duration: 'half-day' as 'half-day' | 'full-day',
      selectedThemes: [] as { id: string, priority: number }[],
      themeOrSector: '', // For custom text
      selectedActivities: [] as string[],
      adults: 2,
      children: 0,
      departureAddress: '',
      pickupTime: '09:00',
      accommodationCity: '',
      circuitDate: '',
      dailyBudget: 500
    };
  });
  const [customTourProposal, setCustomTourProposal] = useState<any>(() => {
    const saved = localStorage.getItem('vtc_custom_tour_proposal');
    return saved ? JSON.parse(saved) : null;
  });
  const [activeThemeIndex, setActiveThemeIndex] = useState(0);

  useEffect(() => {
    safeSetItem('vtc_custom_tour_params', JSON.stringify(customTourParams));
    updateCartTimestamp();
  }, [customTourParams]);

  useEffect(() => {
    safeSetItem('vtc_custom_tour_proposal', JSON.stringify(customTourProposal));
    updateCartTimestamp();
  }, [customTourProposal]);

  useEffect(() => {
    const checkCartExpiration = () => {
      const lastUpdated = localStorage.getItem('vtc_cart_last_updated');
      if (lastUpdated) {
        const time = parseInt(lastUpdated);
        if (Date.now() - time > 60 * 60 * 1000) { // 1 hour
          localStorage.removeItem('vtc_tour_step_v2');
          localStorage.removeItem('vtc_tour_questionnaire');
          localStorage.removeItem('vtc_analyzed_activities');
          localStorage.removeItem('vtc_selected_analyzed_activity_ids');
          localStorage.removeItem('vtc_prioritized_activity_ids');
          localStorage.removeItem('vtc_overflow_option');
          localStorage.removeItem('vtc_generated_itinerary');
          localStorage.removeItem('vtc_custom_tour_params');
          localStorage.removeItem('vtc_custom_tour_proposal');
          localStorage.removeItem('vtc_cart_order_number');
          localStorage.removeItem('vtc_cart_last_updated');
          
          setTourStep(1);
          setTourQuestionnaire(getDefaultTourQuestionnaire());
          setAnalyzedActivities([]);
          setSelectedAnalyzedActivityIds([]);
          setPrioritizedActivityIds([]);
          setOverflowOption(null);
          setCustomTourParams({
            duration: 'half-day',
            selectedThemes: [],
            themeOrSector: '',
            selectedActivities: [],
            adults: 2,
            children: 0,
            departureAddress: '',
            pickupTime: '09:00',
            accommodationCity: '',
            circuitDate: '',
            dailyBudget: 500
          });
          setCustomTourProposal(null);
          setCartOrderNumber(null);
        }
      }
    };
    checkCartExpiration();
  }, []);

  const [pendingAction, setPendingAction] = useState<'book_vtc' | 'book_tour' | 'book_custom' | 'book_custom_circuit' | null>(null);
  const [pendingTourCircuit, setPendingTourCircuit] = useState<any>(null);
  const [pendingTour, setPendingTour] = useState<Tour | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // User is signed in, see if we have their data in our storage
        try {
          const currentData = await storageService.loadData();
          const loggedUser = currentData.users?.find((u: User) => u.id === firebaseUser.uid);
          
          if (loggedUser) {
            setUser(loggedUser);
            safeSetItem('vtc_user', JSON.stringify(loggedUser));
          }
        } catch (error) {
          console.error("Error restoring session from Firebase:", error);
        }
      } else {
        // User is signed out
        // Only clear if we were previously logged in via Firebase
        const savedUser = localStorage.getItem('vtc_user');
        if (savedUser) {
          const parsed = JSON.parse(savedUser);
          if (parsed.provider !== 'manual' || auth.currentUser === null) {
            // If it's a social login or if we're sure they're logged out of Firebase
            // we might want to clear, but let's be careful not to break manual login
            // For now, let's just sync the state
          }
        }
      }
    });
    
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user?.id) {
      storageService.loadData()
        .then(data => {
          const serverUser = data.users?.find((u: User) => u.id === user.id);
          if (serverUser) {
            setUser(serverUser);
            safeSetItem('vtc_user', JSON.stringify(serverUser));
          }
        })
        .catch(err => console.error("Error syncing user:", err));
    }
  }, []);

  const handleGoogleLogin = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      const googleUser: User = {
        id: user.uid,
        firstName: user.displayName?.split(' ')[0] || 'Utilisateur',
        lastName: user.displayName?.split(' ')[1] || 'Google',
        email: user.email || '',
        provider: 'google',
        role: 'client'
      };

      const currentData = await storageService.loadData();
      const existingUser = currentData.users?.find((u: User) => u.email === googleUser.email);
      
      const finalUser = existingUser || googleUser;
      
      if (!existingUser) {
        await saveUser(googleUser);
      } else {
        setUser(finalUser);
        safeSetItem('vtc_user', JSON.stringify(finalUser));
      }
      
      if (pendingAction) {
        if (pendingAction === 'book_vtc') handleBooking(finalUser);
        else if (pendingAction === 'book_tour' && pendingTour) handleBookTour(pendingTour, finalUser);
        else if (pendingAction === 'book_custom') generateCustomTour(finalUser);
        else if (pendingAction === 'book_custom_circuit' && pendingTourCircuit) handleTourBooking(pendingTourCircuit, finalUser);
        setPendingAction(null);
        setPendingTour(null);
        setPendingTourCircuit(null);
      } else {
        setView('home');
      }
    } catch (error: any) {
      if (error.code === 'auth/popup-closed-by-user') {
        console.log("Connexion Google annulée par l'utilisateur (fenêtre fermée).");
        return;
      }
      console.error("Google login error:", error);
      alert("Erreur lors de la connexion Google: " + (error.message || "Erreur inconnue"));
    }
  };

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (authMode === 'signup') {
      if (!manualUserForm.firstName || !manualUserForm.lastName || !manualUserForm.phone) {
        alert("Veuillez renseigner votre nom, prénom et numéro de téléphone.");
        return;
      }
      if (manualUserForm.password !== manualUserForm.confirmPassword) {
        alert("Les mots de passe ne correspondent pas.");
        return;
      }
      if (manualUserForm.password.length < 8 || !/[A-Z]/.test(manualUserForm.password)) {
        alert("Le mot de passe doit contenir au moins 8 caractères et une majuscule.");
        return;
      }
      try {
        const userCredential = await createUserWithEmailAndPassword(auth, manualUserForm.email, manualUserForm.password);
        const user = userCredential.user;
        
        const newUser: User = {
          id: user.uid,
          firstName: manualUserForm.firstName,
          lastName: manualUserForm.lastName,
          email: manualUserForm.email,
          phone: manualUserForm.phone,
          address: manualUserForm.address,
          provider: 'manual',
          role: manualUserForm.email === appData.settings?.adminEmail ? 'admin' : 'client'
        };
        
        await saveUser(newUser);
        setAuthMessage({ text: "Compte créé avec succès ! Vous pouvez maintenant vous connecter.", type: 'success' });
        setAuthMode('login');
      } catch (error: any) {
        if (error.code === 'auth/email-already-in-use' || error.message?.includes('auth/email-already-in-use')) {
          setAuthMessage({ text: "Ce compte existe déjà. Vous avez été redirigé vers la page de connexion.", type: 'info' });
          setAuthMode('login');
          return;
        }
        console.error("Signup error:", error);
        setAuthMessage({ text: "Erreur lors de la création du compte: " + error.message, type: 'error' });
      }
    } else {
      try {
        const userCredential = await signInWithEmailAndPassword(auth, manualUserForm.email, manualUserForm.password);
        const user = userCredential.user;
        
        const currentData = await storageService.loadData();
        const loggedUser = currentData.users?.find((u: User) => u.id === user.uid);
        
        if (!loggedUser) {
          setAuthMessage({ text: "Utilisateur non trouvé dans la base de données.", type: 'error' });
          return;
        }
        
        setUser(loggedUser);
        safeSetItem('vtc_user', JSON.stringify(loggedUser));
        setAuthMessage({ text: "Connexion réussie !", type: 'success' });
        
        if (pendingAction) {
          if (pendingAction === 'book_vtc') handleBooking(loggedUser);
          else if (pendingAction === 'book_tour' && pendingTour) handleBookTour(pendingTour, loggedUser);
          else if (pendingAction === 'book_custom') generateCustomTour(loggedUser);
          else if (pendingAction === 'book_custom_circuit' && pendingTourCircuit) handleTourBooking(pendingTourCircuit, loggedUser);
          setPendingAction(null);
          setPendingTour(null);
          setPendingTourCircuit(null);
        } else {
          setView('home');
        }
      } catch (error: any) {
        if (error.code === 'auth/invalid-credential' || error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
          setAuthMessage({ text: "Identifiants invalides. Veuillez vérifier votre e-mail et votre mot de passe.", type: 'error' });
          return;
        }
        if (error.code === 'auth/too-many-requests') {
          setAuthMessage({ text: "Trop de tentatives. Compte temporairement bloqué. Réessayez dans quelques minutes.", type: 'error' });
          return;
        }
        console.error("Login error:", error);
        setAuthMessage({ text: "Erreur lors de la connexion: " + error.message, type: 'error' });
      }
    }
  };

  const handleEnterNext = useCallback((e: any) => {
    if (e.key === 'Enter') {
      const form = (e.currentTarget as HTMLElement).closest('form') || (e.currentTarget as HTMLElement).closest('.form-container');
      if (form) {
        const elements = Array.from(form.querySelectorAll('input, select, textarea')).filter(el => {
          const style = window.getComputedStyle(el);
          return style.display !== 'none' && style.visibility !== 'hidden';
        });
        const index = elements.indexOf(e.currentTarget as any);
        if (index > -1 && index < elements.length - 1) {
          e.preventDefault();
          const nextEl = elements[index + 1] as HTMLElement;
          if (nextEl.tagName === 'GMP-PLACE-AUTOCOMPLETE' && nextEl.shadowRoot) {
            const innerInput = nextEl.shadowRoot.querySelector('input');
            if (innerInput) {
              innerInput.focus();
            } else {
              nextEl.focus();
            }
          } else {
            nextEl.focus();
          }
        }
      }
    }
  }, []);
  const handleGeolocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          if (window.google) {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
              if (status === 'OK' && results?.[0]) {
                setPickup({
                  lat: latitude,
                  lng: longitude,
                  address: results[0].formatted_address
                });
              } else {
                setPickup({ lat: latitude, lng: longitude, address: "Ma position actuelle" });
              }
            });
          }
        },
        (error) => {
          console.error("Erreur de géolocalisation:", error);
          // Fallback or message to user could be added here
        },
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
      );
    }
  };

  const [driverPos, setDriverPos] = useState<Location | null>(() => {
    const saved = localStorage.getItem('vtc_driver_pos');
    return saved ? JSON.parse(saved) : null;
  });
  const [animatedDriverPos, setAnimatedDriverPos] = useState<Location | null>(null);
  const [customerPos, setCustomerPos] = useState<Location | null>(() => {
    const saved = localStorage.getItem('vtc_customer_pos');
    return saved ? JSON.parse(saved) : null;
  });

  useEffect(() => {
    if (driverPos) safeSetItem('vtc_driver_pos', JSON.stringify(driverPos));
  }, [driverPos]);

  useEffect(() => {
    if (customerPos) safeSetItem('vtc_customer_pos', JSON.stringify(customerPos));
  }, [customerPos]);
  const [activeBooking, setActiveBooking] = useState<Booking | null>(() => {
    const saved = localStorage.getItem('vtc_active_booking');
    return saved ? JSON.parse(saved) : null;
  });
  const [tripValidated, setTripValidated] = useState(false);
  const [approachInfo, setApproachInfo] = useState<{ distance: string, duration: string } | null>(null);
  const defaultSettings: Settings = { 
      kmRate: 2.15, 
      highwayTolls: true, 
      maxDailyKm: 250, 
      companyName: "Céven'VTC",
      email: 'ceven.vtc@gmail.com',
      phone: '06 17 76 68 44',
      vehicle: 'Peugeot 5008 (7 places, Bleu Bourrasque)',
      driverExperience: '34 ans de permis',
      siret: '123 456 789 00012', 
      baseAddress: 'Cazilhac 34190',
      legalMentions: "Mentions légales de Céven'VTC...",
      cgv: "Conditions Générales de Vente de Céven'VTC...",
      nightRateIncrease: 35,
      nightStart: "19:00",
      nightEnd: "07:00",
      discountThreshold: 110,
      discountPercentage: 10,
      heroImageLastUpdate: 0,
      adminEmail: 'gerald.verdier@gmail.com',
      driverHomeAddress: 'Cazilhac 34190',
      touristCircuitsTitle: 'Découvrez les Cévennes autrement',
      touristCircuitsText: 'Profitez du confort de notre Peugeot 5008 Bleu et de notre expertise locale pour un circuit inoubliable.',
      tourMaxKm: 200,
      tourReturnTime: "19:00",
      tourMaxDurationHalfDay: 5,
      tourMaxDurationFullDay: 10,
      heroImageBase64: '',
      tourPriceFullDay: 280,
      tourPriceHalfDay: 175,
      tourToleranceMinutes: 30,
      bufferTimeMinutes: 15,
      standardLunchStart: "11:30",
      standardLunchEnd: "13:30",
      childLunchStart: "11:30",
      childLunchEnd: "13:00",
      lunchDurationMinutes: 60,
      picnicDurationMinutes: 30,
      heroVtcGlassEffect: false,
      btnDriverText: 'ACCÈS CHAUFFEUR',
      btnAdminText: 'ACCÈS ADMIN',
      btnVtcText: 'COURSE IMMÉDIATE',
      btnReservationText: 'RÉSERVATION',
      btnTourText: 'CIRCUIT TOURISTIQUE',
      touristCircuitsBtnText: 'Commencer ma réservation',
      categoryConditions: {
        'baignade': 'Prévoir maillot de bain et serviette.',
        'randonnee': 'Prévoir des chaussures de marche adaptées et de l\'eau.',
        'patrimoine': 'Certains sites peuvent être frais, prévoir un gilet.',
        'insolite': 'Consultez les conditions spécifiques de la fiche.',
        'nature': 'Prévoir une tenue adaptée à l\'extérieur.',
        'activites': 'Prévoir une tenue de sport et des chaussures fermées.',
        'points_de_vue': 'Pensez à votre appareil photo !',
        'terroir': 'Prévoir un sac isotherme pour vos achats.',
        'restauration': 'Pensez à réserver votre table à l\'avance.',
        'autre': ''
      }
    };

  const [serverSiteIds, setServerSiteIds] = useState<Set<string>>(new Set());
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [appData, setAppData] = useState<{ sites: TouristSite[], settings: Settings, users: User[], packs: TourPack[], bookings?: Booking[], activeBooking?: Booking | null, driverLocation?: { lat: number, lng: number }, customerLocation?: any }>({ sites: [], settings: defaultSettings, users: [], packs: [], bookings: [], activeBooking: null, driverLocation: { lat: 43.923, lng: 3.847 }, customerLocation: null });
  const appDataRef = useRef(appData);
  useEffect(() => { appDataRef.current = appData; }, [appData]);

  const [saveSettingsSuccess, setSaveSettingsSuccess] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [isGeneratingText, setIsGeneratingText] = useState(false);
  const [completedBookings, setCompletedBookings] = useState<Booking[]>(() => {
    const saved = localStorage.getItem('vtc_completed_bookings');
    return saved ? JSON.parse(saved) : [];
  });
  const [suggestedSites, setSuggestedSites] = useState<any[]>([]);

  const saveUser = async (userData: User) => {
    const currentData = { ...appData };
    if (!currentData.users) currentData.users = [];
    
    const existingIndex = currentData.users.findIndex((u: User) => u.id === userData.id);
    if (existingIndex >= 0) {
      currentData.users[existingIndex] = userData;
    } else {
      currentData.users.push(userData);
    }
    
    const savedData = await saveAppData(currentData);
    setAppData(savedData);
    setUser(userData);
    safeSetItem('vtc_user', JSON.stringify(userData));
    return userData;
  };

  const [showChat, setShowChat] = useState(false);

  const currentActivityIds = useMemo(() => {
    if (tourStep === 7 && overflowOption === 'priority' && prioritizedActivityIds.length > 0) {
      return prioritizedActivityIds;
    }
    return selectedAnalyzedActivityIds;
  }, [tourStep, overflowOption, prioritizedActivityIds, selectedAnalyzedActivityIds]);

  const estimatedDistance = useMemo(() => {
    const selected = analyzedActivities.filter(a => currentActivityIds.includes(a.id));
    // Very rough estimation: 30km base + 25km per activity
    return 30 + (selected.length * 25);
  }, [currentActivityIds, analyzedActivities]);

  const currentTransportPrice = useMemo(() => {
    const basePrice = tourQuestionnaire.logistics?.duration === 'half-day' 
      ? (appData.settings?.tourPriceHalfDay || 175) 
      : (appData.settings?.tourPriceFullDay || 280);
      
    if (estimatedDistance < (appData.settings?.discountDistanceThreshold || 150)) {
      const discountRate = (appData.settings?.tourDiscountPercentage || 20) / 100;
      return basePrice * (1 - discountRate);
    }
    return basePrice;
  }, [estimatedDistance, appData.settings?.tourPriceHalfDay, appData.settings?.tourPriceFullDay, appData.settings?.discountDistanceThreshold, appData.settings?.tourDiscountPercentage, tourQuestionnaire.logistics?.duration]);

  // Fetch appData from server on mount
  useEffect(() => {
    const controller = new AbortController();
    const fetchAppData = async () => {
      try {
        const data = await storageService.loadData(undefined, controller.signal);
        if (controller.signal.aborted) return;
        setAppData(prev => {
          // Merge distance matrices carefully to avoid losing local updates if server is stale
          const serverMatrices = data.settings?.distanceMatrices || {};
          const localMatrices = prev.settings.distanceMatrices || {};
          
          // Keep the most recent update for each pair
          const mergedMatrices = { ...localMatrices };
          Object.keys(serverMatrices).forEach(key => {
            if (!mergedMatrices[key] || (serverMatrices[key].lastUpdate > (mergedMatrices[key].lastUpdate || 0))) {
              mergedMatrices[key] = serverMatrices[key];
            }
          });

          return {
            ...prev,
            sites: data.sites || prev.sites,
            settings: { 
              ...prev.settings, 
              ...(data.settings || {}),
              distanceMatrices: mergedMatrices
            },
            users: data.users || prev.users,
            packs: data.packs || prev.packs,
            bookings: data.bookings || prev.bookings
          };
        });
        setIsDataLoaded(true);
      } catch (error: any) {
        if (error.name === 'AbortError' || error.message?.includes('aborted')) return;
        console.error("Error fetching app data from server:", error);
      }
    };
    fetchAppData();
    return () => controller.abort();
  }, []);

  // Persist completedBookings
  useEffect(() => {
    safeSetItem('vtc_completed_bookings', JSON.stringify(completedBookings));
  }, [completedBookings]);

  // Persist activeBooking
  useEffect(() => {
    safeSetItem('vtc_active_booking', JSON.stringify(activeBooking));
  }, [activeBooking]);

  // Auto-promote admin based on email
  useEffect(() => {
    if (user && appData.settings?.adminEmail && user.email === appData.settings?.adminEmail && user.role !== 'admin') {
      const updatedUser = { ...user, role: 'admin' as const };
      setUser(updatedUser);
      safeSetItem('vtc_user', JSON.stringify(updatedUser));
    }
  }, [user, appData.settings?.adminEmail]);

  // Track customer location when booking is active
  useEffect(() => {
    if (!socket || !activeBooking || !user || activeBooking.customerId !== user.id) return;
    
    // Only track if driver is approaching or arrived
    const shouldTrack = ['accepted', 'approaching', 'arrived'].includes(activeBooking.status);
    if (!shouldTrack) return;

    let watchId: number;
    if (navigator.geolocation) {
      watchId = navigator.geolocation.watchPosition(
        (position) => {
          const loc = { lat: position.coords.latitude, lng: position.coords.longitude };
          socket.emit('update_customer_location', loc);
        },
        (error) => console.error("Customer tracking error:", error),
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
      );
    }

    return () => {
      if (watchId) navigator.geolocation.clearWatch(watchId);
    };
  }, [socket, activeBooking, user]);
  const [selectedActivityDetail, setSelectedActivityDetail] = useState<any>(null);
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);

  const [requestSms, setRequestSms] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pendingBooking, setPendingBooking] = useState<any>(null);
  
  // VTC Booking state
  const [pickup, setPickup] = useState<Location | null>(null);
  const [destination, setDestination] = useState<Location | null>(null);
  const [tripInfo, setTripInfo] = useState<{ distance: string, duration: string } | null>(null);
  const [scheduledDate, setScheduledDate] = useState<string>('');
  const [scheduledTime, setScheduledTime] = useState<string>('');
  const [bookingType, setBookingType] = useState<'immediate' | 'advance'>('immediate');

  // Admin state
  const [newSiteName, setNewSiteName] = useState('');

  const [editingPrice, setEditingPrice] = useState(false);
  const [newPrice, setNewPrice] = useState(0);

  const [isGeminiKeyMissing, setIsGeminiKeyMissing] = useState(false);
  const [isMapsKeyMissing, setIsMapsKeyMissing] = useState(false);

  const isAiStudio = useMemo(() => 
    window.location.hostname.includes("run.app") || window.location.hostname.includes("localhost"), 
  []);

  const [cachedHeroImage, setCachedHeroImage] = useState<string | null>(() => {
    try {
      return localStorage.getItem('vtc_hero_image_cache');
    } catch (e) {
      return null;
    }
  });

  const heroImage = useMemo(() => {
    if (appData.settings?.heroImageBase64) {
      return appData.settings.heroImageBase64;
    }
    return cachedHeroImage || "/accueil-vtc.png";
  }, [appData.settings?.heroImageBase64, cachedHeroImage]);

  // Update cache when new image arrives from server
  useEffect(() => {
    if (appData.settings?.heroImageBase64 && appData.settings.heroImageBase64 !== cachedHeroImage) {
      try {
        localStorage.setItem('vtc_hero_image_cache', appData.settings.heroImageBase64);
        setCachedHeroImage(appData.settings.heroImageBase64);
      } catch (e) {
        console.warn("Failed to cache hero image:", e);
      }
    }
  }, [appData.settings?.heroImageBase64, cachedHeroImage]);

  const [heroImageError, setHeroImageError] = useState(false);

  // Reset image error when the image source changes
  useEffect(() => {
    setHeroImageError(false);
  }, [heroImage]);

  useEffect(() => {
    if (activeBooking) {
      setNewPrice(activeBooking.price);
    }
  }, [activeBooking]);

  useEffect(() => {
    if (!driverPos) return;
    if (!animatedDriverPos) {
      setAnimatedDriverPos(driverPos);
      return;
    }

    const step = 0.1;
    const interval = setInterval(() => {
      setAnimatedDriverPos(prev => {
        if (!prev) return driverPos;
        const lat = prev.lat + (driverPos.lat - prev.lat) * step;
        const lng = prev.lng + (driverPos.lng - prev.lng) * step;
        if (Math.abs(lat - driverPos.lat) < 0.00001 && Math.abs(lng - driverPos.lng) < 0.00001) {
          return driverPos;
        }
        return { ...prev, lat, lng };
      });
    }, 50);
    return () => clearInterval(interval);
  }, [driverPos, animatedDriverPos]);

  useEffect(() => {
    // Only attempt socket connection if we are in development (AI Studio)
    // Ionos production doesn't support WebSockets and it causes lag
    if (!isAiStudio) {
      return;
    }

    const socketUrl = window.location.origin;
    
    const newSocket = io(socketUrl, {
      reconnectionAttempts: 3,
      timeout: 5000,
      transports: ['polling'] // Force polling to avoid WebSocket issues
    });
    
    setSocket(newSocket);

    newSocket.on('connect_error', (err) => {
      console.warn("Socket connection error (expected if no backend is running):", err.message);
    });

    // Initial data fetch
    storageService.loadData()
      .then(data => {
        setAppData(data);
        setIsDataLoaded(true);
        if (data.sites) {
          setServerSiteIds(new Set(data.sites.map((s: any) => s.id)));
        }
        if (data.bookings) {
          setCompletedBookings(data.bookings.filter((b: Booking) => b.status === 'completed'));
        }
        if (data.driverLocation) {
          setDriverPos(data.driverLocation);
        }
        if (data.customerLocation !== undefined) {
          setCustomerPos(data.customerLocation);
        }
        if (data.activeBooking !== undefined) {
          setActiveBooking(data.activeBooking);
        }
      })
      .catch(err => {
        if (err.name !== 'AbortError') {
          console.error("Error fetching initial data:", err);
        }
      });

    newSocket.on('driver_update', (data) => {
      setDriverPos(data.location);
    });

    newSocket.on('customer_update', (data) => {
      setCustomerPos(data.location);
    });

    newSocket.on('data_update', (data) => {
      setAppData(prev => {
        const merged = {
          ...prev,
          ...data,
          settings: {
            ...prev.settings,
            ...data.settings,
            // SÉCURITÉ CRITIQUE : On préfère l'image du serveur si elle est là, sinon on garde la locale
            heroImageBase64: data.settings?.heroImageBase64 || prev.settings?.heroImageBase64,
            heroImageLastUpdate: data.settings?.heroImageLastUpdate || prev.settings?.heroImageLastUpdate
          }
        };
        return merged;
      });
      if (data.bookings) {
        setCompletedBookings(data.bookings.filter((b: Booking) => b.status === 'completed'));
      }
      if (data.driverLocation) {
        setDriverPos(data.driverLocation);
      }
      if (data.customerLocation !== undefined) {
        setCustomerPos(data.customerLocation);
      }
      if (data.activeBooking !== undefined) {
        setActiveBooking(data.activeBooking);
        safeSetItem('vtc_active_booking', JSON.stringify(data.activeBooking));
      }
    });

    newSocket.on('booking_update', (booking) => {
      setActiveBooking(booking);
      if (booking && booking.status !== 'completed' && booking.status !== 'pending') {
        setView('status');
      } else if (!booking) {
        setActiveBooking(null);
        if (view === 'status') setView('home');
      }
    });

    return () => {
      newSocket.disconnect();
    };
  }, []);

  // Organize sites into tours
  const generatedTours: Tour[] = React.useMemo(() => {
    if (appData.sites.length === 0) return [];
    
    // Simple clustering logic: group sites that are close to each other
    // For a real app, we'd use a more sophisticated TSP or clustering algorithm
    const tours: Tour[] = [];
    const unvisited = [...appData.sites];
    
    let tourCount = 1;
    while (unvisited.length > 0) {
      const currentTourSites: TouristSite[] = [];
      let currentPos = { lat: 44.127, lng: 4.083 }; // Start from Alès
      let tourDistance = 0;

      // Greedy approach to build a tour
      while (unvisited.length > 0) {
        let nearestIdx = -1;
        let minDist = Infinity;

        unvisited.forEach((site, idx) => {
          if (!site.location?.lat || !site.location?.lng || !currentPos?.lat || !currentPos?.lng) return;
          const d = Math.sqrt(
            Math.pow(site.location.lat - currentPos.lat, 2) + 
            Math.pow(site.location.lng - currentPos.lng, 2)
          ) * 111; // Approx km
          if (d < minDist) {
            minDist = d;
            nearestIdx = idx;
          }
        });

        if (nearestIdx !== -1 && tourDistance + minDist < appData.settings.maxDailyKm / 2) {
          const site = unvisited.splice(nearestIdx, 1)[0];
          if (site.location) {
            currentTourSites.push(site);
            tourDistance += minDist;
            currentPos = site.location;
          }
        } else {
          break;
        }
      }

      if (currentTourSites.length > 0) {
        const totalPrice = Math.round(tourDistance * 2 * appData.settings.kmRate + (appData.settings.highwayTolls ? 15 : 0));
        tours.push({
          id: `tour-${tourCount}`,
          title: `Circuit Découverte ${tourCount}`,
          description: `Un magnifique circuit incluant : ${currentTourSites.map(s => s.name).join(', ')}.`,
          duration: `${Math.round(tourDistance / 40 + currentTourSites.length)}h`,
          price: totalPrice,
          image: currentTourSites[0].image,
          sites: currentTourSites
        });
        tourCount++;
      } else if (unvisited.length > 0) {
        // If a site is too far, just skip it for this simple demo or put it in its own tour
        unvisited.shift();
      }
    }
    return tours;
  }, [appData]);

  const handleAddSite = async () => {
    if (!newSiteName) return;
    const content = {
      description: "Un lieu touristique à découvrir.",
      fullDescription: "Une description plus détaillée sera ajoutée ultérieurement.",
      adultPrice: 0,
      child6_12Price: 0,
      child3_6Price: 0,
      babyPrice: 0,
      isPaid: false,
      openingHours: "9h00 - 18h00",
      dogsNotAllowed: false,
      isHandicapAccessible: true,
      accessibility: "Accessible",
      phone: "",
      website: "",
      address: newSiteName,
      gps: "43.923,3.847",
      image: `https://loremflickr.com/800/600/${newSiteName.replace(/\s+/g, ',')}`,
      tags: [],
      categories: ['nature'],
      googleRating: 4.5,
      googleReviewsCount: 0
    };
    const newSite: TouristSite = {
      id: Date.now().toString(),
      name: newSiteName,
      location: { 
        lat: 43.923 + (Math.random() - 0.5) * 0.1, 
        lng: 3.847 + (Math.random() - 0.5) * 0.1, 
        address: newSiteName 
      },
      description: content.description,
      image: content.image,
      tags: content.tags,
      categories: content.categories,
      isPaid: content.isPaid ?? false,
      adultPrice: content.adultPrice || 0,
      child6_12Price: content.child6_12Price || 0,
      child3_6Price: 0,
      babyPrice: 0,
      sector: 'Vis et Larzac', // Default sector
      isVisibleClient: false,
      googleRating: content.googleRating || 4.5,
      googleReviewsCount: content.googleReviewsCount || 0
    };
    const updated = [...appData.sites, newSite];
    setAppData(prev => ({ ...prev, sites: updated }));
    setNewSiteName('');
    setIsGenerating(false);
  };

  const handleSiteImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, siteId: string) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingSiteImage(true);
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const targetWidth = 400;
        const targetHeight = 300;
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const imgRatio = img.width / img.height;
        const targetRatio = targetWidth / targetHeight;
        let drawWidth, drawHeight, offsetX, offsetY;
        if (imgRatio > targetRatio) {
          drawHeight = targetHeight;
          drawWidth = img.width * (targetHeight / img.height);
          offsetX = (targetWidth - drawWidth) / 2;
          offsetY = 0;
        } else {
          drawWidth = targetWidth;
          drawHeight = img.height * (targetWidth / img.width);
          offsetX = 0;
          offsetY = (targetHeight - drawHeight) / 2;
        }
        ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
        const optimizedData = canvas.toDataURL('image/jpeg', 0.7);

        const updatedSites = appData.sites.map(s => s.id === siteId ? { ...s, image: optimizedData } : s);
        setAppData(prev => ({ ...prev, sites: updatedSites }));
        setIsUploadingSiteImage(false);
      };
      img.src = event.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  const handleGenerateSiteImage = async (siteId: string, siteName: string) => {
    setIsUploadingSiteImage(true);
    try {
      // AI generation disabled
      console.log("AI Image generation disabled for", siteName);
      /*
      const result = await withRetry(async () => {
        const ai = new GoogleGenAI({ apiKey: process.env.GEMINI_API_KEY });
        const response = await ai.models.generateContent({
          model: 'gemini-2.5-flash-image',
          contents: `Génère une photo professionnelle et attrayante du site touristique "${siteName}" dans les Cévennes. Format paysage, haute qualité.`,
        });
        return response;
      }, 15, 12000, "Gemini Site Image");
      
      let base64Image = '';
      for (const part of result.candidates[0].content.parts) {
        if (part.inlineData) {
          base64Image = `data:image/png;base64,${part.inlineData.data}`;
          break;
        }
      }

      if (base64Image) {
        setSiteImagePreview(base64Image);
        setPendingImageSiteId(siteId);
      }
      */
    } catch (error) {
      console.error("Error generating site image:", error);
    } finally {
      setIsUploadingSiteImage(false);
    }
  };

  const confirmSiteImage = () => {
    if (pendingImageSiteId && siteImagePreview) {
      const updatedSites = appData.sites.map(s => s.id === pendingImageSiteId ? { ...s, image: siteImagePreview } : s);
      setAppData(prev => ({ ...prev, sites: updatedSites }));
      setPendingImageSiteId(null);
      setSiteImagePreview(null);
    }
  };

  const [isUploadingSectorImage, setIsUploadingSectorImage] = useState<string | null>(null);

  const handleSectorImageUpload = (event: React.ChangeEvent<HTMLInputElement>, sector: string) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploadingSectorImage(sector);
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const targetWidth = 1200;
        const targetHeight = 800;
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const imgRatio = img.width / img.height;
        const targetRatio = targetWidth / targetHeight;
        let drawWidth, drawHeight, offsetX, offsetY;
        if (imgRatio > targetRatio) {
          drawHeight = targetHeight;
          drawWidth = img.width * (targetHeight / img.height);
          offsetX = (targetWidth - drawWidth) / 2;
          offsetY = 0;
        } else {
          drawWidth = targetWidth;
          drawHeight = img.height * (targetWidth / img.width);
          offsetX = 0;
          offsetY = (targetHeight - drawHeight) / 2;
        }
        ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
        const optimizedData = canvas.toDataURL('image/jpeg', 0.8);

        const configs = { ...(appData.settings.sectorConfigs || {}) };
        configs[sector] = { 
          ...(configs[sector] || { title: '', description: '' }), 
          imageBase64: optimizedData,
          imageLastUpdate: Date.now()
        };
        
        updateSettings({ ...appData.settings, sectorConfigs: configs });
        setIsUploadingSectorImage(null);
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingImage(true);
    setUploadProgress(10);
    setUploadSuccess(false);

    // Nettoyage préalable du stockage local pour libérer la mémoire
    localStorage.removeItem('vtc_hero_image');

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        setUploadProgress(40);
        const canvas = document.createElement('canvas');
        
        // Optimisation : Préserver l'aspect ratio original tout en limitant la taille
        let width = img.width;
        let height = img.height;
        const maxDim = 2000;
        
        if (width > height) {
          if (width > maxDim) {
            height = Math.round(height * (maxDim / width));
            width = maxDim;
          }
        } else {
          if (height > maxDim) {
            width = Math.round(width * (maxDim / height));
            height = maxDim;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          setIsUploadingImage(false);
          return;
        }
        
        ctx.drawImage(img, 0, 0, width, height);
        setUploadProgress(70);
        
        // Optimisation : Compression JPEG (plus léger que PNG pour éviter le blocage)
        const optimizedData = canvas.toDataURL('image/jpeg', 0.7);
        
        console.log("Uploading optimized hero image, size:", Math.round(optimizedData.length / 1024), "KB");
        setUploadProgress(90);
        
        // Update local state first
        const updatedSettings = {
          ...appData.settings,
          heroImageBase64: optimizedData,
          heroImageLastUpdate: Date.now()
        };
        const updatedData = { ...appData, settings: updatedSettings };
        setAppData(updatedData);

        // Save using storageService which handles both dev and production (bridge)
        storageService.saveData(updatedData)
        .then(() => {
          console.log("Hero image saved successfully via storageService");
          setUploadProgress(100);
          setUploadSuccess(true);
          setUploadDebugMessage('Image optimisée et enregistrée avec succès.');
          
          setTimeout(() => {
            setIsUploadingImage(false);
            setUploadProgress(0);
            window.location.reload();
          }, 1500);
        })
        .catch((error) => {
          console.error("Erreur lors de la sauvegarde de l'image:", error);
          setUploadDebugMessage(`Erreur: ${error.message}`);
          setIsUploadingImage(false);
          setUploadProgress(0);
        });
      };
      img.onerror = () => {
        setUploadDebugMessage("Erreur lors du chargement de l'image.");
        setIsUploadingImage(false);
      };
      img.src = event.target?.result as string;
    };
    reader.onerror = () => {
      setUploadDebugMessage("Erreur lors de la lecture du fichier.");
      setIsUploadingImage(false);
    };
    reader.readAsDataURL(file);
  };

  const handleSuggestSites = async () => {
    if (!adminSearchCity) return;
    setIsSuggesting(true);
    setSelectedSuggestedSites([]);
    
    try {
      const { Place } = await Promise.race([
        google.maps.importLibrary("places") as Promise<google.maps.PlacesLibrary>,
        new Promise<never>((_, reject) => setTimeout(() => reject(new Error("Google Maps Library timeout")), 30000))
      ]);
      
      // On récupère la limite demandée par l'utilisateur dans la case (par défaut 20 si non valide)
      const limit = parseInt(aiSearchLimit, 10) || 40;

      // On adapte les requêtes en fonction du secteur sélectionné pour être plus précis géographiquement
      // adminSearchCity contient le nom du secteur (ex: "Vis et Larzac")
      let locationQuery = adminSearchCity;
      
      // Affinage géographique selon le secteur pour guider Google Maps avec des délimitations précises
      if (adminSearchCity.includes("Vis") || adminSearchCity.includes("Larzac")) {
        locationQuery = "Cirque de Navacelles, Causse du Larzac, Vallée de la Vis, Aveyron";
      } else if (adminSearchCity.includes("Hérault") || adminSearchCity.includes("Salagou")) {
        locationQuery = "Gorges de l'Hérault, Grottes des Demoiselles, Saint-Guilhem-le-Désert, Lac du Salagou";
      } else if (adminSearchCity.includes("Romains") || adminSearchCity.includes("Nîmes")) {
        locationQuery = "Nîmes, Maison Carrée, Arènes de Nîmes, Pont du Gard, Uzès";
      } else if (adminSearchCity.includes("Anduze") || adminSearchCity.includes("Cévennes")) {
        locationQuery = "Anduze, Train à vapeur des Cévennes, Bambouseraie, Corniche des Cévennes";
      } else if (adminSearchCity.includes("Littoral") || adminSearchCity.includes("Camargue")) {
        locationQuery = "Aigues-Mortes, Le Grau-du-Roi, Camargue, Sète, Littoral méditerranéen";
      } else if (adminSearchCity.includes("Aigoual")) {
        locationQuery = "Mont Aigoual, Vallée de Valleraugue, Observatoire du Mont Aigoual, Meyrueis";
      }

      // On lance plusieurs requêtes avec des mots-clés très spécifiques pour obtenir un maximum de résultats qualifiés
      // On diversifie pour avoir un panel complet de ce qui intéresse les touristes
      const queries = [
        // 1. Incontournables & Patrimoine
        `sites touristiques ${locationQuery}`,
        `patrimoine historique ${locationQuery}`,
        `monuments ${locationQuery}`,
        `châteaux ${locationQuery}`,
        `musées ${locationQuery}`,
        
        // 2. Nature & Paysages
        `points de vue ${locationQuery}`,
        `panoramas ${locationQuery}`,
        `grottes ${locationQuery}`,
        `nature ${locationQuery}`,
        `randonnées ${locationQuery}`,
        `cascades ${locationQuery}`,
        
        // 3. Activités & Loisirs
        `activités de plein air ${locationQuery}`,
        `accrobranche ${locationQuery}`,
        `canoë ${locationQuery}`,
        `parcs animaliers ${locationQuery}`,
        
        // 4. Terroir & Gastronomie
        `domaines viticoles ${locationQuery}`,
        `marchés locaux ${locationQuery}`,
        `fermes ${locationQuery}`,
        
        // 5. Restauration & Pause
        `restaurants ${locationQuery}`,
        `auberges ${locationQuery}`,
        `boulangeries ${locationQuery}`,
        
        // 6. Hébergements atypiques
        `hébergements insolites ${locationQuery}`,
        `cabanes ${locationQuery}`,
        `maisons d'hôtes ${locationQuery}`
      ];

      const allPlacesMap = new window.Map();

      await Promise.all(queries.map(async (q) => {
        try {
          const request = {
            textQuery: q,
            fields: ["id", "displayName", "location", "rating", "userRatingCount", "regularOpeningHours", "priceLevel", "formattedAddress", "types", "editorialSummary", "photos", "accessibilityOptions", "internationalPhoneNumber", "websiteUri"],
            maxResultCount: 20, // Maximum autorisé par requête par Google
          };
          
          // @ts-ignore - new Places API
          const { places } = await Place.searchByText(request);
          
          if (places) {
            places.forEach((p: any) => {
              // Filtrage strict pour exclure les hôtels classiques si on cherche de l'atypique
              const types = p.types || [];
              const isHotel = types.includes('hotel') || types.includes('motel');
              const isAtypicalQuery = q.includes('hébergements insolites') || q.includes("chambres d'hôtes");
              
              // On exclut les hôtels classiques des résultats d'hébergement
              if (isAtypicalQuery && isHotel && !types.includes('bed_and_breakfast') && !types.includes('campground')) {
                return; // On ignore cet hôtel
              }

              if (p.id && !allPlacesMap.has(p.id)) {
                allPlacesMap.set(p.id, p);
              }
            });
          }
        } catch (e) {
          console.warn(`Erreur lors de la requête Google Places pour "${q}":`, e);
        }
      }));

      // On limite au nombre de résultats demandé dans la case
      const uniquePlaces = Array.from(allPlacesMap.values()).slice(0, limit);
      
      const suggestions = uniquePlaces.map((p: any) => ({
        name: p.displayName || '',
        description: p.editorialSummary || 'Un site touristique à découvrir.',
        category: p.types?.[0] || 'nature',
        isPaid: p.priceLevel !== undefined && p.priceLevel > 0,
        googleRating: p.rating || 4.5,
        googleReviewsCount: p.userRatingCount || 0,
        address: p.formattedAddress || '',
        location: p.location ? { lat: p.location.lat(), lng: p.location.lng() } : undefined,
        openingHours: p.regularOpeningHours?.weekdayDescriptions?.[0] || "Non spécifié",
        photoUri: p.photos?.[0]?.getURI({ maxWidth: 800, maxHeight: 600 }) || `https://loremflickr.com/800/600/${(p.displayName || '').replace(/\s+/g, ',')}`,
        isHandicapAccessible: p.accessibilityOptions?.wheelchairAccessibleEntrance === true,
        phone: p.internationalPhoneNumber || '',
        website: p.websiteUri || ''
      }));
      
      setSuggestedSites(suggestions);
    } catch (error) {
      console.error("Error fetching places:", error);
      setSuggestedSites([]);
    }
    
    setIsSuggesting(false);
  };

  const handleSaveSelectedSites = async () => {
    if (selectedSuggestedSites.length === 0) return;
    setIsGenerating(true);
    
    const sitesToSave = suggestedSites.filter(s => selectedSuggestedSites.includes(s.name));
    const newSites: TouristSite[] = [];
    
    for (const suggestion of sitesToSave) {
      newSites.push({
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        name: suggestion.name,
        location: { 
          lat: suggestion.location?.lat || 43.923 + (Math.random() - 0.5) * 0.1, 
          lng: suggestion.location?.lng || 3.847 + (Math.random() - 0.5) * 0.1, 
          address: suggestion.address || suggestion.name 
        },
        description: suggestion.description || "Un lieu touristique à découvrir.",
        image: suggestion.photoUri || `https://loremflickr.com/800/600/${suggestion.name.replace(/\s+/g, ',')}`,
        tags: suggestion.tags || [],
        categories: suggestion.category ? [suggestion.category] : ['nature'],
        isPaid: suggestion.isPaid ?? false,
        adultPrice: suggestion.adultPrice || 0,
        child6_12Price: suggestion.child6_12Price || 0,
        child3_6Price: 0,
        babyPrice: 0,
        sector: 'Vis et Larzac',
        isVisibleClient: false,
        googleRating: suggestion.googleRating || 4.5,
        googleReviewsCount: suggestion.googleReviewsCount || 0,
        isHandicapAccessible: suggestion.isHandicapAccessible ?? false,
        phone: suggestion.phone || '',
        website: suggestion.website || '',
        openingHours: suggestion.openingHours || "Non spécifié"
      });
    }
    
    setAppData(prev => ({ ...prev, sites: [...prev.sites, ...newSites] }));
    setSuggestedSites(prev => prev.filter(s => !selectedSuggestedSites.includes(s.name)));
    setSelectedSuggestedSites([]);
    setIsGenerating(false);
  };

  const handleUpdateSite = async (updatedSite: TouristSite, onConflictResolved?: () => void) => {
    // Règle d'or : On ne modifie jamais un site qui existe déjà sur le serveur via iStudio
    if (serverSiteIds.has(updatedSite.id)) {
      alert("⚠️ Règle de Publication Strict : Ce site existe déjà sur le serveur. Toute modification doit être effectuée via l'interface d'administration du serveur.");
      setEditingSite(null);
      return;
    }

    // 1. Vérifier les conflits avec le serveur (pour les nouveaux sites en cours de création si besoin, mais ici c'est surtout pour la sécurité)
    try {
      const serverData = await storageService.loadData();
      const serverSite = serverData.sites?.find((s: any) => s.id === updatedSite.id);
      const localSiteBefore = appData.sites.find(s => s.id === updatedSite.id);
      
      if (serverSite && serverSite.updated_at > (localSiteBefore?.updated_at || 0)) {
        setConflictData({
          serverItem: serverSite,
          localItem: updatedSite,
          onResolve: async (choice) => {
            if (choice === 'server') {
              setAppData(prev => ({
                ...prev,
                sites: prev.sites.map(s => s.id === serverSite.id ? serverSite : s)
              }));
              setConflictData(null);
              setEditingSite(null);
              onConflictResolved?.();
            } else {
              const finalSite = { ...updatedSite, updated_at: Date.now() };
              let updated = appData.sites.map(s => s.id === finalSite.id ? finalSite : s);
              if (finalSite.isEmblematic) {
                const sectorEmblematicSites = updated.filter(s => s.sector === finalSite.sector && s.isEmblematic && s.id !== finalSite.id);
                if (sectorEmblematicSites.length >= 8) {
                  const siteToUnmark = sectorEmblematicSites[0];
                  updated = updated.map(s => s.id === siteToUnmark.id ? { ...s, isEmblematic: false } : s);
                }
              }
              const newAppData = { ...appData, sites: updated };
              const savedData = await saveAppData(newAppData);
              setAppData(savedData);
              setConflictData(null);
              setEditingSite(null);
              onConflictResolved?.();
            }
          }
        });
        return;
      }
    } catch (e) {
      console.warn("Conflit check failed:", e);
    }

    const finalSite = { ...updatedSite, updated_at: Date.now() };
    let updated = appData.sites.map(s => s.id === finalSite.id ? finalSite : s);
    if (finalSite.isEmblematic) {
      const sectorEmblematicSites = updated.filter(s => s.sector === finalSite.sector && s.isEmblematic && s.id !== finalSite.id);
      if (sectorEmblematicSites.length >= 8) {
        // Unmark the first one found to keep only 8
        const siteToUnmark = sectorEmblematicSites[0];
        updated = updated.map(s => s.id === siteToUnmark.id ? { ...s, isEmblematic: false } : s);
      }
    }
    const newAppData = { ...appData, sites: updated };
    setEditingSite(null);

    // Save to server immediately (Full Update via saveAppData to prevent data loss on Ionos)
    try {
      const savedData = await saveAppData(newAppData);
      setAppData(savedData);
      onConflictResolved?.();
    } catch (error) {
      console.error("Error saving site update to server:", error);
    }
  };

  const handleVtcNavigation = async (type: 'immediate' | 'advance') => {
    if (navigator.vibrate) navigator.vibrate(50);
    
    // Naviguer immédiatement pour ne pas bloquer l'UI
    setBookingType(type);
    setView('vtc');

    // Vérifier et demander la géolocalisation
    if (navigator.permissions) {
      try {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        if (permission.state === 'denied') {
          alert("📍 Géolocalisation bloquée\n\nPour faciliter la saisie de votre adresse de départ, veuillez autoriser la géolocalisation dans les paramètres de votre téléphone ou de votre navigateur.");
        } else if (permission.state === 'prompt') {
          navigator.geolocation.getCurrentPosition(() => {}, () => {}, { timeout: 10000 });
        }
      } catch (e) {
        // Fallback pour les navigateurs ne supportant pas l'API Permissions
        navigator.geolocation.getCurrentPosition(
          () => {}, 
          (error) => {
            if (error.code === error.PERMISSION_DENIED) {
              alert("📍 Géolocalisation bloquée\n\nPour faciliter la saisie de votre adresse de départ, veuillez autoriser la géolocalisation dans les paramètres de votre téléphone ou de votre navigateur.");
            }
          }, 
          { timeout: 10000 }
        );
      }
    } else if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        () => {}, 
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            alert("📍 Géolocalisation bloquée\n\nPour faciliter la saisie de votre adresse de départ, veuillez autoriser la géolocalisation dans les paramètres de votre téléphone ou de votre navigateur.");
          }
        }, 
        { timeout: 10000 }
      );
    }
  };

  const handleGeneratePackContent = async (sector: string) => {
    if (!aiPackPrompt.trim()) return;
    setIsAiGeneratingPack(true);
    try {
      const content = await generatePackContent(sector, aiPackPrompt);
      if (content) {
        const configs = { ...(appData.settings.sectorConfigs || {}) };
        configs[sector] = { 
          ...(configs[sector] || {}), 
          title: content.title,
          description: content.description
        };
        updateSettings({ ...appData.settings, sectorConfigs: configs });
        setAiPackPrompt('');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsAiGeneratingPack(false);
    }
  };

  const handleGenerateAllPacksContent = async () => {
    if (!aiPackPrompt.trim()) return;
    setIsAiGeneratingAllPacks(true);
    try {
      const configs = { ...(appData.settings.sectorConfigs || {}) };
      
      // Process sectors sequentially to avoid rate limits and ensure stability
      for (const sector of TOUR_SECTORS) {
        const content = await generatePackContent(sector, aiPackPrompt);
        if (content) {
          configs[sector] = { 
            ...(configs[sector] || {}), 
            title: content.title,
            description: content.description
          };
        }
      }
      
      updateSettings({ ...appData.settings, sectorConfigs: configs });
      setAiPackPrompt('');
      alert("Tous les titres et descriptifs des packs ont été générés avec succès.");
    } catch (error) {
      console.error(error);
      alert("Une erreur est survenue lors de la génération globale.");
    } finally {
      setIsAiGeneratingAllPacks(false);
    }
  };

  const handleSearchCircuitByAI = async (overridePrompt?: string, overrideLimit?: string) => {
    const promptValue = (overridePrompt ?? aiCircuitSearchPrompt).trim();
    if (!promptValue) return;
    
    const limitValue = parseInt(overrideLimit ?? aiSearchLimit) || 40;
    
    // Update global state so it's consistent
    setAiCircuitSearchPrompt(promptValue);
    setAiSearchLimit(limitValue.toString());
    
    setIsAiSearchingCircuit(true);
    
    try {
      const result = await searchCircuitByAI(promptValue, aiSearchSector, appData.sites, limitValue);
      
      // Verify isExisting and existingId against real data
      if (result && result.propositions) {
        result.propositions = result.propositions.map((prop: any) => {
          // Check if it exists in the current sector by name or ID
          const existingSite = appData.sites.find(s => 
            (prop.existingId && s.id === prop.existingId) || 
            (s.sector === aiSearchSector && s.name.toLowerCase() === prop.name.toLowerCase())
          );
          
          if (existingSite) {
            return {
              ...prop,
              isExisting: true,
              existingId: existingSite.id,
              selected: false
            };
          } else {
            return {
              ...prop,
              isExisting: false,
              existingId: null,
              selected: true
            };
          }
        });
      }
      
      setAiSearchResult((prevResult: AiSearchResult | null) => {
        if (!prevResult) return result;
        
        // Merge and deduplicate
        const newPropositions = [...(prevResult?.propositions || []), ...(result?.propositions || [])];
        const uniquePropositions = newPropositions.filter((prop, index, self) =>
          index === self.findIndex((p) => p.name.toLowerCase() === prop.name.toLowerCase())
        );
        
        return {
          ...prevResult,
          propositions: uniquePropositions,
          sectorTitle: result?.sectorTitle || (prevResult ? prevResult.sectorTitle : undefined),
          sectorDescription: result?.sectorDescription || (prevResult ? prevResult.sectorDescription : undefined)
        };
      });

      // Also update the sector configuration in settings if AI returned a title/description
      if (result?.sectorTitle || result?.sectorDescription) {
        const configs = { ...(appData.settings.sectorConfigs || {}) };
        configs[aiSearchSector] = { 
          ...(configs[aiSearchSector] || {}), 
          title: result.sectorTitle || configs[aiSearchSector]?.title || '',
          description: result.sectorDescription || configs[aiSearchSector]?.description || ''
        };
        updateSettings({ ...appData.settings, sectorConfigs: configs });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsAiSearchingCircuit(false);
    }
  };

  const getPassengerCount = () => {
    const l = tourQuestionnaire.logistics;
    let count = l.seniors + l.adults + l.children6_12 + l.children3_6 + l.babies;
    // Animals count: Mini: 0, Petit/Moyen: 1, Gros: 2
    if (l.hasAnimal && l.animals > 0) {
      const sizeWeights: Record<string, number> = { mini: 0, petit: 1, moyen: 1, gros: 2 };
      count += l.animals * (sizeWeights[l.animalSize] || 0);
    }
    return count;
  };

  const canAddPassenger = () => getPassengerCount() < 6;

  const resetTouristCircuits = () => {
    setTourStep(1);
    setTourQuestionnaire({
      date: new Date().toISOString().split('T')[0],
      pickupLocation: null,
      accommodationLocation: null,
      departureTime: '09:00',
      hasAnimal: null,
      animalRace: '',
      animalSize: null,
      animalCount: 0,
      accommodationPreference: null,
      motionSickness: false,
      pmr: false,
      passengers: {
        seniors: 0,
        adults: 0,
        children6_12: 0,
        children3_6: 0,
        babies: 0,
      },
      preferences: {
        patrimoine: null,
        nature: null,
        activites: null,
        randonnee: null,
        points_de_vue: null,
        terroir: null,
        insolite: null,
        baignade: null,
        grotte: null,
        detente: null,
      },
      catering: {
        type: null,
      },
      sector: null,
      selectedActivities: [],
    });
    localStorage.removeItem('vtc_tour_step_v2');
    localStorage.removeItem('vtc_tour_questionnaire');
  };

  const calculateActivityScore = (questionnaire: TourQuestionnaire, site: TouristSite): { score: 'VERT' | 'JAUNE' | 'ORANGE' | 'ROUGE', reason: string, points: number } => {
    let score: 'VERT' | 'JAUNE' | 'ORANGE' | 'ROUGE' = 'JAUNE';
    let reason = '';
    let points = 500;

    const hasChildrenUnder6 = (questionnaire.passengers.children3_6 || 0) > 0 || (questionnaire.passengers.babies || 0) > 0;
    const isPmr = questionnaire.pmr === true;
    const hasDog = questionnaire.hasAnimal === true;
    const hasMotionSickness = questionnaire.motionSickness === true;

    // ROUGE - Inadapté
    if (hasChildrenUnder6 && site.notSuitableForChildren) {
      return { score: 'ROUGE', reason: 'Inadapté : Site déconseillé aux jeunes enfants', points: 0 };
    }
    if (isPmr && !site.isHandicapAccessible) {
      return { score: 'ROUGE', reason: 'Inadapté : Site non accessible PMR', points: 0 };
    }
    if ((questionnaire.catering.type === 'pique-nique' || questionnaire.catering.type === 'snack') && site.cateringType === 'restaurant') {
      return { score: 'ROUGE', reason: 'Inadapté : Vous souhaitez un repas rapide, ce site est un restaurant classique', points: 0 };
    }
    if (questionnaire.catering.type === 'restaurant' && site.cateringType === 'snack') {
      return { score: 'ROUGE', reason: 'Inadapté : Vous souhaitez un restaurant, ce site propose uniquement de la restauration rapide', points: 0 };
    }

    // ORANGE - Moins adapté
    if (hasDog && site.dogsNotAllowed) {
      score = 'ORANGE';
      reason = 'Moins adapté : Chiens non admis sur ce site';
      points = 100;
    } else if (hasMotionSickness && site.isWindingRoad) {
      score = 'ORANGE';
      reason = 'Moins adapté : Route sinueuse pour accéder au site';
      points = 100;
    } else if (questionnaire.catering.type === 'restaurant' && site.cateringType === 'restaurant' && site.forks && questionnaire.catering.standing) {
      const diff = Math.abs(site.forks - questionnaire.catering.standing);
      if (diff >= 2) {
        score = 'ORANGE';
        reason = 'Moins adapté : Standing du restaurant très différent de votre souhait';
        points = 100;
      }
    } else if (questionnaire.catering.type === 'pique-nique' && site.cateringType === 'snack') {
      score = 'ORANGE';
      reason = 'Moins adapté : Vous préférez un pique-nique, ce site propose un snack';
      points = 100;
    }

    // Check categories match
    const siteCategories = site.categories || [];
    const userPrefs = questionnaire.preferences;
    let bestPrefRank = 9;
    let hasAnyMatch = false;

    Object.entries(userPrefs).forEach(([cat, rank]) => {
      if (rank !== null && siteCategories.includes(cat)) {
        hasAnyMatch = true;
        if (rank < bestPrefRank) bestPrefRank = rank;
      }
    });

    if (!hasAnyMatch && score !== 'ORANGE') {
      score = 'ORANGE';
      reason = "Moins adapté : Ne correspond à aucune de vos catégories préférées";
      points = 100;
    }

    if (score === 'ORANGE') return { score, reason, points };

    // VERT - Idéal
    if (bestPrefRank === 1) {
      return { score: 'VERT', reason: 'Idéal : Correspond à votre choix N°1', points: 1000 };
    }
    const userCateringType = questionnaire.catering.type;
    if (userCateringType === (site.cateringType as string) && site.cateringType !== 'none') {
      if (site.cateringType === 'restaurant') {
        if (site.forks === questionnaire.catering.standing) {
          return { score: 'VERT', reason: 'Idéal : Type de restauration et standing parfaits', points: 1000 };
        }
      } else {
        return { score: 'VERT', reason: 'Idéal : Type de restauration parfait', points: 1000 };
      }
    }

    // JAUNE - Adapté
    if (bestPrefRank >= 2 && bestPrefRank <= 8) {
      return { score: 'JAUNE', reason: `Adapté : Correspond à votre choix N°${bestPrefRank}`, points: 500 + (10 - bestPrefRank) * 10 };
    }
    if (questionnaire.catering.type === 'restaurant' && site.cateringType === 'restaurant' && site.forks && questionnaire.catering.standing) {
      if (Math.abs(site.forks - questionnaire.catering.standing) === 1) {
        return { score: 'JAUNE', reason: 'Adapté : Standing du restaurant proche de votre souhait', points: 550 };
      }
    }

    return { score: 'JAUNE', reason: 'Adapté à votre profil', points: 500 };
  };

  const handleAnalyzeActivities = async (sector: string) => {
    setTourQuestionnaire(prev => ({ ...prev, sector }));
    setIsAnalyzingActivities(true);
    setTourStep(6); // Go to results step
    setSelectedAnalyzedActivityIds([]);
    
    try {
      const sectorActivities = appData.sites.filter(s => {
        const siteCategories = (s.categories || []).map(c => c.toLowerCase().trim());
        const isRestauration = siteCategories.includes('restauration') || s.cateringType === 'restaurant' || s.cateringType === 'snack';
        const isAccommodation = siteCategories.includes('hebergement') || s.isAccommodation === true;
        
        return s.sector === sector && 
               s.isVisibleClient && 
               !isRestauration && 
               !isAccommodation;
      });
      
      const mergedActivities = sectorActivities.map(site => {
        const { score, reason, points } = calculateActivityScore(tourQuestionnaire, site);
        
        // Calculate total estimated price for the group
        const adultPrice = site.adultPrice || 0;
        const child6_12Price = site.child6_12Price || 0;
        const child3_6Price = site.child3_6Price || 0;
        const babyPrice = site.babyPrice || 0;
        
        const totalEstimatedPrice = 
          (tourQuestionnaire.passengers.adults * adultPrice) +
          (tourQuestionnaire.passengers.children6_12 * child6_12Price) +
          (tourQuestionnaire.passengers.children3_6 * child3_6Price) +
          (tourQuestionnaire.passengers.babies * babyPrice);

        return {
          ...site,
          score,
          reason,
          totalEstimatedPrice,
          points,
          dogAlert: site.dogsNotAllowed ? 'Chiens interdits' : ''
        } as AnalyzedActivity;
      });

      // Sort by points (High to Low)
      // then by Google Rating (High to Low)
      // then by Price (Low to High)
      mergedActivities.sort((a, b) => {
        if (b.points !== a.points) {
          return b.points - a.points;
        }
        const ratingA = a.googleRating || 0;
        const ratingB = b.googleRating || 0;
        if (ratingB !== ratingA) {
          return ratingB - ratingA;
        }
        return a.totalEstimatedPrice - b.totalEstimatedPrice;
      });

      setAnalyzedActivities(mergedActivities);
    } catch (error) {
      console.error("Error during activity analysis:", error);
    } finally {
      setIsAnalyzingActivities(false);
    }
  };

  const handleToggleActivitySelection = (id: string) => {
    setSelectedAnalyzedActivityIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleTogglePriority = (id: string) => {
    setPrioritizedActivityIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  // Threshold for "too many" activities

  const [driverHomeCoords, setDriverHomeCoords] = useState<google.maps.LatLngLiteral | null>({ lat: 43.923, lng: 3.847 }); // Default to Cazilhac

  useEffect(() => {
    const updateHomeCoords = async () => {
      if (appData.settings.driverHomeAddress && appData.settings.driverHomeAddress !== 'Cazilhac 34190, France') {
        const coords = await geocodeAddress(appData.settings.driverHomeAddress);
        if (coords) setDriverHomeCoords(coords);
      }
    };
    updateHomeCoords();
  }, [appData.settings.driverHomeAddress]);


  const handleAddSuggestedSite = async (suggestion: any) => {
    setIsGenerating(true);
    const content = {
      description: "Un lieu touristique à découvrir.",
      fullDescription: "Une description plus détaillée sera ajoutée ultérieurement.",
      adultPrice: 0,
      child6_12Price: 0,
      child3_6Price: 0,
      babyPrice: 0,
      isPaid: false,
      openingHours: "9h00 - 18h00",
      dogsNotAllowed: false,
      isHandicapAccessible: true,
      accessibility: "Accessible",
      phone: "",
      website: "",
      address: suggestion.name,
      gps: "43.923,3.847",
      image: suggestion.photoUri || `https://loremflickr.com/800/600/${suggestion.name.replace(/\s+/g, ',')}`,
      tags: suggestion.tags || [],
      categories: suggestion.category ? [suggestion.category] : ['nature'],
      googleRating: suggestion.googleRating || 4.5,
      googleReviewsCount: suggestion.googleReviewsCount || 0
    };
    const newSite: TouristSite = {
      id: Date.now().toString(),
      name: suggestion.name,
      location: { 
        lat: 43.923 + (Math.random() - 0.5) * 0.1, 
        lng: 3.847 + (Math.random() - 0.5) * 0.1, 
        address: suggestion.name 
      },
      description: content.description || suggestion.description,
      image: content.image,
      tags: content.tags,
      categories: content.categories,
      isPaid: content.isPaid ?? false,
      adultPrice: content.adultPrice || 0,
      child6_12Price: content.child6_12Price || 0,
      child3_6Price: 0,
      babyPrice: 0,
      sector: 'Vis et Larzac', // Default sector
      isVisibleClient: false,
      googleRating: content.googleRating || 4.5,
      googleReviewsCount: content.googleReviewsCount || 0
    };
    const updated = [...appData.sites, newSite];
    setAppData(prev => ({ ...prev, sites: updated }));
    setSuggestedSites(prev => prev.filter(s => s.name !== suggestion.name));
    setIsGenerating(false);
  };

  const updateSettings = (newSettings: Settings) => {
    setAppData(prev => ({ ...prev, settings: newSettings }));
  };

  const generateAIText = async () => {
    setIsGeneratingText(true);
    try {
      // AI generation disabled
      console.log("AI Text generation disabled");
      /*
      const result = await withRetry(async () => {
        const ai = new GoogleGenAI({ apiKey: process.env.GEMINI_API_KEY });
        const response = await ai.models.generateContent({
          model: "gemini-3-flash-preview",
          contents: "Génère un message d'accueil court et accrocheur (max 200 caractères) et un texte de bouton court (max 25 caractères) pour une page de réservation de circuits touristiques en VTC dans les Cévennes. Le ton doit être professionnel et invitant.",
          config: {
            responseMimeType: "application/json",
            responseSchema: {
              type: Type.OBJECT,
              properties: {
                welcomeMessage: { type: Type.STRING },
                buttonText: { type: Type.STRING }
              },
              required: ["welcomeMessage", "buttonText"]
            }
          }
        });
        return response;
      }, 15, 12000, "Gemini Welcome Text");
      
      const parsed = JSON.parse(result.text);
      updateSettings({
        ...appData.settings,
        touristCircuitsText: parsed.welcomeMessage,
        touristCircuitsBtnText: parsed.buttonText
      });
      */
    } catch (error) {
      console.error("Error generating AI text", error);
      alert("Erreur lors de la génération du texte par l'IA.");
    } finally {
      setIsGeneratingText(false);
    }
  };

  const saveAppData = async (dataToSave: any) => {
    setIsSavingSettings(true);
    
    // Fetch current data to merge (keep users and bookings)
    try {
      const currentData = await storageService.loadData();
      
      // Helper pour fusionner des tableaux d'objets par ID (évite l'écrasement)
      const mergeById = (serverArr: any[] = [], localArr: any[] = []) => {
        const dict: Record<string, any> = {};
        (serverArr || []).forEach(item => { if (item.id) dict[item.id] = item; });
        (localArr || []).forEach(item => { if (item.id) dict[item.id] = item; });
        return Object.values(dict);
      };

      const mergedData = {
        ...currentData,
        settings: {
          ...currentData.settings, // On garde d'abord tout ce qui est sur le serveur
          ...dataToSave.settings, // On écrase avec les nouvelles valeurs
          // SÉCURITÉ CRITIQUE : On préserve absolument l'image d'accueil si elle n'est pas fournie dans dataToSave
          heroImageBase64: dataToSave.settings?.heroImageBase64 || currentData.settings?.heroImageBase64,
          heroImageLastUpdate: dataToSave.settings?.heroImageLastUpdate || currentData.settings?.heroImageLastUpdate
        },
        sites: mergeById(currentData.sites, dataToSave.sites),
        packs: mergeById(currentData.packs, dataToSave.packs),
        users: mergeById(currentData.users, dataToSave.users),
        bookings: mergeById(currentData.bookings, dataToSave.bookings),
        clients: mergeById(currentData.clients, dataToSave.clients),
        invoices: mergeById(currentData.invoices, dataToSave.invoices),
        activities: mergeById(currentData.activities, dataToSave.activities)
      };
      await storageService.saveData(mergedData);
      setSaveSettingsSuccess(true);
      setTimeout(() => setSaveSettingsSuccess(false), 3000);
      return mergedData;
    } catch (err) {
      console.error("Error saving data:", err);
      const errorMsg = err instanceof Error ? err.message : String(err);
      try {
        alert(`Erreur lors de l'enregistrement des données: ${errorMsg}`);
      } catch (e) {
        console.warn("Alert blocked", e);
      }
      throw err;
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      const savedData = await saveAppData(appData);
      setAppData(savedData);
      setSaveSettingsSuccess(true);
      setTimeout(() => setSaveSettingsSuccess(false), 3000);
    } catch (error) {
      console.error("Error saving settings:", error);
      try {
        alert("Erreur lors de la sauvegarde des paramètres.");
      } catch (e) {
        console.warn("Alert blocked", e);
      }
    }
  };

  // Intelligent approach calculation
  useEffect(() => {
    if (!pickup || !hasValidKey || !window.google) return;

    // Fixed origin: 1 Domaine de la Séranne, 34190 Cazilhac
    const fixedOrigin = { lat: 43.918, lng: 3.702 };

    const calculateApproach = (target: Location) => {
      const service = new window.google.maps.DistanceMatrixService();
      service.getDistanceMatrix({
        origins: [fixedOrigin],
        destinations: [target],
        travelMode: window.google.maps.TravelMode.DRIVING,
      }, (response, status) => {
        if (status === 'OK' && response?.rows[0].elements[0] && response.rows[0].elements[0].status === 'OK') {
          const element = response.rows[0].elements[0];
          setApproachInfo({
            distance: element.distance.text,
            duration: element.duration.text
          });
        }
      });
    };

    if (destination) {
      // Find closest point on route pickup -> destination
      const directionsService = new window.google.maps.DirectionsService();
      directionsService.route({
        origin: { lat: pickup.lat, lng: pickup.lng },
        destination: { lat: destination.lat, lng: destination.lng },
        travelMode: window.google.maps.TravelMode.DRIVING,
      }, (result, status) => {
        if (status === 'OK' && result?.routes[0]?.overview_path) {
          const path = result.routes[0].overview_path;
          let minDistance = Infinity;
          let closestPoint = { lat: pickup.lat, lng: pickup.lng };

          path.forEach(point => {
            const d = Math.sqrt(
              Math.pow(point.lat() - fixedOrigin.lat, 2) + 
              Math.pow(point.lng() - fixedOrigin.lng, 2)
            );
            if (d < minDistance) {
              minDistance = d;
              closestPoint = { lat: point.lat(), lng: point.lng() };
            }
          });

          calculateApproach(closestPoint);
        } else {
          calculateApproach(pickup);
        }
      });
    } else {
      calculateApproach(pickup);
    }
  }, [pickup, destination, hasValidKey]);

  // Calculate trip distance/time when pickup or destination is set
  useEffect(() => {
    if (!pickup || !destination || !hasValidKey || !window.google) return;

    const service = new window.google.maps.DistanceMatrixService();
    service.getDistanceMatrix({
      origins: [pickup],
      destinations: [destination],
      travelMode: window.google.maps.TravelMode.DRIVING,
    }, (response, status) => {
      if (status === 'OK' && response?.rows[0].elements[0] && response.rows[0].elements[0].status === 'OK') {
        const element = response.rows[0].elements[0];
        setTripInfo({
          distance: element.distance.text,
          duration: element.duration.text
        });
      }
    });
  }, [pickup, destination, hasValidKey]);

  const [isBooking, setIsBooking] = useState(false);

  const handleTourBooking = async (circuit?: any, overrideUser?: User) => {
    const finalUser = overrideUser || user;
    if (!socket || !circuit) return;
    
    if (!finalUser) {
      setPendingTourCircuit(circuit);
      setPendingAction('book_custom_circuit');
      setView('profile');
      return;
    }
    
    setIsBooking(true);
    try {
      socket.emit('request_booking', {
        type: 'tour',
        pickup: tourQuestionnaire.pickupLocation,
        scheduledDate: tourQuestionnaire.date,
        scheduledTime: tourQuestionnaire.departureTime,
        timestamp: Date.now(),
        customerId: finalUser.id,
        price: circuit.price,
        status: 'pending',
        paymentStatus: 'pending_at_board',
        paymentMethod: 'cash',
        params: {
          questionnaire: tourQuestionnaire,
          circuit: circuit
        }
      });
      
      setTimeout(() => {
        setView('tour_confirmation');
        setPendingTourCircuit(null);
        setPendingAction(null);
      }, 1000);
    } finally {
      setTimeout(() => setIsBooking(false), 1000);
    }
  };

  const handleBooking = async (overrideUser?: User) => {
    const finalUser = overrideUser || user;
    if (!pickup || !destination || !socket) return;
    
    if (!finalUser) {
      setPendingAction('book_vtc');
      setView('profile');
      return;
    }
    
    setIsBooking(true);
    try {
      const priceInfo = calculatePrice();
      
      socket.emit('request_booking', {
        type: 'vtc',
        pickup,
        destination,
        scheduledDate,
        scheduledTime,
        tripDistance: tripInfo?.distance || "0 km",
        approachDistance: approachInfo?.distance || "0 km",
        timestamp: Date.now(),
        requestSms,
        customerId: finalUser.id,
        price: priceInfo.total,
        basePrice: priceInfo.base,
        isNight: priceInfo.isNight,
        nightSurcharge: priceInfo.nightSurcharge,
        discountAmount: priceInfo.discount,
        status: 'pending',
        paymentStatus: 'pending_at_board',
        paymentMethod: 'cash'
      });
      
      setTimeout(() => {
        setTripValidated(false);
        setView('status');
      }, 1000);
    } finally {
      setTimeout(() => setIsBooking(false), 1000);
    }
  };

  const calculatePrice = () => {
    if (!approachInfo) return { total: 15, base: 15, isNight: false, nightSurcharge: 0, discount: 0 };
    const approachKm = parseFloat(approachInfo.distance.replace(',', '.'));
    const tripKm = tripInfo ? parseFloat(tripInfo.distance.replace(',', '.')) : 0;
    const totalKm = tripKm + approachKm;
    const rate = appData.settings.kmRate || 2.15;
    const basePrice = totalKm * rate;
    let price = basePrice;
    let isNight = false;
    let nightSurcharge = 0;
    let discount = 0;

    // Night tariff
    const checkNight = (h: number) => {
      const startHour = parseInt(appData.settings.nightStart.split(':')[0]);
      const endHour = parseInt(appData.settings.nightEnd.split(':')[0]);
      return startHour > endHour 
        ? (h >= startHour || h < endHour)
        : (h >= startHour && h < endHour);
    };

    const hour = scheduledTime ? parseInt(scheduledTime.split(':')[0]) : new Date().getHours();
    isNight = checkNight(hour);
    
    if (isNight) {
      const surchargeRate = appData.settings.nightRateIncrease / 100;
      nightSurcharge = basePrice * surchargeRate;
      price += nightSurcharge;
    }
    
    // Discount
    if (price > appData.settings.discountThreshold) {
      const discountRate = appData.settings.discountPercentage / 100;
      discount = price * discountRate;
      price -= discount;
    }

    return {
      total: Math.max(15, Math.round(price * 100) / 100),
      base: Math.round(basePrice * 100) / 100,
      isNight,
      nightSurcharge: Math.round(nightSurcharge * 100) / 100,
      discount: Math.round(discount * 100) / 100
    };
  };

  const isCardRequired = () => {
    if (!approachInfo) return false;
    const approachKm = parseFloat(approachInfo.distance.replace(',', '.'));
    return approachKm > 10;
  };

  const handleAcceptBooking = () => {
    socket?.emit('accept_booking');
  };

  const handleValidateBooking = () => {
    socket?.emit('validate_booking');
  };

  const handleStartTrip = () => {
    socket?.emit('start_trip');
  };

  const handleCancelBooking = () => {
    socket?.emit('cancel_booking');
  };

  const openWaze = () => {
    if (!activeBooking) return;
    const target = activeBooking.status === 'accepted' || activeBooking.status === 'approaching' 
      ? activeBooking.pickup 
      : activeBooking.destination;
    if (target) {
      window.open(`https://www.waze.com/ul?ll=${target.lat},${target.lng}&navigate=yes`, '_blank');
    }
  };

  const onPaymentSuccess = () => {
    if (!socket || !pendingBooking) return;
    
    setIsBooking(true);
    socket.emit('request_booking', {
      ...pendingBooking,
      paymentStatus: 'paid'
    });
    
    setPendingBooking(null);
    setIsBooking(false);
  };

  const handleBookTour = (tour: Tour, overrideUser?: User) => {
    const finalUser = overrideUser || user;
    if (!socket) return;
    
    if (!finalUser) {
      setPendingAction('book_tour');
      setPendingTour(tour);
      setView('profile');
      return;
    }
    
    setIsCalculating(true);
    try {
      socket.emit('request_booking', {
        type: 'tour',
        tourId: tour.id,
        pickup: { lat: 44.127, lng: 4.083, address: "Hôtel de Ville d'Alès" },
        timestamp: Date.now(),
        requestSms,
        customerId: finalUser.id,
        price: tour.price,
        status: 'pending',
        paymentStatus: 'pending',
        paymentMethod: 'cash'
      });
    } finally {
      setTimeout(() => setIsCalculating(false), 1000);
    }
  };

  const [customTourStep, setCustomTourStep] = useState(1);
  const [viewingActivityDetail, setViewingActivityDetail] = useState<any>(null);
  const [viewingItinerary, setViewingItinerary] = useState(false);

  const CUSTOM_TOUR_THEMES = [
    { id: 'nature', label: 'Nature & Panoramas', icon: <Trees className="w-4 h-4" />, prompt: 'Nature, panoramas, parcs naturels' },
    { id: 'gastronomy', label: 'Gastronomie & Terroir', icon: <UtensilsCrossed className="w-4 h-4" />, prompt: 'Gastronomie, dégustation, produits locaux, pélardon' },
    { id: 'history', label: 'Histoire & Patrimoine', icon: <Landmark className="w-4 h-4" />, prompt: 'Histoire, châteaux, villages médiévaux, Uzès' },
    { id: 'adventure', label: 'Aventure & Sport', icon: <Mountain className="w-4 h-4" />, prompt: 'Aventure, sport, canoë, rando-rail' },
    { id: 'culture', label: 'Culture & Musées', icon: <Palette className="w-4 h-4" />, prompt: 'Culture, musées, artisanat' },
    { id: 'relaxation', label: 'Détente & Baignade', icon: <Footprints className="w-4 h-4" />, prompt: 'Détente, baignade, cascades, rivières' },
  ];

  const SUB_ACTIVITIES: Record<string, { 
    id: string; 
    label: string; 
    priceAdult: number; 
    priceChild: number; 
    description: string; 
    longDescription?: string;
    image?: string;
    familyFriendly?: boolean;
    googleRating?: number;
  }[]> = {
    nature: [
      { 
        id: 'cirque_navacelles', 
        label: 'Cirque de Navacelles', 
        priceAdult: 0, 
        priceChild: 0, 
        description: 'Grand Site de France, panorama exceptionnel.', 
        longDescription: 'Le Cirque de Navacelles est un cirque naturel du Massif central, situé dans le département du Gard. C\'est un site classé au patrimoine mondial de l\'UNESCO.',
        image: 'https://picsum.photos/seed/navacelles/800/600',
        familyFriendly: true 
      },
      { 
        id: 'lac_salagou', 
        label: 'Lac du Salagou', 
        priceAdult: 0, 
        priceChild: 0, 
        description: 'Paysages rouges lunaires, idéal balade.', 
        longDescription: 'Le lac du Salagou est un lac de retenue du barrage du Salagou. Il est situé au centre du département de l\'Hérault. Ses terres rouges (les ruffes) offrent un paysage unique.',
        image: 'https://picsum.photos/seed/salagou/800/600',
        familyFriendly: true 
      },
      { 
        id: 'corniche_cevennes', 
        label: 'Corniche des Cévennes', 
        priceAdult: 0, 
        priceChild: 0, 
        description: 'Route panoramique historique.', 
        longDescription: 'La Corniche des Cévennes est une route touristique qui relie Saint-Jean-du-Gard à Florac. Elle offre des points de vue spectaculaires sur les vallées cévenoles.',
        image: 'https://picsum.photos/seed/cevennes/800/600'
      },
      { 
        id: 'mont_aigoual', 
        label: 'Observatoire de l\'Aigoual', 
        priceAdult: 0, 
        priceChild: 0, 
        description: 'Point culminant, vue à 360°.', 
        longDescription: 'Le mont Aigoual est le point culminant du département du Gard. Son observatoire météo est le dernier habité en France. Vue imprenable jusqu\'aux Alpes et aux Pyrénées par temps clair.',
        image: 'https://picsum.photos/seed/aigoual/800/600',
        familyFriendly: true 
      },
    ],
    gastronomy: [
      { 
        id: 'degustation_vin', 
        label: 'Dégustation de Vin (Cave)', 
        priceAdult: 0, 
        priceChild: 0, 
        description: 'Visite de cave et dégustation gratuite.', 
        longDescription: 'Découvrez les vins du Languedoc et des Cévennes. Visite des chais et dégustation des meilleurs crus locaux.',
        image: 'https://picsum.photos/seed/wine/800/600'
      },
      { 
        id: 'pelardon', 
        label: 'Fromagerie Pélardon', 
        priceAdult: 8, 
        priceChild: 4, 
        description: 'Découverte du célèbre fromage de chèvre.', 
        longDescription: 'Le Pélardon est un fromage de chèvre au lait cru, AOP, typique des Cévennes. Visitez une ferme traditionnelle et dégustez les différentes affinages.',
        image: 'https://picsum.photos/seed/cheese/800/600',
        familyFriendly: true 
      },
      { 
        id: 'marche_local', 
        label: 'Marché Local (Uzès/Alès)', 
        priceAdult: 0, 
        priceChild: 0, 
        description: 'Produits du terroir et ambiance cévenole.', 
        longDescription: 'Les marchés des Cévennes sont réputés pour leur authenticité. Celui d\'Uzès, sur la place aux Herbes, est l\'un des plus beaux de France.',
        image: 'https://picsum.photos/seed/market/800/600',
        familyFriendly: true 
      },
    ],
    history: [
      { 
        id: 'pont_du_gard', 
        label: 'Pont du Gard', 
        priceAdult: 9.5, 
        priceChild: 0, 
        description: 'Aqueduc romain majestueux.', 
        longDescription: 'Le Pont du Gard est un pont-aqueduc romain à trois niveaux, situé à Vers-Pont-du-Gard. Chef-d\'œuvre de l\'ingénierie antique, il est classé à l\'UNESCO.',
        image: 'https://picsum.photos/seed/pontdugard/800/600',
        familyFriendly: true 
      },
      { 
        id: 'arenes_nimes', 
        label: 'Arènes de Nîmes', 
        priceAdult: 10, 
        priceChild: 5, 
        description: 'Amphithéâtre romain le mieux conservé.', 
        longDescription: 'L\'amphithéâtre de Nîmes est l\'un des mieux conservés au monde. Il témoigne de la grandeur de la Nîmes romaine.',
        image: 'https://picsum.photos/seed/nimes/800/600',
        familyFriendly: true 
      },
      { 
        id: 'st_guilhem', 
        label: 'Saint-Guilhem-le-Désert', 
        priceAdult: 0, 
        priceChild: 0, 
        description: 'Un des plus beaux villages de France.', 
        longDescription: 'Étape sur le chemin de Saint-Jacques-de-Compostelle, ce village médiéval est niché dans les gorges de l\'Hérault. Son abbaye est un joyau de l\'art roman.',
        image: 'https://picsum.photos/seed/stguilhem/800/600',
        familyFriendly: true 
      },
      { 
        id: 'uzes', 
        label: 'Duché d\'Uzès', 
        priceAdult: 12, 
        priceChild: 6, 
        description: 'Premier duché de France, ville médiévale.', 
        longDescription: 'Uzès est une ville d\'art et d\'histoire. Son château ducal, ses tours médiévales et ses ruelles pavées en font une destination incontournable.',
        image: 'https://picsum.photos/seed/uzes/800/600'
      },
    ],
    adventure: [
      { 
        id: 'canoe', 
        label: 'Canoë Montana (Gorges de l\'Hérault)', 
        priceAdult: 25, 
        priceChild: 15, 
        description: 'Descente ludique en rivière.', 
        longDescription: 'Canoë Montana vous propose des parcours adaptés à tous dans le cadre grandiose des Gorges de l\'Hérault. Une aventure rafraîchissante en famille ou entre amis.',
        image: 'https://picsum.photos/seed/canoe/800/600',
        familyFriendly: true,
        googleRating: 4.8
      },
      { 
        id: 'rando_rail', 
        label: 'Rando-Rail', 
        priceAdult: 15, 
        priceChild: 10, 
        description: 'Parcours insolite sur une ancienne voie ferrée.', 
        longDescription: 'Pédalez sur une ancienne voie ferrée à travers les paysages cévenols. Une activité ludique et originale pour toute la famille.',
        image: 'https://picsum.photos/seed/randorail/800/600',
        familyFriendly: true,
        googleRating: 4.4
      },
      { 
        id: 'grotte_demoiselles', 
        label: 'Grotte des Demoiselles', 
        priceAdult: 14, 
        priceChild: 9, 
        description: 'Cathédrale des abîmes, impressionnant.', 
        longDescription: 'Située dans la haute vallée de l\'Hérault, cette grotte offre des dimensions gigantesques et des concrétions spectaculaires. Un funiculaire vous y emmène sans effort.',
        image: 'https://picsum.photos/seed/grotte/800/600',
        familyFriendly: true,
        googleRating: 4.7
      },
      { 
        id: 'grotte_trabuc', 
        label: 'Grotte de Trabuc', 
        priceAdult: 13.5, 
        priceChild: 8.5, 
        description: 'La grotte aux 100 000 soldats.', 
        longDescription: 'Située près d\'Anduze, cette grotte est célèbre pour son mystérieux phénomène des "100 000 soldats", des milliers de petites stalagmites uniques au monde.',
        image: 'https://picsum.photos/seed/trabuc/800/600',
        familyFriendly: true,
        googleRating: 4.6
      },
      { 
        id: 'grotte_clamouse', 
        label: 'Grotte de Clamouse', 
        priceAdult: 14.5, 
        priceChild: 9.5, 
        description: 'Merveille souterraine près de St-Guilhem.', 
        longDescription: 'Située à deux pas de Saint-Guilhem-le-Désert, Clamouse est une référence mondiale pour la diversité de ses cristaux et ses illuminations spectaculaires.',
        image: 'https://picsum.photos/seed/clamouse/800/600',
        familyFriendly: true,
        googleRating: 4.5
      },
      { 
        id: 'grotte_dargilan', 
        label: 'Grotte de Dargilan', 
        priceAdult: 13, 
        priceChild: 8, 
        description: 'La Grotte Rose des Cévennes.', 
        longDescription: 'Sur le Causse Noir, Dargilan impressionne par ses dimensions et ses couleurs naturelles uniques. Une vue imprenable sur les Gorges de la Jonte.',
        image: 'https://picsum.photos/seed/dargilan/800/600',
        familyFriendly: true,
        googleRating: 4.6
      },
      { 
        id: 'abime_bramabiau', 
        label: 'Abîme de Bramabiau', 
        priceAdult: 11, 
        priceChild: 7, 
        description: 'Rivière souterraine spectaculaire.', 
        longDescription: 'Une véritable rivière qui traverse la montagne. Une visite rafraîchissante et impressionnante au cœur du massif de l\'Aigoual.',
        image: 'https://picsum.photos/seed/bramabiau/800/600',
        familyFriendly: true,
        googleRating: 4.6
      },
      { 
        id: 'via_ferrata', 
        label: 'Via Ferrata', 
        priceAdult: 45, 
        priceChild: 35, 
        description: 'Itinéraire sportif sur paroi rocheuse.', 
        longDescription: 'Prenez de la hauteur sur les parois rocheuses des Cévennes. Un parcours sécurisé mêlant escalade et randonnée verticale.',
        image: 'https://picsum.photos/seed/climbing/800/600',
        googleRating: 4.9
      },
      { 
        id: 'canyoning', 
        label: 'Canyoning', 
        priceAdult: 50, 
        priceChild: 40, 
        description: 'Sauts, toboggans et rappels en rivière.', 
        longDescription: 'Explorez les canyons sauvages des Cévennes. Une immersion totale dans l\'eau et la roche pour un maximum de sensations.',
        image: 'https://picsum.photos/seed/canyoning/800/600',
        googleRating: 4.8
      },
    ],
    culture: [
      { 
        id: 'bambouseraie', 
        label: 'La Bambouseraie', 
        priceAdult: 12.5, 
        priceChild: 8.5, 
        description: 'Jardin exotique unique en Europe.', 
        longDescription: 'La Bambouseraie en Cévennes est un jardin botanique unique. Découvrez des bambous géants, des séquoias et un jardin japonais zen.',
        image: 'https://picsum.photos/seed/bamboo/800/600',
        familyFriendly: true,
        googleRating: 4.5
      },
      { 
        id: 'train_vapeur', 
        label: 'Train à Vapeur des Cévennes', 
        priceAdult: 17, 
        priceChild: 11.5, 
        description: 'Voyage authentique entre Anduze et St-Jean.', 
        longDescription: 'Embarquez pour un voyage nostalgique à bord d\'un train à vapeur. Traversez les viaducs et tunnels de la vallée du Gardon.',
        image: 'https://picsum.photos/seed/train/800/600',
        familyFriendly: true,
        googleRating: 4.6
      },
      { 
        id: 'musee_desert', 
        label: 'Musée du Désert', 
        priceAdult: 7, 
        priceChild: 0, 
        description: 'Histoire du protestantisme en Cévennes.', 
        longDescription: 'Situé dans la maison natale du chef camisard Roland, ce musée retrace l\'histoire de la résistance protestante au XVIIIe siècle.',
        image: 'https://picsum.photos/seed/museum/800/600',
        googleRating: 4.4
      },
      { 
        id: 'musee_soie', 
        label: 'Musée de la Soie', 
        priceAdult: 6, 
        priceChild: 0, 
        description: 'Découverte du savoir-faire séricicole.', 
        longDescription: 'Découvrez l\'histoire de la soie en Cévennes, de l\'élevage du ver à soie au tissage des étoffes précieuses.',
        image: 'https://picsum.photos/seed/silk/800/600',
        googleRating: 4.3
      },
      { 
        id: 'maison_rouge', 
        label: 'Maison Rouge (Musée des Vallées Cévenoles)', 
        priceAdult: 8, 
        priceChild: 4, 
        description: 'Musée de société sur la vie en Cévennes.', 
        longDescription: 'Installé dans une ancienne filature de soie, ce musée présente la vie quotidienne, les outils et les traditions des habitants des Cévennes.',
        image: 'https://picsum.photos/seed/redhouse/800/600',
        googleRating: 4.5
      },
    ],
    relaxation: [
      { 
        id: 'baignade_cascade', 
        label: 'Baignade aux Cascades', 
        priceAdult: 0, 
        priceChild: 0, 
        description: 'Détente au bord de l\'eau (St-Laurent-le-Minier).', 
        longDescription: 'Profitez de la fraîcheur des cascades de Saint-Laurent-le-Minier. Un lieu idyllique pour une pause baignade en pleine nature.',
        image: 'https://picsum.photos/seed/waterfall/800/600',
        familyFriendly: true,
        googleRating: 4.5
      },
      { 
        id: 'moulin_foux', 
        label: 'Moulin de la Foux', 
        priceAdult: 0, 
        priceChild: 0, 
        description: 'Résurgence de la Vis, lieu rafraîchissant.', 
        longDescription: 'Une courte randonnée vous mène à la résurgence de la Vis et aux ruines d\'un ancien moulin. Un spectacle naturel saisissant.',
        image: 'https://picsum.photos/seed/mill/800/600',
        familyFriendly: true,
        googleRating: 4.7
      },
    ]
  };

  const geocodeAddress = async (address: string) => {
    try {
      const result = await geocodePhoton('', address);
      if (result && result.location) {
        return result.location;
      }
      return null;
    } catch (error) {
      console.error('Geocoding error for address:', address, 'Error:', error);
    }
    return null;
  };

  const TourRoute = ({ proposal, driverHome, pickup, dropoff }: { proposal: any, driverHome: string, pickup: string, dropoff: string }) => {
    const map = useMap();
    const maps = useMapsLibrary('maps');
    const [homeCoords, setHomeCoords] = useState<google.maps.LatLngLiteral | null>(null);
    const [pickupCoords, setPickupCoords] = useState<google.maps.LatLngLiteral | null>(null);
    const [dropoffCoords, setDropoffCoords] = useState<google.maps.LatLngLiteral | null>(null);
    const [selectedActivity, setSelectedActivity] = useState<any>(null);
    const rendererRef = useRef<google.maps.DirectionsRenderer | null>(null);

    useEffect(() => {
      const geocode = async () => {
        const h = await geocodeAddress(driverHome);
        const p = await geocodeAddress(pickup);
        const d = await geocodeAddress(dropoff);
        if (h) setHomeCoords(h);
        if (p) setPickupCoords(p);
        if (d) setDropoffCoords(d);
      };
      geocode();
    }, [driverHome, pickup, dropoff]);

    useEffect(() => {
      if (!map || !maps || !homeCoords || !pickupCoords || !dropoffCoords || !proposal.activities.length) return;

      if (rendererRef.current) {
        rendererRef.current.setMap(null);
      }

      const directionsService = new google.maps.DirectionsService();
      const waypoints = proposal.activities
        .filter((a: any) => a.coordinates)
        .map((a: any) => ({
          location: a.coordinates,
          stopover: true
        }));

      directionsService.route({
        origin: homeCoords,
        destination: homeCoords,
        waypoints: [
          { location: pickupCoords, stopover: true },
          ...waypoints,
          { location: dropoffCoords, stopover: true }
        ],
        travelMode: google.maps.TravelMode.DRIVING,
        optimizeWaypoints: false
      }, (result, status) => {
        if (status === google.maps.DirectionsStatus.OK) {
          rendererRef.current = new google.maps.DirectionsRenderer({
            map,
            directions: result,
            suppressMarkers: true,
            polylineOptions: {
              strokeColor: '#10b981',
              strokeWeight: 5,
              strokeOpacity: 0.8
            }
          });
        }
      });
      
      return () => {
        if (rendererRef.current) {
          rendererRef.current.setMap(null);
        }
      };
    }, [map, maps, homeCoords, pickupCoords, dropoffCoords, proposal]);

    return (
      <>
        {homeCoords && (
          <AdvancedMarker position={homeCoords}>
            <div className="bg-zinc-900 p-2 rounded-full border-2 border-white shadow-lg">
              <Home className="w-4 h-4 text-white" />
            </div>
          </AdvancedMarker>
        )}
        {pickupCoords && (
          <AdvancedMarker position={pickupCoords}>
            <div className="bg-emerald-500 p-2 rounded-full border-2 border-white shadow-lg">
              <UserIcon className="w-4 h-4 text-white" />
            </div>
          </AdvancedMarker>
        )}
        {proposal.activities.map((activity: any, idx: number) => (
          activity.coordinates && (
            <React.Fragment key={idx}>
              <AdvancedMarker 
                position={activity.coordinates}
                onClick={() => setSelectedActivity(activity)}
              >
                 <div className="bg-emerald-600 p-2 rounded-lg shadow-xl border border-white/20 transform hover:scale-110 transition-transform cursor-pointer">
                    <MapPin className="w-4 h-4 text-white" />
                 </div>
              </AdvancedMarker>
              {selectedActivity === activity && (
                <InfoWindow
                  position={activity.coordinates}
                  onCloseClick={() => setSelectedActivity(null)}
                >
                  <div className="p-2 max-w-[200px]">
                    {activity.photos?.[0] && (
                        <img src={activity.photos[0]} className="w-full h-24 object-cover rounded-lg mb-2" />
                      )}
                    <h4 className="font-bold text-sm">{activity.name}</h4>
                    <p className="text-[10px] text-zinc-500 mb-1">{activity.durationMinutes} min de visite</p>
                    <p className="text-[10px] leading-tight">{activity.shortDescription || activity.description}</p>
                  </div>
                </InfoWindow>
              )}
            </React.Fragment>
          )
        ))}
      </>
    );
  };

  const validateProposal = async (proposal: any) => {
    if (!proposal) return proposal;
    
    const validated = { ...proposal, warnings: [...(proposal.warnings || [])] };
    
    // 1. Calculate total distance including Driver's Home
    const homeCoords = await geocodeAddress(appData.settings.driverHomeAddress);
    const pickupCoords = await geocodeAddress(customTourParams.departureAddress || customTourParams.accommodationCity);
    
    if (homeCoords && pickupCoords) {
      const distHomeToPickup = getDistance(homeCoords, pickupCoords);
      const circuitDist = validated.totalDistanceKm || 0;
      
      const lastActivity = validated.activities[validated.activities.length - 1];
      const distDropoffToHome = lastActivity && lastActivity.coordinates
        ? getDistance(lastActivity.coordinates, homeCoords)
        : distHomeToPickup;
        
      const totalDist = distHomeToPickup + circuitDist + distDropoffToHome;
      validated.totalDistanceKm = Math.round(totalDist);
      
      if (totalDist > 250) {
        if (!validated.warnings.some((w: string) => w.includes('250 km'))) {
          validated.warnings.push(`Attention : L'itinéraire total (${Math.round(totalDist)} km) dépasse la limite autorisée de 250 km.`);
        }
      }
    }
    
    // 2. Budget check
    const totalActivityCost = validated.totalEstimatedActivityCost;
    if (totalActivityCost > customTourParams.dailyBudget) {
      if (!validated.warnings.some((w: string) => w.includes('budget'))) {
        validated.warnings.push(`Attention : Le coût estimé des activités (${totalActivityCost}€) dépasse votre budget de ${customTourParams.dailyBudget}€.`);
      }
    }
    
    // 3. Time check (Curfew 19:00)
    if (validated.endTime) {
      const [hours, minutes] = validated.endTime.split(':').map(Number);
      if (hours > 19 || (hours === 19 && minutes > 0)) {
        if (!validated.warnings.some((w: string) => w.includes('19:00'))) {
          validated.warnings.push(`Attention : L'heure de retour prévue (${validated.endTime}) dépasse le couvre-feu de 19:00.`);
        }
      }
    }
    
    return validated;
  };

  const generateCustomTour = async (overrideUser?: User) => {
    const finalUser = overrideUser || user;
    if (!socket) return;
    
    if (!finalUser) {
      setPendingAction('book_custom');
      setView('profile');
      return;
    }
    
    setIsGenerating(true);
    
    try {
      const themeLabels = customTourParams.selectedThemes
        .sort((a, b) => a.priority - b.priority)
        .map(st => `${st.priority}. ${CUSTOM_TOUR_THEMES.find(t => t.id === st.id)?.label}`)
        .filter(Boolean)
        .join(', ');
      
      const activityLabels = customTourParams.selectedActivities
        .map((act, idx) => `${idx + 1}. ${act}`)
        .join(', ');
      
      const themePrompt = [
        themeLabels ? `Thèmes par priorité: ${themeLabels}` : '',
        customTourParams.themeOrSector ? `Demande spécifique: ${customTourParams.themeOrSector}` : '',
        activityLabels ? `Activités spécifiques par priorité: ${activityLabels}` : '',
        `Ville d'hébergement: ${customTourParams.accommodationCity}`,
        `Date: ${customTourParams.circuitDate}`,
        `Budget journalier max: ${customTourParams.dailyBudget}€`
      ].filter(Boolean).join(' | ');

      // Calculate transport price for custom tour
      const basePrice = customTourParams?.duration === 'half-day' 
        ? (appData.settings?.tourPriceHalfDay || 175) 
        : (appData.settings?.tourPriceFullDay || 280);
      
      // We don't have estimated distance here, so we just use base price
      // Or we could try to estimate it, but it's a custom tour without selected activities yet.
      // Let's just use basePrice for now, or assume it's > threshold.
      // Actually, let's just use basePrice.
      const transportPrice = basePrice;

      // AI generation disabled
      const proposal = {
        title: "Proposition de circuit sur mesure",
        description: "Un circuit personnalisé selon vos préférences.",
        sites: []
      };
      /*
      const proposal = await generateCustomTourProposal(
        customTourParams?.duration,
        themePrompt,
        customTourParams.adults,
        customTourParams.children,
        customTourParams.departureAddress || customTourParams.accommodationCity,
        appData.settings.driverHomeAddress,
        customTourParams.pickupTime,
        customTourParams.dailyBudget,
        [],
        user?.displayName || 'Client',
        customTourParams.circuitDate,
        null,
        transportPrice,
        appData.sites.filter(s => s.isVisibleClient)
      );

      const validatedProposal = await validateProposal(proposal);
      */
      setCustomTourProposal(proposal as any);
    } catch (error) {
      console.error(error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUpdatePrice = () => {
    if (activeBooking) {
      socket?.emit('update_booking_price', { bookingId: activeBooking.id, newPrice });
      setEditingPrice(false);
    }
  };

  const confirmCustomTour = async () => {
    if (!socket || !user || !customTourProposal) return;
    
    let pickupLat = 0;
    let pickupLng = 0;
    
    const address = customTourParams.departureAddress || customTourParams.accommodationCity;
    
    try {
      const result = await geocodePhoton('', address);
      if (result && result.location) {
        pickupLat = result.location.lat;
        pickupLng = result.location.lng;
      }
    } catch (error) {
      console.error('Geocoding error for address:', address, 'Error:', error);
    }
    
    socket.emit('request_booking', {
      type: 'custom',
      proposal: customTourProposal,
      params: customTourParams,
      customerId: user.id,
      timestamp: Date.now(),
      requestSms,
      status: 'pending',
      paymentStatus: 'pending',
      paymentMethod: 'cash',
      pickup: { lat: pickupLat, lng: pickupLng, address: address },
      destination: { lat: pickupLat, lng: pickupLng, address: address },
      price: customTourProposal.transportPrice
    });
    setView('status');
  };

  if (!hasValidKey) return <SplashScreen />;

  return (
    <APIProvider apiKey={API_KEY} version="weekly">
      <div className="h-screen flex flex-col overflow-hidden bg-zinc-950 text-zinc-50 font-sans">
      <header className="bg-zinc-900 border-b border-zinc-800 px-4 md:px-6 py-4 flex items-center justify-between shrink-0 h-16 md:h-20 z-50">
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => setView('home')}>
          <div className="bg-emerald-500/20 p-1.5 md:p-2 rounded-lg border border-emerald-500/50 shadow-[0_0_10px_rgba(16,185,129,0.3)]">
            <Car className="text-emerald-400 w-4 h-4 md:w-5 md:h-5" />
          </div>
          <h1 className="text-lg md:text-xl font-vtc font-bold tracking-tight text-white">{appData.settings.companyName}</h1>
        </div>
        <div className="flex items-center gap-2 md:gap-4">
          <button 
            onClick={() => setView('home')}
            className="p-2 text-zinc-400 hover:text-emerald-400 transition-colors"
            title="Accueil"
          >
            <Home className="w-5 h-5" />
          </button>
          <div className="relative">
            <button 
              onClick={() => setIsLangOpen(!isLangOpen)}
              className="flex items-center gap-1.5 bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1 text-sm hover:bg-zinc-700 transition-colors"
            >
              <span className="text-lg">{flags[lang]}</span>
              <ChevronDown className={cn("w-3 h-3 text-zinc-400 transition-transform", isLangOpen && "rotate-180")} />
            </button>
            
            <AnimatePresence>
              {isLangOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setIsLangOpen(false)} />
                  <motion.div 
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="absolute right-0 mt-2 w-32 bg-white border border-zinc-200 rounded-xl shadow-xl z-50 overflow-hidden"
                  >
                    {(Object.keys(flags) as Array<keyof typeof flags>).map((l) => (
                      <button
                        key={l}
                        onClick={() => handleSetLang(l)}
                        className={cn(
                          "w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-zinc-50 transition-colors",
                          lang === l ? "text-emerald-600 font-bold bg-emerald-50/50" : "text-zinc-600"
                        )}
                      >
                        <span className="text-lg">{flags[l]}</span>
                        <span className="uppercase">{l}</span>
                      </button>
                    ))}
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
          {activeBooking && (
            <button 
              onClick={() => setView('status')}
              className="bg-emerald-50 text-emerald-700 px-3 py-1.5 md:px-4 md:py-2 rounded-full text-xs md:text-sm font-semibold flex items-center gap-1.5 md:gap-2 animate-pulse"
            >
              <Clock className="w-3.5 h-3.5 md:w-4 md:h-4" />
              <span className="hidden sm:inline">Course en cours</span>
              <span className="sm:hidden">En cours</span>
            </button>
          )}
          {user && (
            <button 
              onClick={() => setView('orders')}
              className={cn(
                "p-2 rounded-xl transition-colors",
                view === 'orders' ? "text-emerald-600 bg-emerald-50" : "text-zinc-400 hover:text-zinc-900"
              )}
              title="Mes Commandes"
            >
              <FileText className="w-5 h-5" />
            </button>
          )}
          <button 
            onClick={() => setView('profile')}
            className={cn(
              "transition-all flex items-center justify-center",
              user 
                ? "w-9 h-9 rounded-full bg-emerald-100/80 text-emerald-700 font-bold text-sm border border-emerald-200/50 shadow-sm hover:shadow-md hover:bg-emerald-200/80" 
                : "p-2 rounded-xl text-zinc-400 hover:text-zinc-900 hover:bg-zinc-50"
            )}
            title={user ? "Mon Espace" : "Se connecter"}
          >
            {user ? (
              <span>
                {user.firstName && user.lastName 
                  ? `${user.firstName[0].toUpperCase()}${user.lastName[0].toUpperCase()}`
                  : user.email ? user.email[0].toUpperCase() : 'U'
                }
              </span>
            ) : (
              <UserIcon className="w-5 h-5" />
            )}
          </button>

          {/* Secondary Menu for Footer Links */}
          <div className="hidden md:flex items-center gap-4 ml-4 pl-4 border-l border-zinc-200">
            <button onClick={() => setView('cgv')} className="text-[10px] font-bold text-zinc-400 hover:text-zinc-900 uppercase tracking-widest transition-colors">CGV</button>
            <button onClick={() => setView('contact')} className="text-[10px] font-bold text-zinc-400 hover:text-zinc-900 uppercase tracking-widest transition-colors">Contact</button>
          </div>
          <div className="md:hidden flex items-center ml-2">
             <button 
               onClick={() => {
                 const menu = document.getElementById('mobile-secondary-menu');
                 if (menu) menu.classList.toggle('hidden');
               }}
               className="p-2 text-zinc-400 hover:text-zinc-900"
             >
               <MoreVertical className="w-5 h-5" />
             </button>
             <div id="mobile-secondary-menu" className="hidden absolute top-16 right-4 bg-white shadow-xl rounded-2xl border border-zinc-100 p-4 z-[100] flex flex-col gap-3">
               <button onClick={() => { setView('cgv'); document.getElementById('mobile-secondary-menu')?.classList.add('hidden'); }} className="text-xs font-bold text-zinc-600 text-left">CGV</button>
               <button onClick={() => { setView('contact'); document.getElementById('mobile-secondary-menu')?.classList.add('hidden'); }} className="text-xs font-bold text-zinc-600 text-left">Contact</button>
               <button onClick={() => { setView('legal'); document.getElementById('mobile-secondary-menu')?.classList.add('hidden'); }} className="text-xs font-bold text-zinc-600 text-left">Mentions Légales</button>
             </div>
          </div>
          
          {user?.email === appData.settings?.adminEmail && (
            <>
              <button 
                onClick={() => setView('driver_dashboard')}
                className={cn(
                  "p-2 rounded-xl transition-colors",
                  view === 'driver_dashboard' ? "text-emerald-600 bg-emerald-50" : "text-zinc-400 hover:text-zinc-900"
                )}
                title="Tableau de bord Chauffeur"
              >
                <Navigation className="w-5 h-5" />
              </button>
              <button 
                onClick={() => setView('admin')}
                className={cn(
                  "p-2 rounded-xl transition-colors",
                  view === 'admin' ? "text-emerald-600 bg-emerald-50" : "text-zinc-400 hover:text-zinc-900"
                )}
                title="Administration"
              >
                <SettingsIcon className="w-5 h-5" />
              </button>
            </>
          )}
          {user?.role === 'admin' && user?.email !== appData.settings?.adminEmail && (
            <button 
              onClick={() => setView('admin')}
              className={cn(
                "p-2 rounded-xl transition-colors",
                view === 'admin' ? "text-emerald-600 bg-emerald-50" : "text-zinc-400 hover:text-zinc-900"
              )}
              title="Administration"
            >
              <SettingsIcon className="w-5 h-5" />
            </button>
          )}
        </div>
      </header>

      <main className={cn(
        "flex-1 relative custom-scrollbar",
        view === 'custom_tour' ? "overflow-hidden" : "overflow-y-auto"
      )}>
        {/* Persistent Hero Background - Only visible on Home */}
        <div 
          className={cn(
            "fixed inset-0 z-0 hero-bg-fixed",
            view === 'home' ? "opacity-100" : "opacity-0 pointer-events-none transition-opacity duration-700 ease-in-out"
          )}
          style={{ 
            backgroundImage: `url(${heroImage})`,
            backgroundColor: '#09090b' // zinc-950 fallback
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-transparent to-black/90" />
        </div>

        <AnimatePresence mode="wait">
          {view === 'status' && activeBooking && (
            <motion.div 
              key="status"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-white"
            >
              {/* Full Screen Map */}
              <div className="absolute inset-0 z-0">
                <APIProvider apiKey={API_KEY} version="weekly">
                  <Map
                    defaultCenter={activeBooking.pickup || DEFAULT_CENTER}
                    defaultZoom={15}
                    mapId="STATUS_MAP_FULL"
                    style={{ width: '100%', height: '100%' }}
                    disableDefaultUI
                    gestureHandling={'greedy'}
                  >
                    {activeBooking.pickup && activeBooking.destination && (
                      <MapAutoFit points={[activeBooking.pickup, activeBooking.destination, animatedDriverPos]} />
                    )}
                    
                    {activeBooking.pickup && (
                      <AdvancedMarker position={activeBooking.pickup} zIndex={20}>
                        <div className="pickup-flag">
                          <Flag className="w-5 h-5 text-white" />
                        </div>
                      </AdvancedMarker>
                    )}
                    {activeBooking.destination && (
                      <AdvancedMarker position={activeBooking.destination} zIndex={20}>
                        <div className="destination-flag">
                          <CheckeredFlag className="w-5 h-5 text-white" />
                        </div>
                      </AdvancedMarker>
                    )}
                    {animatedDriverPos && (
                      <AdvancedMarker position={animatedDriverPos} zIndex={30}>
                        <div className="car-marker-premium">
                          <div className="car-body">
                            <Car className="w-6 h-6 text-white" />
                          </div>
                          <div className="car-pulse" />
                        </div>
                      </AdvancedMarker>
                    )}
                    {activeBooking.type !== 'custom' && customerPos && (activeBooking.status === 'accepted' || activeBooking.status === 'approaching' || activeBooking.status === 'arrived') && (
                      (() => {
                        const dist = activeBooking.pickup ? Math.sqrt(Math.pow(customerPos.lat - activeBooking.pickup.lat, 2) + Math.pow(customerPos.lng - activeBooking.pickup.lng, 2)) : 1;
                        if (dist < 0.0002) return null; // Hide if too close to pickup to avoid blue spot
                        return (
                          <AdvancedMarker position={customerPos} zIndex={10}>
                            <div className="customer-marker">
                              <div className="customer-body">
                                <UserIcon className="w-4 h-4 text-white" />
                              </div>
                              <div className="customer-pulse" />
                            </div>
                          </AdvancedMarker>
                        );
                      })()
                    )}
                    
                    {/* Ride Path - Thick Highlight */}
                    {activeBooking.pickup && activeBooking.destination && (
                      <RouteDisplay 
                        origin={activeBooking.pickup} 
                        destination={activeBooking.destination} 
                        waypoints={activeBooking.type === 'custom' && activeBooking.proposal?.activities ? activeBooking.proposal.activities.filter((a: any) => a.coordinates).map((a: any) => a.coordinates) : []}
                        strokeColor="#10b981" 
                        strokeWeight={16} 
                        strokeOpacity={1} 
                      />
                    )}
                    
                    {/* Approach Path - Different Color */}
                    {animatedDriverPos && activeBooking && (activeBooking.status === 'accepted' || activeBooking.status === 'approaching') && (
                      <RouteDisplay 
                        origin={animatedDriverPos} 
                        destination={activeBooking.pickup} 
                        strokeColor="#3b82f6" 
                        strokeWeight={12}
                        strokeOpacity={0.9}
                      />
                    )}
                  </Map>
                </APIProvider>
              </div>

              {/* Discrete Top Banner */}
              <div className="absolute top-0 left-0 right-0 z-10 p-4 md:p-6 pointer-events-none">
                <div className="max-w-5xl mx-auto flex flex-col gap-4 pointer-events-auto">
                  {/* Status Banner */}
                  {activeBooking.status === 'pending' && (
                    <motion.div 
                      initial={{ y: -50, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      className="bg-amber-500 text-white py-3 px-6 rounded-2xl shadow-xl text-center font-bold text-sm md:text-base border-2 border-amber-400/50 backdrop-blur-md"
                    >
                      En attente de confirmation du chauffeur
                    </motion.div>
                  )}

                  <div className="flex-1 bg-white/90 backdrop-blur-md border border-zinc-200 shadow-2xl rounded-3xl p-4 md:p-6 flex items-center justify-between gap-6">
                    <div className="flex items-center gap-4">
                      <button onClick={() => setView('home')} className="p-2 hover:bg-zinc-100 rounded-full transition-colors">
                        <ChevronLeft className="w-6 h-6 text-zinc-900" />
                      </button>
                      <div>
                        <p className="text-[10px] text-emerald-600 uppercase font-bold tracking-widest">
                          {activeBooking.status === 'pending' && 'En attente...'}
                          {activeBooking.status === 'accepted' && 'Chauffeur en route'}
                          {activeBooking.status === 'approaching' && 'Arrivée imminente'}
                          {activeBooking.status === 'arrived' && 'Chauffeur sur place'}
                          {activeBooking.status === 'in_progress' && 'Course en cours'}
                        </p>
                        <h2 className="text-lg md:text-xl font-display font-bold text-zinc-900">
                          {activeBooking.type === 'custom' ? (
                            <span>{activeBooking.pickup?.address.split(',')[0]} → Circuit sur mesure</span>
                          ) : activeBooking.type === 'tour' ? (
                            <span>{activeBooking.pickup?.address.split(',')[0]} → Circuit Touristique</span>
                          ) : (
                            <span>{activeBooking.pickup?.address.split(',')[0]} → {activeBooking.destination?.address.split(',')[0]}</span>
                          )}
                        </h2>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 md:gap-6 border-l border-zinc-200 pl-4 md:pl-6">
                      <div className="hidden md:block text-right">
                        <p className="text-[10px] text-zinc-400 uppercase font-bold tracking-wider">Prix</p>
                        <p className="text-lg font-bold text-zinc-900">{activeBooking.price?.toFixed(2) || '0.00'} €</p>
                      </div>
                      <button 
                        onClick={() => {
                          navigator.clipboard.writeText(window.location.href);
                          setCopied(true);
                          setTimeout(() => setCopied(false), 2000);
                        }}
                        className="p-3 bg-zinc-100 text-zinc-600 rounded-2xl hover:bg-zinc-200 transition-colors shadow-sm relative"
                        title="Partager le trajet"
                      >
                        <Share2 className="w-6 h-6" />
                        {copied && (
                          <motion.span 
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="absolute -top-8 left-1/2 -translate-x-1/2 bg-zinc-900 text-white text-[10px] py-1 px-2 rounded-lg whitespace-nowrap"
                          >
                            Copié !
                          </motion.span>
                        )}
                      </button>

                      {activeBooking.type === 'custom' && activeBooking.proposal && (
                        <button 
                          onClick={() => setViewingItinerary(true)}
                          className="p-3 bg-emerald-100 text-emerald-700 rounded-2xl hover:bg-emerald-200 transition-colors shadow-sm"
                          title="Voir l'itinéraire"
                        >
                          <Map className="w-6 h-6" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Driver Info & Chat Bubble */}
              <div className="absolute bottom-6 left-6 right-6 md:left-auto md:w-96 z-10 flex flex-col items-end gap-4">
                {/* Chat Window (Conditional) */}
                <AnimatePresence>
                  {showChat && activeBooking && user && (
                    <motion.div 
                      initial={{ y: 20, opacity: 0, scale: 0.95 }}
                      animate={{ y: 0, opacity: 1, scale: 1 }}
                      exit={{ y: 20, opacity: 0, scale: 0.95 }}
                      className="w-full bg-white shadow-2xl rounded-[32px] overflow-hidden border border-zinc-100"
                    >
                      <div className="p-4 bg-emerald-600 text-white flex justify-between items-center">
                        <h3 className="font-bold flex items-center gap-2">
                          <MessageSquare className="w-4 h-4" /> Chat Chauffeur
                        </h3>
                        <button onClick={() => setShowChat(false)} className="p-1 hover:bg-white/20 rounded-lg transition-colors">
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      <Chat 
                        booking={activeBooking} 
                        user={user} 
                        onSendMessage={(msg) => socket?.emit('send_message', { bookingId: activeBooking.id, message: msg })} 
                      />
                    </motion.div>
                  )}
                </AnimatePresence>

                <motion.div 
                  initial={{ y: 100, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  className="w-full bg-white/95 backdrop-blur-md border border-zinc-200 shadow-2xl rounded-[32px] overflow-hidden"
                >
                  <div className="p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-zinc-900 rounded-2xl flex items-center justify-center shadow-lg">
                          <UserIcon className="text-white w-6 h-6" />
                        </div>
                        <div>
                          <h3 className="font-bold text-zinc-900">Gerald</h3>
                          <p className="text-xs text-zinc-500">{appData.settings.vehicle}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1 bg-amber-50 px-2 py-1 rounded-lg border border-amber-100">
                          <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                          <span className="text-xs font-bold text-amber-700">4.9</span>
                        </div>
                        <button 
                          onClick={() => setShowChat(!showChat)}
                          className={cn(
                            "p-3 rounded-2xl transition-all shadow-lg",
                            showChat ? "bg-emerald-600 text-white" : "bg-white text-emerald-600 border border-emerald-100"
                          )}
                        >
                          <MessageSquare className="w-6 h-6" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <a 
                        href={`tel:${appData.settings.phone}`}
                        className="flex-1 flex items-center justify-center gap-2 py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-all shadow-lg text-sm"
                      >
                        <Phone className="w-4 h-4" /> Appeler
                      </a>
                      {activeBooking.status !== 'in_progress' && activeBooking.status !== 'completed' && (
                        <button 
                          onClick={() => setShowCancelConfirm(true)}
                          className="flex items-center justify-center p-3 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-all border border-red-100"
                          title="Annuler la course"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      )}
                      {user?.role === 'admin' && (
                        <button 
                          onClick={openWaze}
                          className="flex items-center justify-center p-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-all shadow-lg"
                        >
                          <Navigation className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              </div>


            </motion.div>
          )}

          {view === 'profile' && (
            <motion.div 
              key="profile"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              className="relative -mx-4 -mt-4 p-4 md:p-8 min-h-[calc(100vh-160px)] bg-zinc-950/90"
            >
              <div className="relative z-10 max-w-2xl mx-auto pb-10 md:pb-20">
                <div className="bg-white rounded-[32px] md:rounded-[48px] shadow-2xl border border-zinc-100 overflow-hidden">
                  <div className="p-5 md:p-8 text-left border-b border-zinc-50 relative bg-zinc-50/30 flex items-center gap-4">
                    <button 
                      onClick={() => setView('home')}
                      className="p-2 md:p-3 text-zinc-400 hover:text-zinc-900 transition-colors bg-white rounded-xl md:rounded-2xl shadow-sm border border-zinc-100"
                    >
                      <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
                    </button>
                    
                    <div className="flex-1 min-w-0">
                      <h2 className="text-xl md:text-2xl font-display font-bold text-zinc-900 leading-tight truncate">
                        {user ? `${user.firstName} ${user.lastName}` : "Votre Espace Personnel"}
                      </h2>
                      <p className="text-zinc-400 font-medium text-xs md:text-sm truncate">
                        {user ? user.email || user.phone : "Connectez-vous pour continuer"}
                      </p>
                    </div>

                    <div className="w-12 h-12 md:w-16 md:h-16 bg-white rounded-full flex items-center justify-center shadow-lg border-2 border-white ring-1 ring-zinc-100 overflow-hidden shrink-0">
                      {user ? (
                        <div className="w-full h-full bg-emerald-500 flex items-center justify-center text-white text-lg md:text-xl font-display font-bold">
                          {user.firstName[0]}{user.lastName[0]}
                        </div>
                      ) : (
                        <UserIcon className="w-5 h-5 md:w-6 md:h-6 text-zinc-200" />
                      )}
                    </div>
                  </div>
                </div>
                <div className="p-5 md:p-10 space-y-6 md:space-y-8">
                  {!user ? (
                    <div className="space-y-6 md:space-y-8">
                      <div className="flex p-1 bg-zinc-100 rounded-xl md:rounded-2xl">
                        <button 
                          onClick={() => { setAuthMode('login'); setAuthMessage(null); }}
                          className={cn(
                            "flex-1 py-2.5 md:py-3.5 rounded-lg md:rounded-xl font-bold text-xs md:text-sm transition-all",
                            authMode === 'login' ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-400 hover:text-zinc-600"
                          )}
                        >
                          Se Connecter
                        </button>
                        <button 
                          onClick={() => { setAuthMode('signup'); setAuthMessage(null); }}
                          className={cn(
                            "flex-1 py-2.5 md:py-3.5 rounded-lg md:rounded-xl font-bold text-xs md:text-sm transition-all",
                            authMode === 'signup' ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-400 hover:text-zinc-600"
                          )}
                        >
                          Créer un compte
                        </button>
                      </div>

                      {authMessage && (
                        <div className={cn(
                          "p-3 md:p-4 rounded-xl md:rounded-2xl text-xs font-medium flex items-center gap-3 animate-in fade-in slide-in-from-top-2",
                          authMessage.type === 'success' ? "bg-emerald-50 text-emerald-700 border border-emerald-100" :
                          authMessage.type === 'info' ? "bg-blue-50 text-blue-700 border border-blue-100" :
                          "bg-red-50 text-red-700 border border-red-100"
                        )}>
                          <div className={cn(
                            "w-1.5 h-1.5 rounded-full shrink-0",
                            authMessage.type === 'success' ? "bg-emerald-500" :
                            authMessage.type === 'info' ? "bg-blue-500" :
                            "bg-red-500"
                          )} />
                          {authMessage.text}
                        </div>
                      )}

                      <button 
                        onClick={handleGoogleLogin}
                        className="w-full py-4 md:py-5 bg-white border border-zinc-200 rounded-2xl md:rounded-3xl font-bold flex items-center justify-center gap-3 hover:bg-zinc-50 transition-all shadow-sm group"
                      >
                        <div className="w-5 h-5 flex items-center justify-center shrink-0">
                          <svg viewBox="0 0 24 24" className="w-full h-full">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                          </svg>
                        </div>
                        <span className="text-sm md:text-base text-zinc-900">
                          {authMode === 'login' ? "Se connecter avec Google" : "S'inscrire avec Google"}
                        </span>
                      </button>
                      
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-zinc-100"></div></div>
                        <div className="relative flex justify-center text-[9px] uppercase font-bold tracking-[0.3em]"><span className="bg-white px-4 text-emerald-600 bg-emerald-50/50 rounded-full py-1 border border-emerald-100/50">Ou manuellement</span></div>
                      </div>

                      <form onSubmit={handleManualSubmit} className="space-y-4 md:space-y-5">
                        {pendingAction && (
                          <div className="bg-emerald-50 p-3 rounded-xl border border-emerald-100 flex items-center gap-3 mb-4">
                            <div className="bg-emerald-500 p-1.5 rounded-lg">
                              <Zap className="w-3.5 h-3.5 text-white" />
                            </div>
                            <p className="text-emerald-800 text-[10px] font-medium">
                              Connectez-vous pour finaliser votre réservation.
                            </p>
                          </div>
                        )}
                        
                        {authMode === 'signup' && (
                          <div className="grid grid-cols-2 gap-3 md:gap-4">
                            <div className="space-y-1.5">
                              <label className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest px-1">Prénom</label>
                              <input 
                                type="text" 
                                name="first-name"
                                autoComplete="given-name"
                                placeholder="Jean" 
                                required
                                className="w-full p-3 md:p-4 bg-zinc-50 border border-zinc-100 rounded-xl text-sm text-zinc-900 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none"
                                value={manualUserForm.firstName || ''}
                                onChange={e => setManualUserForm({...manualUserForm, firstName: e.target.value})}
                                onKeyDown={handleEnterNext}
                              />
                            </div>
                            <div className="space-y-1.5">
                              <label className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest px-1">Nom</label>
                              <input 
                                type="text" 
                                name="last-name"
                                autoComplete="family-name"
                                placeholder="Dupont" 
                                required
                                className="w-full p-3 md:p-4 bg-zinc-50 border border-zinc-100 rounded-xl text-sm text-zinc-900 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none"
                                value={manualUserForm.lastName || ''}
                                onChange={e => setManualUserForm({...manualUserForm, lastName: e.target.value})}
                                onKeyDown={handleEnterNext}
                              />
                            </div>
                          </div>
                        )}

                        <div className="space-y-1.5">
                          <label className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest px-1">Email</label>
                          <input 
                            type="email" 
                            name="email"
                            autoComplete="email"
                            placeholder="jean.dupont@email.com" 
                            required
                            className="w-full p-3 md:p-4 bg-zinc-50 border border-zinc-100 rounded-xl text-sm text-zinc-900 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none"
                            value={manualUserForm.email || ''}
                            onChange={e => setManualUserForm({...manualUserForm, email: e.target.value})}
                            onKeyDown={handleEnterNext}
                          />
                        </div>

                        {authMode === 'login' && (
                          <div className="space-y-1.5">
                            <label className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest px-1">Mot de passe</label>
                            <input 
                              type="password" 
                              name="password"
                              autoComplete="current-password"
                              placeholder="••••••••" 
                              required
                              className="w-full p-3 md:p-4 bg-zinc-50 border border-zinc-100 rounded-xl text-sm text-zinc-900 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none"
                              value={manualUserForm.password || ''}
                              onChange={e => setManualUserForm({...manualUserForm, password: e.target.value})}
                              onKeyDown={handleEnterNext}
                            />
                          </div>
                        )}

                        {authMode === 'signup' && (
                          <>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                              <div className="space-y-1.5">
                                <label className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest px-1">Téléphone</label>
                                <input 
                                  type="tel" 
                                  name="tel"
                                  autoComplete="tel"
                                  placeholder="06 12 34 56 78" 
                                  required
                                  className="w-full p-3 md:p-4 bg-zinc-50 border border-zinc-100 rounded-xl text-sm text-zinc-900 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none"
                                  value={manualUserForm.phone || ''}
                                  onChange={e => setManualUserForm({...manualUserForm, phone: e.target.value})}
                                  onKeyDown={handleEnterNext}
                                />
                              </div>
                              <div className="space-y-1.5">
                                <label className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest px-1">Adresse</label>
                                <input 
                                  type="text" 
                                  name="address"
                                  autoComplete="street-address"
                                  placeholder="Votre adresse complète" 
                                  required
                                  className="w-full p-3 md:p-4 bg-zinc-50 border border-zinc-100 rounded-xl text-sm text-zinc-900 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none"
                                  value={manualUserForm.address || ''}
                                  onChange={e => setManualUserForm({...manualUserForm, address: e.target.value})}
                                  onKeyDown={handleEnterNext}
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                              <div className="space-y-1.5">
                                <label className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest px-1">Mot de passe</label>
                                <input 
                                  type="password" 
                                  name="new-password"
                                  autoComplete="new-password"
                                  placeholder="••••••••" 
                                  required
                                  className="w-full p-3 md:p-4 bg-zinc-50 border border-zinc-100 rounded-xl text-sm text-zinc-900 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none"
                                  value={manualUserForm.password || ''}
                                  onChange={e => setManualUserForm({...manualUserForm, password: e.target.value})}
                                  onKeyDown={handleEnterNext}
                                />
                              </div>
                              <div className="space-y-1.5">
                                <label className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest px-1">Confirmer</label>
                                <input 
                                  type="password" 
                                  name="confirm-password"
                                  autoComplete="new-password"
                                  placeholder="••••••••" 
                                  required
                                  className="w-full p-3 md:p-4 bg-zinc-50 border border-zinc-100 rounded-xl text-sm text-zinc-900 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none"
                                  value={manualUserForm.confirmPassword || ''}
                                  onChange={e => setManualUserForm({...manualUserForm, confirmPassword: e.target.value})}
                                  onKeyDown={handleEnterNext}
                                />
                              </div>
                            </div>
                          </>
                        )}

                        <button type="submit" className="w-full py-4 md:py-5 bg-zinc-900 text-white rounded-2xl md:rounded-3xl font-bold text-base md:text-lg hover:bg-emerald-600 transition-all shadow-xl hover:-translate-y-1 active:translate-y-0 mt-2">
                          {authMode === 'login' ? "Se Connecter" : (pendingAction ? "Finaliser ma réservation" : "Créer mon profil")}
                        </button>
                      </form>
                    </div>
                  ) : (
                    <div className="space-y-5 md:space-y-6">
                      <div className="grid grid-cols-2 gap-3 md:gap-4">
                        <button 
                          onClick={() => setView('orders')}
                          className="flex flex-col items-center gap-2 md:gap-3 p-5 md:p-6 bg-white rounded-2xl md:rounded-3xl border border-zinc-200 hover:bg-emerald-50 hover:border-emerald-200 transition-all group shadow-sm"
                        >
                          <div className="p-2.5 md:p-3 bg-emerald-50 rounded-xl group-hover:scale-110 transition-transform">
                            <FileText className="w-5 h-5 text-emerald-600" />
                          </div>
                          <span className="font-bold text-zinc-900 text-xs md:text-sm">Mes Commandes</span>
                        </button>
                        <button 
                          onClick={() => setView('profile_edit')}
                          className="flex flex-col items-center gap-2 md:gap-3 p-5 md:p-6 bg-white rounded-2xl md:rounded-3xl border border-zinc-200 hover:bg-emerald-50 hover:border-emerald-200 transition-all group shadow-sm"
                        >
                          <div className="p-2.5 md:p-3 bg-emerald-50 rounded-xl group-hover:scale-110 transition-transform">
                            <SettingsIcon className="w-5 h-5 text-emerald-600" />
                          </div>
                          <span className="font-bold text-zinc-900 text-xs md:text-sm">Paramètres</span>
                        </button>
                      </div>

                      {/* Commande en cours */}
                      {appData.bookings?.find(b => b.customerId === user.id && !['completed', 'cancelled'].includes(b.status)) ? (
                        <div className="bg-emerald-600 p-5 md:p-6 rounded-2xl md:rounded-3xl shadow-lg shadow-emerald-600/20 space-y-4">
                          <div className="flex items-center justify-between">
                            <h3 className="text-[10px] font-bold text-emerald-100 uppercase tracking-widest">Commande en cours</h3>
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                              <span className="text-[10px] font-bold text-white uppercase tracking-wider">Actif</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="p-3 bg-white/10 rounded-xl backdrop-blur-md">
                              <Car className="w-6 h-6 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold text-white truncate">Votre trajet est planifié</p>
                              <p className="text-[10px] text-emerald-100 font-medium">Suivez l'arrivée de votre chauffeur</p>
                            </div>
                            <button 
                              onClick={() => setView('status')}
                              className="px-4 py-2 bg-white text-emerald-600 text-xs font-bold rounded-lg hover:bg-emerald-50 transition-all shadow-sm"
                            >
                              Suivre
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-zinc-50 p-8 text-center rounded-2xl md:rounded-3xl border border-zinc-100 border-dashed">
                          <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm">
                            <Car className="w-6 h-6 text-zinc-300" />
                          </div>
                          <p className="text-zinc-400 text-xs font-medium">Aucune commande en cours</p>
                          <button 
                            onClick={() => setView('home')}
                            className="mt-4 text-emerald-600 text-xs font-bold hover:underline"
                          >
                            Réserver un trajet
                          </button>
                        </div>
                      )}

                      <button 
                        onClick={async () => {
                          try {
                            await signOut(auth);
                            localStorage.removeItem('vtc_user');
                            setUser(null);
                          } catch (error) {
                            console.error("Logout error:", error);
                            localStorage.removeItem('vtc_user');
                            setUser(null);
                          }
                        }}
                        className="w-full py-3.5 md:py-4 text-red-500 font-bold hover:bg-red-50 rounded-xl md:rounded-2xl transition-all border border-transparent hover:border-red-100 text-xs md:text-sm flex items-center justify-center gap-2"
                      >
                        <LogOut className="w-4 h-4" />
                        Se déconnecter
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
          {view === 'profile_edit' && user && (
            <motion.div 
              key="profile_edit"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              className="relative -mx-4 -mt-4 p-4 md:p-8 min-h-[calc(100vh-160px)] bg-zinc-950/90"
            >
              <div className="relative z-10 max-w-2xl mx-auto pb-10 md:pb-20">
                <div className="bg-white rounded-[32px] md:rounded-[48px] shadow-2xl border border-zinc-100 overflow-hidden">
                  <div className="p-5 md:p-8 text-left border-b border-zinc-50 relative bg-zinc-50/30 flex items-center gap-4">
                    <button 
                      onClick={() => setView('profile')}
                      className="p-2 md:p-3 text-zinc-400 hover:text-zinc-900 transition-colors bg-white rounded-xl md:rounded-2xl shadow-sm border border-zinc-100"
                    >
                      <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
                    </button>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-xl md:text-2xl font-display font-bold text-zinc-900 leading-tight truncate">
                        Paramètres du profil
                      </h2>
                      <p className="text-zinc-400 font-medium text-xs md:text-sm truncate">
                        Modifiez vos informations personnelles
                      </p>
                    </div>
                  </div>

                  <div className="p-5 md:p-10 space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest px-1">Prénom</label>
                        <input 
                          type="text" 
                          className="w-full p-3 md:p-4 bg-zinc-50 border border-zinc-100 rounded-xl text-sm text-zinc-900 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none"
                          value={editUserForm.firstName}
                          onChange={e => setEditUserForm({...editUserForm, firstName: e.target.value})}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest px-1">Nom</label>
                        <input 
                          type="text" 
                          className="w-full p-3 md:p-4 bg-zinc-50 border border-zinc-100 rounded-xl text-sm text-zinc-900 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none"
                          value={editUserForm.lastName}
                          onChange={e => setEditUserForm({...editUserForm, lastName: e.target.value})}
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest px-1">Téléphone</label>
                      <input 
                        type="tel" 
                        className="w-full p-3 md:p-4 bg-zinc-50 border border-zinc-100 rounded-xl text-sm text-zinc-900 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none"
                        value={editUserForm.phone}
                        onChange={e => setEditUserForm({...editUserForm, phone: e.target.value})}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest px-1">Adresse</label>
                      <input 
                        type="text" 
                        className="w-full p-3 md:p-4 bg-zinc-50 border border-zinc-100 rounded-xl text-sm text-zinc-900 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none"
                        value={editUserForm.address}
                        onChange={e => setEditUserForm({...editUserForm, address: e.target.value})}
                      />
                    </div>

                    <button 
                      onClick={async () => {
                        if (user) {
                          await saveUser({
                            ...user,
                            firstName: editUserForm.firstName,
                            lastName: editUserForm.lastName,
                            phone: editUserForm.phone,
                            address: editUserForm.address
                          });
                          setView('profile');
                        }
                      }}
                      className="w-full py-4 bg-emerald-600 text-white rounded-2xl font-bold hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-600/20"
                    >
                      Enregistrer les modifications
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
          {view === 'orders' && user && (
            <motion.div
              key="orders"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              className="max-w-4xl mx-auto space-y-8 md:space-y-12 pb-10 md:pb-20"
            >
              <div className="space-y-4 md:space-y-6 text-center px-4">
                <NavButton 
                  onClick={() => setView('profile')} 
                  type="secondary"
                  icon={ChevronLeft}
                  label="Retour au profil"
                  className="mx-auto"
                />
                <span className="text-emerald-600 font-bold uppercase tracking-widest text-[10px] md:text-xs">Historique</span>
                <h2 className="text-4xl md:text-6xl editorial-title text-zinc-900">Mes Réservations</h2>
                <p className="text-zinc-500 text-lg md:text-xl font-light">Retrouvez le détail de vos trajets et circuits passés.</p>
              </div>

              <div className="bg-white rounded-2xl md:rounded-3xl border border-zinc-200 overflow-hidden shadow-sm">
                <div className="p-4 md:p-6 border-b border-zinc-100 bg-zinc-50/50">
                  <h3 className="font-bold text-zinc-900">Historique des courses</h3>
                </div>
                <div className="divide-y divide-zinc-100">
                  {completedBookings.filter(b => b.customerId === user.id).length > 0 ? (
                    completedBookings
                      .filter(b => b.customerId === user.id)
                      .sort((a, b) => (b.completedAt || 0) - (a.completedAt || 0))
                      .map((booking) => (
                        <motion.div
                          key={booking.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="p-8 hover:bg-zinc-50 transition-colors group cursor-pointer"
                        >
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                            <div className="flex items-start gap-6">
                              <div className="bg-zinc-100 p-4 rounded-2xl group-hover:bg-white group-hover:shadow-sm transition-all">
                                <Car className="w-8 h-8 text-zinc-600" />
                              </div>
                              <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-bold uppercase tracking-wider text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                                    Trajet Simple
                                  </span>
                                  <span className="text-zinc-400 text-xs">•</span>
                                  <span className="text-zinc-500 text-xs font-medium">
                                    {booking.completedAt ? new Date(booking.completedAt).toLocaleDateString('fr-FR', {
                                      day: 'numeric',
                                      month: 'long',
                                      year: 'numeric'
                                    }) : 'Date inconnue'}
                                  </span>
                                </div>
                                <h3 className="text-lg md:text-2xl font-semibold text-zinc-900 break-words">
                                  {(booking.pickup?.address || 'N/A').split(',')[0]} → {booking.type === 'custom' ? 'Circuit sur mesure' : booking.type === 'tour' ? 'Circuit Touristique' : (booking.destination?.address || 'N/A').split(',')[0]}
                                </h3>
                                <div className="flex items-center gap-4 text-zinc-500 text-sm">
                                  {booking.type === 'vtc' && booking.tripDistance && (
                                    <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {booking.tripDistance}</span>
                                  )}
                                  {booking.type === 'custom' && booking.params?.duration && (
                                    <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {booking.params.duration === 'half' ? 'Demi-journée' : 'Journée entière'}</span>
                                  )}
                                  {booking.type === 'tour' && (
                                    <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> Circuit Touristique</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center justify-between md:justify-end gap-8 border-t md:border-t-0 pt-4 md:pt-0">
                              <div className="text-right">
                                <p className="text-zinc-400 text-xs uppercase tracking-widest font-bold">Prix Total</p>
                                <p className="text-2xl md:text-3xl font-bold text-zinc-900">{booking.price?.toFixed(2)}€</p>
                              </div>

                            </div>
                          </div>
                        </motion.div>
                      ))
                  ) : (
                    <div className="p-20 text-center space-y-6">
                      <div className="bg-zinc-50 w-24 h-24 rounded-full flex items-center justify-center mx-auto">
                        <FileText className="w-10 h-10 text-zinc-300" />
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-2xl font-semibold text-zinc-900">Aucune réservation</h3>
                        <p className="text-zinc-500 max-w-xs mx-auto">Vous n'avez pas encore effectué de réservation avec nous.</p>
                      </div>
                      <button onClick={() => setView('home')} className="bg-zinc-900 text-white px-8 py-4 rounded-2xl font-bold hover:bg-emerald-600 transition-all">
                        Découvrir nos services
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {view === 'home' && (
            <motion.div 
              key="home"
              initial={isDataLoaded ? { opacity: 0 } : { opacity: 1 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="flex-1 flex flex-col min-h-full relative overflow-hidden"
            >
              <div className="relative z-10 flex-1 flex flex-col justify-between items-center text-center px-4 md:px-12 lg:px-24 pt-8 pb-12 md:pt-32 md:pb-32 w-full max-w-7xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  className="max-w-5xl w-full mt-2 md:mt-8"
                >
                    <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-vtc font-semibold italic text-white mb-2 md:mb-6 text-shadow-lg leading-[0.85] tracking-tight">
                      {(() => {
                        const title = appData.settings.heroTitle || "Céven'VTC";
                        if (title.includes('VTC')) {
                          const parts = title.split('VTC');
                          if (appData.settings.heroVtcGlassEffect) {
                            return (
                              <>
                                {parts[0]}
                                <span className="inline-block bg-white/10 backdrop-blur-md border border-white/30 px-4 py-1 rounded-3xl shadow-[0_8px_32px_0_rgba(255,255,255,0.1)] text-emerald-500 not-italic">
                                  VTC
                                </span>
                                {parts[1]}
                              </>
                            );
                          } else {
                            return (
                              <>
                                {parts[0]}
                                <span className="text-emerald-500">VTC</span>
                                {parts[1]}
                              </>
                            );
                          }
                        }
                        return title;
                      })()}
                    </h2>
                    <p className="text-white/90 text-xs sm:text-sm md:text-lg lg:text-xl max-w-2xl mx-auto font-light leading-relaxed text-shadow-md px-2 md:px-4">
                      {appData.settings.heroSubtitle || t('heroSubtitle')}
                    </p>

                    {/* Admin Toggle Buttons */}
                    {(user?.email === appData.settings?.adminEmail || user?.role === 'admin') && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="mt-6 md:mt-10 flex flex-wrap justify-center gap-4"
                      >
                        <button
                          onClick={() => setView('driver_dashboard')}
                          className="flex items-center gap-3 px-4 md:px-6 py-2 md:py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-2xl transition-all group shadow-xl border border-emerald-400/30"
                        >
                          <Navigation className="w-5 h-5 md:w-5 md:h-5 group-hover:rotate-12 transition-transform" />
                          <span className="font-bold uppercase tracking-widest text-[10px] md:text-xs lg:text-sm">{appData.settings.btnDriverText || 'Espace Chauffeur'}</span>
                        </button>
                        <button
                          onClick={() => setView('admin')}
                          className="flex items-center gap-3 px-4 md:px-6 py-2 md:py-3 bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 text-white rounded-2xl transition-all group shadow-xl"
                        >
                          <SettingsIcon className="w-5 h-5 md:w-5 md:h-5 group-hover:rotate-12 transition-transform" />
                          <span className="font-bold uppercase tracking-widest text-[10px] md:text-xs lg:text-sm">{appData.settings.btnAdminText || 'Administration'}</span>
                        </button>
                      </motion.div>
                    )}
                  </motion.div>
                  
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0 }}
                    className="w-full max-w-2xl"
                  >
                    <div className="grid grid-cols-2 gap-2 md:gap-4 lg:gap-6 px-1 md:px-4">
                      <motion.button 
                        whileTap={{ scale: 0.95 }}
                        disabled={isCalculating}
                        onClick={() => handleVtcNavigation('immediate')}
                        className="group relative overflow-hidden bg-white/10 backdrop-blur-2xl border border-white/30 text-white p-3 md:p-5 lg:p-5 rounded-[20px] md:rounded-[24px] transition-all hover:bg-sky-500/30 hover:scale-[1.02] shadow-2xl flex flex-col items-center gap-1.5 md:gap-3 disabled:opacity-50"
                      >
                        <div className="bg-sky-500 p-2 md:p-3 rounded-lg md:rounded-xl shadow-lg group-hover:scale-110 transition-transform">
                          {isCalculating ? <Hourglass className="w-5 h-5 md:w-6 md:h-6 text-white animate-spin" /> : <Zap className="w-5 h-5 md:w-6 md:h-6 text-white" />}
                        </div>
                        <div className="space-y-0 md:space-y-1">
                          <h3 className="text-[10px] md:text-sm lg:text-base font-display font-bold uppercase tracking-wider">{appData.settings.btnVtcText || t('immediateRide')}</h3>
                          <p className="text-sky-300 text-[8px] md:text-xs font-bold leading-tight hidden xs:block">{t('taxiVtcPro')}</p>
                        </div>
                      </motion.button>

                      <motion.button 
                        whileTap={{ scale: 0.95 }}
                        disabled={isCalculating}
                        onClick={() => handleVtcNavigation('advance')}
                        className="group relative overflow-hidden bg-white/10 backdrop-blur-2xl border border-white/30 text-white p-3 md:p-5 lg:p-5 rounded-[20px] md:rounded-[24px] transition-all hover:bg-emerald-500/30 hover:scale-[1.02] shadow-2xl flex flex-col items-center gap-1.5 md:gap-3 disabled:opacity-50"
                      >
                        <div className="bg-emerald-500 p-2 md:p-3 rounded-lg md:rounded-xl shadow-lg group-hover:scale-110 transition-transform">
                          {isCalculating ? <Hourglass className="w-5 h-5 md:w-6 md:h-6 text-white animate-spin" /> : <Clock className="w-5 h-5 md:w-6 md:h-6 text-white" />}
                        </div>
                        <div className="space-y-0 md:space-y-1">
                          <h3 className="text-[10px] md:text-sm lg:text-base font-display font-bold uppercase tracking-wider">{appData.settings.btnReservationText || t('booking')}</h3>
                          <p className="text-emerald-300 text-[8px] md:text-xs font-bold leading-tight hidden xs:block">{t('planTrip')}</p>
                        </div>
                      </motion.button>

                      <motion.button 
                        whileTap={{ scale: 0.95 }}
                        disabled={isCalculating}
                        onClick={() => {
                          if (navigator.vibrate) navigator.vibrate(50);
                          resetTouristCircuits();
                          setView('custom_tour');
                        }}
                        className="col-span-2 group relative overflow-hidden bg-white/10 backdrop-blur-2xl border border-white/30 text-white p-3 md:p-5 lg:p-5 rounded-[20px] md:rounded-[24px] transition-all hover:bg-emerald-500/30 hover:scale-[1.02] shadow-2xl flex flex-row items-center justify-center gap-3 md:gap-6 disabled:opacity-50"
                      >
                        <div className="bg-emerald-500 p-2 md:p-3 rounded-lg md:rounded-xl shadow-lg group-hover:scale-110 transition-transform shrink-0">
                          {isCalculating ? <Hourglass className="w-5 h-5 md:w-6 md:h-6 text-white animate-spin" /> : <MapPin className="w-5 h-5 md:w-6 md:h-6 text-white" />}
                        </div>
                        <div className="text-left">
                          <h3 className="text-[10px] md:text-sm lg:text-base font-display font-bold uppercase tracking-wider">{appData.settings.btnTourText || t('touristCircuits')}</h3>
                          <p className="text-white/90 text-[8px] md:text-xs font-medium leading-tight hidden xs:block">{t('touristCircuitsSub')}</p>
                        </div>
                      </motion.button>
                    </div>
                  </motion.div>
                </div>
            </motion.div>
          )}

          {view === 'vtc' && (
            <motion.div 
              key="vtc"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="relative -mx-4 -mt-4 p-4 md:p-8 h-full bg-zinc-950/85"
            >
              <div className="relative z-10 flex flex-col md:grid md:grid-cols-2 gap-6 md:gap-12 h-full">
                <div className={cn(
                  "space-y-4 md:space-y-6 md:overflow-y-auto md:pr-6 custom-scrollbar order-2 md:order-1",
                  (!activeBooking || activeBooking.status === 'pending') ? "col-span-2 max-w-2xl mx-auto w-full" : "col-span-2 md:col-span-1"
                )}>
                  <NavButton 
                    label={t('backToHome')} 
                    onClick={() => setView('home')} 
                    type="secondary" 
                    icon={Home}
                    className="px-4 py-2 min-w-0"
                  />
                  
                  <div className="space-y-1 md:space-y-2">
                    <span className="text-emerald-500 font-bold uppercase tracking-widest text-[10px]">Céven'VTC</span>
                    <h2 className="text-xl md:text-5xl font-display font-bold text-zinc-900 leading-tight">{t('yourRide')}</h2>
                    <p className="text-zinc-500 text-xs md:text-lg hidden sm:block">{t('enterAddresses')}</p>
                  </div>
                </div>
                <div className="space-y-4 md:space-y-8 pb-32 md:pb-0">
                  {activeBooking ? (
                    <div className="bg-white p-6 md:p-10 rounded-[32px] border border-zinc-100 shadow-2xl space-y-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-2xl bg-emerald-500 flex items-center justify-center shadow-lg">
                            <Car className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">{t('rideStatus')}</p>
                            <h3 className="text-xl font-bold text-zinc-900 flex items-center gap-2">
                              {activeBooking.status === 'pending' && (
                                <>
                                  <div className="w-2 h-2 bg-emerald-500 rounded-full animate-ping" />
                                  {t('waitingForDriver')}
                                </>
                              )}
                              {activeBooking.status === 'accepted' && t('driverOnWay')}
                              {activeBooking.status === 'approaching' && t('driverApproaching')}
                              {activeBooking.status === 'arrived' && t('driverArrived')}
                              {activeBooking.status === 'in_progress' && t('rideInProgress')}
                              {activeBooking.status === 'completed' && t('rideCompleted')}
                            </h3>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Prix TTC</p>
                          <p className="text-2xl font-bold text-emerald-600">{activeBooking.price.toFixed(2)} €</p>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="flex items-center gap-3 text-sm text-zinc-600">
                          <MapPin className="w-4 h-4 text-emerald-500" />
                          <span className="truncate"><strong>{t('pickupPoint')}:</strong> {activeBooking.pickup.address}</span>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-zinc-600">
                          <Flag className="w-4 h-4 text-zinc-900" />
                          <span className="truncate"><strong>{t('destinationPoint')}:</strong> {activeBooking.type === 'custom' ? 'Circuit sur mesure' : activeBooking.type === 'tour' ? 'Circuit Touristique' : activeBooking.destination?.address}</span>
                        </div>
                      </div>

                      {user?.role === 'admin' && (
                        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-zinc-100">
                          {activeBooking.status === 'pending' && (
                            <button 
                              onClick={handleAcceptBooking}
                              className="col-span-2 bg-emerald-500 text-white py-4 rounded-2xl font-bold hover:bg-emerald-600 transition-all shadow-lg flex items-center justify-center gap-2"
                            >
                              <CheckCircle className="w-5 h-5" /> Accepter la course
                            </button>
                          )}
                          {(activeBooking.status === 'accepted' || activeBooking.status === 'approaching' || activeBooking.status === 'arrived') && (
                            <>
                              <button 
                                onClick={openWaze}
                                className="bg-blue-500 text-white py-4 rounded-2xl font-bold hover:bg-blue-600 transition-all shadow-lg flex items-center justify-center gap-2"
                              >
                                <Navigation className="w-5 h-5" /> Waze
                              </button>
                              <button 
                                onClick={handleStartTrip}
                                className="bg-zinc-900 text-white py-4 rounded-2xl font-bold hover:bg-zinc-800 transition-all shadow-lg flex items-center justify-center gap-2"
                              >
                                <Play className="w-5 h-5" /> {t('startRide')}
                              </button>
                            </>
                          )}
                          {activeBooking.status === 'in_progress' && (
                            <button 
                              onClick={() => socket.emit('booking_status_change', 'completed')}
                              className="col-span-2 bg-emerald-600 text-white py-4 rounded-2xl font-bold hover:bg-emerald-700 transition-all shadow-lg flex items-center justify-center gap-2"
                            >
                              <CheckCircle className="w-5 h-5" /> {t('endRide')}
                            </button>
                          )}
                        </div>
                      )}

                      {activeBooking.status !== 'completed' && activeBooking.status !== 'in_progress' && (
                        <button 
                          onClick={handleCancelBooking}
                          className="w-full text-zinc-400 text-xs font-bold uppercase tracking-widest hover:text-red-500 transition-colors pt-2"
                        >
                          {t('cancelBooking')}
                        </button>
                      )}
                    </div>
                  ) : (
                    <>
                      <form onSubmit={(e) => e.preventDefault()} className="bg-white p-4 md:p-10 rounded-[24px] md:rounded-[48px] border border-zinc-100 shadow-xl space-y-4 md:space-y-10 relative">
                        <div className="absolute left-[31px] md:left-[51px] top-[48px] md:top-[88px] bottom-[48px] md:bottom-[88px] w-px bg-gradient-to-b from-emerald-500 via-zinc-100 to-zinc-900 opacity-30" />
                        
                        <div className="flex items-start gap-4 md:gap-8 relative z-10">
                          <div className="mt-1.5 w-4 h-4 md:w-6 md:h-6 rounded-full bg-emerald-500 ring-[6px] md:ring-[12px] ring-emerald-50 shadow-lg flex items-center justify-center">
                            <div className="w-1 h-1 md:w-2 md:h-2 rounded-full bg-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-center mb-1 md:mb-3">
                              <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-[0.2em] block">{t('pickupPoint')}</label>
                              <button 
                                onClick={handleGeolocation}
                                className="p-1 hover:bg-emerald-50 rounded-lg transition-colors flex items-center gap-1 text-[10px] font-bold text-emerald-600 uppercase tracking-wider"
                              >
                                <Navigation className="w-3 h-3" /> {t('geolocateMe')}
                              </button>
                            </div>
                            <PhotonAutocompleteInput 
                              placeholder={t('whereToPickup')}
                              onPlaceSelect={setPickup}
                              defaultValue={pickup?.address}
                              onKeyDown={handleEnterNext}
                            />
                          </div>
                        </div>

                        <div className="flex items-start gap-4 md:gap-8 relative z-10">
                          <div className="mt-1.5 w-4 h-4 md:w-6 md:h-6 rounded-full bg-zinc-900 ring-[6px] md:ring-[12px] ring-zinc-50 shadow-lg flex items-center justify-center">
                            <div className="w-1 h-1 md:w-2 md:h-2 rounded-full bg-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-[0.2em] mb-1 md:mb-3 block">{t('destinationPoint')}</label>
                            <PhotonAutocompleteInput 
                              placeholder={t('whereTo')}
                              onPlaceSelect={setDestination}
                              defaultValue={destination?.address}
                              onKeyDown={handleEnterNext}
                            />
                          </div>
                        </div>

                        {bookingType === 'advance' && (
                          <div className="grid grid-cols-2 gap-4 relative z-10">
                            <div className="flex-1 min-w-0">
                              <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-[0.2em] mb-1 md:mb-3 block">{t('date')}</label>
                              <input 
                                type="date" 
                                value={scheduledDate || ''}
                                onChange={(e) => setScheduledDate(e.target.value)}
                                onKeyDown={handleEnterNext}
                                className="w-full bg-zinc-50 border border-zinc-100 rounded-2xl px-6 py-4 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 transition-all font-medium"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-[0.2em] mb-1 md:mb-3 block">{t('time')}</label>
                              <input 
                                type="time" 
                                value={scheduledTime || ''}
                                onChange={(e) => setScheduledTime(e.target.value)}
                                onKeyDown={handleEnterNext}
                                className="w-full bg-zinc-50 border border-zinc-100 rounded-2xl px-6 py-4 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 transition-all font-medium"
                              />
                            </div>
                          </div>
                        )}
                      </form>

                      {approachInfo && !tripValidated && (
                        <motion.div 
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="bg-emerald-500 p-6 rounded-[32px] shadow-xl text-white flex items-center justify-between"
                        >
                          <div className="flex items-center gap-4">
                            <div className="bg-white/20 p-3 rounded-2xl">
                              <Clock className="w-6 h-6" />
                            </div>
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-widest opacity-80">Chauffeur à proximité</p>
                              <p className="text-2xl font-display font-bold">~ {approachInfo?.duration}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-[10px] font-bold uppercase tracking-widest opacity-80">Distance</p>
                            <p className="text-lg font-bold">{approachInfo.distance}</p>
                          </div>
                        </motion.div>
                      )}

                      {tripValidated && approachInfo && (
                        <motion.div 
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="bg-white p-6 md:p-10 rounded-[32px] md:rounded-[48px] shadow-2xl space-y-6 md:space-y-8 overflow-hidden relative group border border-zinc-100"
                        >
                          <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500 rounded-full blur-[100px] opacity-10" />
                          
                          <div className="relative z-10 space-y-6">
                            <div className="flex items-center justify-between border-b border-zinc-100 pb-6">
                              <div className="flex items-center gap-4">
                                <div className="bg-emerald-500 p-4 rounded-2xl">
                                  <Car className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                  <p className="text-[10px] text-emerald-600 uppercase font-bold tracking-widest">Trajet estimé</p>
                                  <p className="text-2xl font-display font-bold text-zinc-900">{tripInfo?.distance || "..."}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-[10px] text-emerald-600 uppercase font-bold tracking-widest">Tarif Fixe</p>
                                <p className="text-3xl font-display font-bold text-zinc-900">{calculatePrice().total.toFixed(2)} €</p>
                              </div>
                            </div>
                            
                            <div className="flex flex-row items-center justify-between gap-4">
                              <label className="flex items-center gap-2 cursor-pointer group/check">
                                <div className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center transition-all ${requestSms ? 'bg-emerald-500 border-emerald-500' : 'border-zinc-200 group-hover/check:border-zinc-300'}`}>
                                  {requestSms && <Check className="w-3 h-3 text-white" />}
                                </div>
                                <input 
                                  type="checkbox" 
                                  className="hidden"
                                  checked={requestSms}
                                  onChange={(e) => setRequestSms(e.target.checked)}
                                />
                                <span className="text-zinc-500 text-xs font-medium">Rappel SMS</span>
                              </label>
                              <motion.button 
                                whileTap={{ scale: 0.95 }}
                                onClick={() => {
                                  if (navigator.vibrate) navigator.vibrate(50);
                                  handleBooking();
                                }}
                                disabled={isBooking}
                                className="flex-1 bg-emerald-500 text-white px-8 py-4 rounded-2xl font-bold hover:bg-emerald-600 transition-all shadow-xl disabled:opacity-50 flex items-center justify-center gap-3"
                              >
                                {isBooking ? (
                                  <Hourglass className="w-5 h-5 animate-spin" />
                                ) : (
                                  <CheckCircle className="w-5 h-5" />
                                )}
                                Demander un chauffeur
                              </motion.button>
                            </div>
                          </div>
                        </motion.div>
                      )}

                      {!tripValidated && (
                        <motion.button 
                          whileTap={pickup && destination ? { scale: 0.98 } : {}}
                          disabled={!pickup || !destination}
                          onClick={() => {
                            if (navigator.vibrate) navigator.vibrate(50);
                            if (!user) {
                              setView('profile');
                              return;
                            }
                            setTripValidated(true);
                          }}
                          className={cn(
                            "w-full py-4 md:py-6 rounded-[20px] md:rounded-[32px] font-bold text-base md:text-xl transition-all shadow-2xl",
                            pickup && destination 
                              ? "bg-white text-zinc-900 hover:bg-zinc-50 border border-zinc-100 shadow-zinc-200 hover:-translate-y-1 active:translate-y-0" 
                              : "bg-zinc-100 text-zinc-400 cursor-not-allowed shadow-none"
                          )}
                        >
                          {user ? "Valider les détails" : "Se connecter pour réserver"}
                        </motion.button>
                      )}

                      {tripValidated && (
                        <motion.button 
                          whileTap={{ scale: 0.98 }}
                          onClick={() => {
                            if (navigator.vibrate) navigator.vibrate(30);
                            setTripValidated(false);
                          }}
                          className="w-full py-3 text-zinc-400 text-xs font-bold uppercase tracking-widest hover:text-zinc-600 transition-colors"
                        >
                          Modifier les adresses
                        </motion.button>
                      )}
                    </>
                  )}
                </div>

                <div className="bg-zinc-50 p-5 md:p-8 rounded-[24px] md:rounded-[32px] flex items-start gap-4 md:gap-6 border border-zinc-100">
                  <div className="bg-white p-2 md:p-3 rounded-xl md:rounded-2xl shadow-sm shrink-0">
                    <AlertCircle className="text-zinc-400 w-4 h-4 md:w-6 md:h-6" />
                  </div>
                  <p className="text-[10px] md:text-sm text-zinc-500 leading-relaxed">
                    Le tarif définitif est calculé selon le barème officiel à la fin du trajet. 
                    Le temps d'approche est indicatif et dépend des conditions de circulation.
                  </p>
                </div>
              </div>

              {(activeBooking || tripValidated) && (activeBooking?.status !== 'completed') && (
                <div className="bg-zinc-100 rounded-[24px] md:rounded-[40px] overflow-hidden relative shadow-inner border border-zinc-200 h-[450px] md:h-full order-1 md:order-2">
                  <Map
                    defaultCenter={DEFAULT_CENTER}
                    defaultZoom={13}
                    mapId="VTC_MAP"
                    style={{ width: '100%', height: '100%' }}
                    disableDefaultUI
                  >
                      {pickup && destination && (
                        <MapAutoFit points={[pickup, destination, driverPos]} />
                      )}

                      {pickup && (
                        <AdvancedMarker position={pickup} zIndex={20}>
                          <div className="pickup-flag">
                            <MapPin className="w-5 h-5 text-white" />
                          </div>
                        </AdvancedMarker>
                      )}
                      {destination && (
                        <AdvancedMarker position={destination} zIndex={20}>
                          <div className="destination-flag">
                            <Check className="w-5 h-5 text-white" />
                          </div>
                        </AdvancedMarker>
                      )}
                      {driverPos && (
                        <AdvancedMarker position={driverPos} zIndex={30}>
                          <div className="car-marker-premium">
                            <div className="car-body">
                              <Car className="w-5 h-5 text-white" />
                            </div>
                            <div className="car-pulse" />
                          </div>
                        </AdvancedMarker>
                      )}
                      {pickup && destination && <RouteDisplay origin={pickup} destination={destination} strokeColor="#10b981" strokeWeight={16} strokeOpacity={1} />}
                      {driverPos && activeBooking && (activeBooking.status === 'accepted' || activeBooking.status === 'approaching') && (
                        <RouteDisplay 
                          origin={driverPos} 
                          destination={activeBooking.pickup} 
                          strokeColor="#f59e0b" 
                          strokeWeight={12}
                          strokeOpacity={0.9}
                        />
                      )}
                    </Map>
                </div>
              )}
            </motion.div>
          )}

                    {view === 'tour_confirmation' && (
                      <div className="w-full max-w-2xl mx-auto px-4 py-20 text-center space-y-8">
                        <div className="w-24 h-24 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto border-2 border-emerald-500/30">
                          <CheckCircle2 className="w-12 h-12 text-emerald-500" />
                        </div>
                        <div className="space-y-4">
                          <h2 className="text-4xl font-black text-white uppercase tracking-tight">Réservation Enregistrée !</h2>
                          <p className="text-zinc-400 text-lg">Votre demande de circuit touristique a bien été reçue. Un bon de commande sera généré prochainement.</p>
                        </div>
                        <div className="pt-8">
                          <button 
                            onClick={() => setView('home')}
                            className="px-12 py-5 bg-emerald-500 text-white rounded-3xl font-black text-lg hover:bg-emerald-600 transition-all shadow-[0_0_30px_rgba(16,185,129,0.3)] uppercase tracking-widest"
                          >
                            Retour à l'accueil
                          </button>
                        </div>
                      </div>
                    )}

                    {view === 'custom_tour' && (
                      <motion.div 
                        key="custom_tour"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="w-full h-full bg-zinc-950/50 backdrop-blur-sm relative z-10 overflow-hidden"
                      >
                        <div className="w-full max-w-7xl mx-auto h-full overflow-hidden">
                          {!isDataLoaded ? (
                            <div className="flex flex-col items-center justify-center h-full space-y-4">
                              <Loader2 className="w-12 h-12 text-emerald-500 animate-spin" />
                              <p className="text-zinc-400 font-medium">Chargement des données...</p>
                            </div>
                          ) : (
                            <ErrorBoundary fallback={
                              <div className="p-12 text-center bg-white rounded-3xl shadow-xl">
                                <h2 className="text-2xl font-bold text-red-600 mb-4">Erreur de chargement</h2>
                                <p className="text-zinc-600 mb-6">Désolé, le tunnel des circuits touristiques a rencontré un problème technique.</p>
                                <button onClick={() => setView('home')} className="px-6 py-3 bg-zinc-900 text-white rounded-xl font-bold">Retour à l'accueil</button>
                              </div>
                            }>
                              <TouristCircuitTunnel
                                settings={appData.settings}
                                sites={appData.sites}
                                questionnaire={tourQuestionnaire}
                                setQuestionnaire={setTourQuestionnaire}
                                onComplete={(circuit) => {
                                  handleTourBooking(circuit);
                                }}
                                onCancel={() => setView('home')}
                              />
                            </ErrorBoundary>
                          )}
                        </div>
                      </motion.div>
                    )}


          {view === 'driver_dashboard' && (
            <motion.div 
              key="driver_dashboard"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              className="max-w-6xl mx-auto space-y-12 pb-20"
            >
      <div className="flex items-end justify-between border-b border-zinc-100 pb-8">
        <div className="space-y-2">
          <span className="text-emerald-600 font-bold uppercase tracking-widest text-xs">Espace Chauffeur</span>
          <div className="flex items-center gap-4">
            <h2 className="text-5xl editorial-title text-zinc-900">Tableau de Bord</h2>
          </div>
        </div>
        <div className="flex gap-4">
          <button 
            onClick={() => setView('admin')} 
            className="px-8 py-4 bg-emerald-100 text-emerald-700 rounded-2xl font-bold hover:bg-emerald-200 transition-all border border-emerald-200"
          >
            Accès Admin
          </button>
          <button 
            onClick={() => setView('home')} 
            className="px-8 py-4 bg-zinc-900 text-white rounded-2xl font-bold hover:bg-emerald-600 transition-all shadow-xl"
          >
            Quitter
          </button>
        </div>
      </div>

      <div className="grid gap-8">
        <div className="p-20 text-center space-y-6 bg-white rounded-[40px] border border-zinc-100">
          <div className="bg-zinc-50 w-24 h-24 rounded-full flex items-center justify-center mx-auto">
            <Car className="w-10 h-10 text-zinc-300" />
          </div>
          <div className="space-y-2">
            <h3 className="text-2xl font-semibold text-zinc-900">Module Chauffeur en cours de développement</h3>
            <p className="text-zinc-500 max-w-xs mx-auto">Cette section sera entièrement refaite prochainement.</p>
          </div>
        </div>
      </div>
    </motion.div>
  )}

          {view === 'admin' && (
            <motion.div 
              key="admin"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              className="w-full max-w-7xl mx-auto px-4 md:px-8 lg:px-12 space-y-6 pb-20 overflow-x-hidden box-border"
            >
              <div className="flex items-center justify-between border-b border-zinc-100 pb-4 px-4 md:px-0">
                <div className="flex items-center gap-4">
                  <h2 className="text-xl font-sans font-light tracking-tight text-zinc-900">Administration</h2>
                  <button 
                    onClick={() => socket?.emit('refresh_data')}
                    className="p-1 text-zinc-300 hover:text-emerald-600 transition-colors"
                    title="Actualiser les données"
                  >
                    <Sparkles className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex gap-2">
                  <button 
                    onClick={() => setView('driver_dashboard')} 
                    className="px-4 py-2 bg-emerald-50 text-emerald-700 rounded-xl font-bold hover:bg-emerald-100 transition-all border border-emerald-100 text-sm"
                  >
                    Espace Chauffeur
                  </button>
                  <button 
                    onClick={() => setView('home')} 
                    className="px-4 py-2 bg-zinc-900 text-white rounded-xl font-bold hover:bg-emerald-600 transition-all shadow-lg text-sm"
                  >
                    Quitter l'Admin
                  </button>
                </div>
              </div>

              <div className="flex gap-6 overflow-x-auto pb-1 no-scrollbar border-b border-zinc-100 px-4 md:px-0 scroll-smooth">
                {[
                  { id: 'settings', label: 'Configuration', icon: SettingsIcon },
                  { id: 'design', label: 'Design', icon: Palette },
                  { id: 'packs', label: 'Packs & Formules', icon: Package },
                  { id: 'customers', label: 'Clients', icon: UserIcon },
                  { id: 'orders', label: 'Commandes', icon: FileText },
                  { id: 'backup', label: 'Sauvegarde', icon: Download },
                  { id: 'system', label: 'Système', icon: ShieldCheck }
                ].map(tab => (
                  <button 
                    key={tab.id}
                    onClick={() => setAdminTab(tab.id as any)}
                    className={cn(
                      "flex items-center gap-2 pb-2 px-1 font-bold transition-all border-b-2 whitespace-nowrap text-sm shrink-0",
                      adminTab === tab.id 
                        ? "border-emerald-600 text-emerald-600" 
                        : "border-transparent text-zinc-400 hover:text-zinc-600"
                    )}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="grid gap-4 px-2 sm:px-4 md:px-0 overflow-x-hidden">
                {adminTab === 'backup' && <BackupRestore />}
                {adminTab === 'system' && (
                  <SystemAdmin 
                    currentData={appData}
                    onPublish={async (data) => {
                      // Géré via l'API interne du composant
                    }}
                  />
                )}
                {adminTab === 'settings' && (
                  <div className="max-w-4xl mx-auto px-2 sm:px-4 py-6">
                    <section className="bg-white p-6 rounded-3xl border border-zinc-200 space-y-6 shadow-sm">
                    <h3 className="text-xl font-bold">Paramètres de l'Entreprise</h3>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-zinc-700 mb-1">Nom de l'Entreprise</label>
                        <input 
                          type="text" 
                          value={appData.settings.companyName || ''} 
                          onChange={(e) => updateSettings({ ...appData.settings, companyName: e.target.value })}
                          className="w-full p-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-zinc-900 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-zinc-700 mb-1">Numéro SIRET</label>
                        <input 
                          type="text" 
                          value={appData.settings.siret || ''} 
                          onChange={(e) => updateSettings({ ...appData.settings, siret: e.target.value })}
                          className="w-full p-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-zinc-900 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-zinc-700 mb-1">Email de l'Entreprise</label>
                        <input 
                          type="email" 
                          value={appData.settings.email || ''} 
                          onChange={(e) => updateSettings({ ...appData.settings, email: e.target.value })}
                          className="w-full p-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-zinc-900 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-zinc-700 mb-1">Véhicule</label>
                        <input 
                          type="text" 
                          value={appData.settings.vehicle || ''} 
                          onChange={(e) => updateSettings({ ...appData.settings, vehicle: e.target.value })}
                          className="w-full p-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-zinc-900 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-zinc-700 mb-1">Téléphone</label>
                        <input 
                          type="text" 
                          value={appData.settings.phone || ''} 
                          onChange={(e) => updateSettings({ ...appData.settings, phone: e.target.value })}
                          className="w-full p-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-zinc-900 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-zinc-700 mb-1">Expérience Chauffeur</label>
                        <input 
                          type="text" 
                          value={appData.settings.driverExperience || ''} 
                          onChange={(e) => updateSettings({ ...appData.settings, driverExperience: e.target.value })}
                          className="w-full p-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-zinc-900 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-zinc-700 mb-1">Adresse de Base</label>
                        <PhotonAutocompleteInput 
                          placeholder="Ex: Cazilhac 34190"
                          defaultValue={appData.settings.baseAddress}
                          onPlaceSelect={(loc) => updateSettings({ ...appData.settings, baseAddress: loc.address })}
                        />
                      </div>
                      <div className="p-4 bg-amber-50 border border-amber-100 rounded-2xl space-y-3">
                        <h4 className="text-sm font-bold text-amber-800 flex items-center gap-2">
                          <ShieldCheck className="w-4 h-4" /> Sécurité Administrateur
                        </h4>
                        <div>
                          <label className="block text-xs font-medium text-amber-700 mb-1">Email Administrateur Autorisé</label>
                          <input 
                            type="email" 
                            value={appData.settings?.adminEmail || ''} 
                            onChange={(e) => updateSettings({ ...(appData.settings || defaultSettings), adminEmail: e.target.value } as Settings)}
                            className="w-full p-2 bg-white border border-amber-200 rounded-lg text-sm text-black"
                            placeholder="gerald.verdier@gmail.com"
                          />
                          <p className="text-[10px] text-amber-600 mt-1 italic">Attention : changer cet email transférera les droits d'accès.</p>
                        </div>
                      </div>
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-2xl space-y-4">
                        <h4 className="text-sm font-bold text-blue-800 flex items-center gap-2">
                          <Compass className="w-4 h-4" /> Circuit Touristique
                        </h4>
                        <div className="grid gap-4">
                          <div>
                            <label className="block text-xs font-medium text-blue-500 mb-1">Domicile Chauffeur (Point de départ/retour fixe)</label>
                            <PhotonAutocompleteInput 
                              placeholder="Ex: Cazilhac 34190"
                              defaultValue={appData.settings.driverHomeAddress}
                              onPlaceSelect={(loc) => updateSettings({ ...appData.settings, driverHomeAddress: loc.address })}
                              theme="light"
                              inputClassName="bg-white text-black border border-blue-200"
                            />
                            <p className="text-[10px] text-blue-600 mt-1 italic">Cette adresse sert de base pour le calcul des 200 km maximum.</p>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-bold text-blue-700 uppercase tracking-wider mb-1">Limite KM par circuit</label>
                              <div className="flex items-center gap-2">
                                <DebouncedInput 
                                  type="number" 
                                  value={appData.settings.tourMaxKm ?? ''} 
                                  onChange={(e) => updateSettings({ ...appData.settings, tourMaxKm: e.target.value === '' ? '' as any : parseInt(e.target.value) })}
                                  className="flex-1 p-2 bg-white border border-blue-200 rounded-lg font-bold text-black text-sm"
                                />
                                <span className="text-blue-600 font-bold text-xs">KM</span>
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-bold text-blue-700 uppercase tracking-wider mb-1">Heure de retour max</label>
                              <input 
                                type="time" 
                                value={appData.settings.tourReturnTime || ''} 
                                onChange={(e) => updateSettings({ ...appData.settings, tourReturnTime: e.target.value })}
                                className="w-full p-2 bg-white border border-blue-200 rounded-lg font-bold text-zinc-900 text-sm"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-bold text-blue-700 uppercase tracking-wider mb-1">Seuil Basculement (H)</label>
                              <div className="flex items-center gap-2">
                                <DebouncedInput 
                                  type="number" 
                                  value={appData.settings.tourMaxDurationHalfDay ?? ''} 
                                  onChange={(e) => updateSettings({ ...appData.settings, tourMaxDurationHalfDay: e.target.value === '' ? '' as any : parseInt(e.target.value) })}
                                  className="flex-1 p-2 bg-white border border-blue-200 rounded-lg font-bold text-zinc-900 text-sm"
                                />
                                <span className="text-blue-600 font-bold text-xs">H</span>
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-bold text-blue-700 uppercase tracking-wider mb-1">Tolérance (Min)</label>
                              <div className="flex items-center gap-2">
                                <DebouncedInput 
                                  type="number" 
                                  value={appData.settings.tourToleranceMinutes ?? ''} 
                                  onChange={(e) => updateSettings({ ...appData.settings, tourToleranceMinutes: e.target.value === '' ? '' as any : parseInt(e.target.value) })}
                                  className="flex-1 p-2 bg-white border border-blue-200 rounded-lg font-bold text-zinc-900 text-sm"
                                />
                                <span className="text-blue-600 font-bold text-xs">Min</span>
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-bold text-blue-700 uppercase tracking-wider mb-1">Tampon Inter-Activités (Min)</label>
                              <div className="flex items-center gap-2">
                                <DebouncedInput 
                                  type="number" 
                                  value={appData.settings.bufferTimeMinutes ?? ''} 
                                  onChange={(e) => updateSettings({ ...appData.settings, bufferTimeMinutes: e.target.value === '' ? '' as any : parseInt(e.target.value) })}
                                  className="flex-1 p-2 bg-white border border-blue-200 rounded-lg font-bold text-zinc-900 text-sm"
                                />
                                <span className="text-blue-600 font-bold text-xs">Min</span>
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                            <div>
                                <label className="block text-xs font-bold text-blue-700 uppercase tracking-wider mb-1">Repas Standard (Début/Fin)</label>
                                <div className="flex gap-2">
                                    <input type="time" value={appData.settings.standardLunchStart} onChange={(e) => updateSettings({...appData.settings, standardLunchStart: e.target.value})} className="w-full p-2 bg-white border border-blue-200 rounded-lg text-sm" />
                                    <input type="time" value={appData.settings.standardLunchEnd} onChange={(e) => updateSettings({...appData.settings, standardLunchEnd: e.target.value})} className="w-full p-2 bg-white border border-blue-200 rounded-lg text-sm" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-blue-700 uppercase tracking-wider mb-1">Repas Enfant &lt; 6ans (Début/Fin)</label>
                                <div className="flex gap-2">
                                    <input type="time" value={appData.settings.childLunchStart} onChange={(e) => updateSettings({...appData.settings, childLunchStart: e.target.value})} className="w-full p-2 bg-white border border-blue-200 rounded-lg text-sm" />
                                    <input type="time" value={appData.settings.childLunchEnd} onChange={(e) => updateSettings({...appData.settings, childLunchEnd: e.target.value})} className="w-full p-2 bg-white border border-blue-200 rounded-lg text-sm" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-blue-700 uppercase tracking-wider mb-1">Durée Repas (Min)</label>
                                <div className="flex items-center gap-2">
                                    <DebouncedInput 
                                      type="number" 
                                      value={appData.settings.lunchDurationMinutes ?? ''} 
                                      onChange={(e) => updateSettings({ ...appData.settings, lunchDurationMinutes: e.target.value === '' ? '' as any : parseInt(e.target.value) })}
                                      className="flex-1 p-2 bg-white border border-blue-200 rounded-lg font-bold text-zinc-900 text-sm"
                                    />
                                    <span className="text-blue-600 font-bold text-xs">Min</span>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-blue-700 uppercase tracking-wider mb-1">Durée Pique-nique (Min)</label>
                                <div className="flex items-center gap-2">
                                    <DebouncedInput 
                                      type="number" 
                                      value={appData.settings.picnicDurationMinutes ?? ''} 
                                      onChange={(e) => updateSettings({ ...appData.settings, picnicDurationMinutes: e.target.value === '' ? '' as any : parseInt(e.target.value) })}
                                      className="flex-1 p-2 bg-white border border-blue-200 rounded-lg font-bold text-zinc-900 text-sm"
                                    />
                                    <span className="text-blue-600 font-bold text-xs">Min</span>
                                </div>
                            </div>
                          </div>
                        </div>

                        <div className="pt-4 border-t border-blue-100">
                          <p className="text-[10px] font-bold text-blue-700 uppercase tracking-widest mb-3">Grille Tarifaire Transport</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-bold text-blue-700 uppercase tracking-wider mb-1">Prix Demi-journée</label>
                              <div className="flex items-center gap-2">
                                <DebouncedInput 
                                  type="number" 
                                  value={appData.settings?.tourPriceHalfDay ?? ''} 
                                  onChange={(e) => updateSettings({ ...defaultSettings, ...(appData.settings || {}), tourPriceHalfDay: e.target.value === '' ? '' as any : parseInt(e.target.value) } as Settings)}
                                  className="flex-1 p-2 bg-white border border-blue-200 rounded-lg font-bold text-zinc-900 text-sm"
                                />
                                <span className="text-blue-600 font-bold text-xs">€</span>
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-bold text-blue-700 uppercase tracking-wider mb-1">Prix Journée</label>
                              <div className="flex items-center gap-2">
                                <DebouncedInput 
                                  type="number" 
                                  value={appData.settings?.tourPriceFullDay ?? ''} 
                                  onChange={(e) => updateSettings({ ...defaultSettings, ...(appData.settings || {}), tourPriceFullDay: e.target.value === '' ? '' as any : parseInt(e.target.value) } as Settings)}
                                  className="flex-1 p-2 bg-white border border-blue-200 rounded-lg font-bold text-zinc-900 text-sm"
                                />
                                <span className="text-blue-600 font-bold text-xs">€</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="p-4 bg-purple-50 border border-purple-200 rounded-2xl space-y-4">
                        <h4 className="text-sm font-bold text-purple-800 flex items-center gap-2">
                          <Info className="w-4 h-4" /> Conditions par Nature d'Activité
                        </h4>
                        <div className="grid gap-3">
                          {[
                            { id: 'patrimoine', label: 'Patrimoine' },
                            { id: 'nature', label: 'Balades en nature' },
                            { id: 'activites', label: 'Activité / Sport' },
                            { id: 'randonnee', label: 'Randonnée' },
                            { id: 'points_de_vue', label: 'Points de vue' },
                            { id: 'terroir', label: 'Produits du terroir' },
                            { id: 'insolite', label: 'Insolite' },
                            { id: 'baignade', label: 'Baignade' }
                          ].map(cat => (
                            <div key={cat.id}>
                              <label className="block text-xs font-bold text-purple-700 uppercase tracking-wider mb-1">{cat.label}</label>
                              <textarea 
                                value={appData.settings.categoryConditions?.[cat.id] || ''} 
                                onChange={(e) => updateSettings({ 
                                  ...appData.settings, 
                                  categoryConditions: { 
                                    ...(appData.settings.categoryConditions || {}), 
                                    [cat.id]: e.target.value 
                                  } 
                                })}
                                className="w-full p-2 bg-white border border-purple-200 rounded-lg text-sm text-zinc-900 h-16"
                                placeholder={`Conseils automatiques pour ${cat.label}...`}
                              />
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-2xl space-y-3">
                        <h4 className="text-sm font-bold text-zinc-800 flex items-center gap-2">
                          <Edit2 className="w-4 h-4" /> Textes de l'Application (CMS)
                        </h4>
                        <div className="grid gap-3">
                          <div>
                            <label className="block text-xs font-medium text-zinc-500 mb-1">Titre d'Accueil (Hero)</label>
                            <div className="flex flex-col sm:flex-row gap-2">
                              <input 
                                type="text" 
                                value={appData.settings.heroTitle || ""} 
                                onChange={(e) => updateSettings({ ...appData.settings, heroTitle: e.target.value })}
                                className="flex-1 w-full p-2 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-900"
                                placeholder="Céven'VTC"
                              />
                              <div className="flex items-center justify-between sm:justify-start gap-2 px-3 py-2 sm:py-0 bg-white border border-zinc-200 rounded-lg">
                                <span className="text-[10px] font-medium text-zinc-400 uppercase tracking-widest">Effet Verre</span>
                                <button 
                                  onClick={() => updateSettings({ ...appData.settings, heroVtcGlassEffect: !appData.settings.heroVtcGlassEffect })}
                                  className={cn(
                                    "w-8 h-4 rounded-full transition-colors relative",
                                    appData.settings.heroVtcGlassEffect ? "bg-emerald-500" : "bg-zinc-300"
                                  )}
                                >
                                  <div className={cn(
                                    "absolute top-0.5 w-3 h-3 bg-white rounded-full transition-all",
                                    appData.settings.heroVtcGlassEffect ? "right-0.5" : "left-0.5"
                                  )} />
                                </button>
                              </div>
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-zinc-500 mb-1">Sous-titre d'Accueil (Hero)</label>
                            <input 
                              type="text" 
                              value={appData.settings.heroSubtitle || ""} 
                              onChange={(e) => updateSettings({ ...appData.settings, heroSubtitle: e.target.value })}
                              className="w-full p-2 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-900"
                              placeholder="Votre chauffeur privé dans les Cévennes"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-zinc-500 mb-1">Titre Section Circuits</label>
                            <input 
                              type="text" 
                              value={appData.settings.touristCircuitsTitle || ""} 
                              onChange={(e) => updateSettings({ ...appData.settings, touristCircuitsTitle: e.target.value })}
                              className="w-full p-2 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-900"
                              placeholder="Découvrez les Cévennes autrement"
                            />
                          </div>
                          <div>
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-1">
                              <label className="block text-xs font-medium text-zinc-500">Texte de Présentation Circuits</label>
                              <button 
                                onClick={generateAIText}
                                disabled={isGeneratingText}
                                className="text-[10px] font-bold text-emerald-600 hover:text-emerald-700 flex items-center justify-center gap-1 bg-emerald-50 px-2 py-1 sm:py-0.5 rounded-full transition-colors disabled:opacity-50 w-full sm:w-auto"
                              >
                                {isGeneratingText ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                                Générer par IA
                              </button>
                            </div>
                            <textarea 
                              value={appData.settings.touristCircuitsText || ""} 
                              onChange={(e) => updateSettings({ ...appData.settings, touristCircuitsText: e.target.value })}
                              className="w-full p-2 bg-white border border-zinc-200 rounded-lg text-sm h-24 text-zinc-900"
                              placeholder="Profitez du confort de notre Peugeot 5008 Bleu..."
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-zinc-500 mb-1">Texte du Bouton Circuits</label>
                            <input 
                              type="text" 
                              value={appData.settings.touristCircuitsBtnText || ""} 
                              onChange={(e) => updateSettings({ ...appData.settings, touristCircuitsBtnText: e.target.value })}
                              className="w-full p-2 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-900"
                              placeholder="Commencer ma réservation"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-2xl space-y-3">
                        <h4 className="text-sm font-bold text-zinc-800 flex items-center gap-2">
                          <MousePointer2 className="w-4 h-4" /> Textes des Boutons
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-zinc-500 mb-1">Bouton Chauffeur</label>
                            <input 
                              type="text" 
                              value={appData.settings.btnDriverText || ""} 
                              onChange={(e) => updateSettings({ ...appData.settings, btnDriverText: e.target.value })}
                              className="w-full p-2 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-900"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-zinc-500 mb-1">Bouton Admin</label>
                            <input 
                              type="text" 
                              value={appData.settings.btnAdminText || ""} 
                              onChange={(e) => updateSettings({ ...appData.settings, btnAdminText: e.target.value })}
                              className="w-full p-2 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-900"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-zinc-500 mb-1">Bouton Course Immédiate</label>
                            <input 
                              type="text" 
                              value={appData.settings.btnVtcText || ""} 
                              onChange={(e) => updateSettings({ ...appData.settings, btnVtcText: e.target.value })}
                              className="w-full p-2 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-900"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-zinc-500 mb-1">Bouton Réservation</label>
                            <input 
                              type="text" 
                              value={appData.settings.btnReservationText || ""} 
                              onChange={(e) => updateSettings({ ...appData.settings, btnReservationText: e.target.value })}
                              className="w-full p-2 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-900"
                            />
                          </div>
                          <div className="col-span-1 sm:col-span-2">
                            <label className="block text-xs font-medium text-zinc-500 mb-1">Bouton Circuits (Accueil)</label>
                            <input 
                              type="text" 
                              value={appData.settings.btnTourText || ""} 
                              onChange={(e) => updateSettings({ ...appData.settings, btnTourText: e.target.value })}
                              className="w-full p-2 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-900"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-zinc-700 mb-1">Tarif au km (€)</label>
                          <DebouncedInput 
                            type="number" 
                            step="0.01"
                            value={appData.settings.kmRate ?? ''} 
                            onChange={(e) => updateSettings({ ...appData.settings, kmRate: e.target.value === '' ? '' as any : parseFloat(e.target.value) })}
                            className="w-full p-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-zinc-900 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-zinc-700 mb-1">Majoration Nuit (%)</label>
                          <DebouncedInput 
                            type="number" 
                            value={appData.settings.nightRateIncrease ?? ''} 
                            onChange={(e) => updateSettings({ ...appData.settings, nightRateIncrease: e.target.value === '' ? '' as any : parseInt(e.target.value) })}
                            className="w-full p-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-zinc-900 text-sm"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-zinc-700 mb-1">Début Nuit (HH:MM)</label>
                          <input 
                            type="text" 
                            value={appData.settings.nightStart || ''} 
                            onChange={(e) => updateSettings({ ...appData.settings, nightStart: e.target.value })}
                            className="w-full p-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-zinc-900 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-zinc-700 mb-1">Fin Nuit (HH:MM)</label>
                          <input 
                            type="text" 
                            value={appData.settings.nightEnd || ''} 
                            onChange={(e) => updateSettings({ ...appData.settings, nightEnd: e.target.value })}
                            className="w-full p-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-zinc-900 text-sm"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-zinc-700 mb-1">Seuil Remise Trajet (€)</label>
                          <DebouncedInput 
                            type="number" 
                            value={appData.settings.discountThreshold ?? ''} 
                            onChange={(e) => updateSettings({ ...appData.settings, discountThreshold: e.target.value === '' ? '' as any : parseInt(e.target.value) })}
                            className="w-full p-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-zinc-900 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-zinc-700 mb-1">Remise Trajet (%)</label>
                          <DebouncedInput 
                            type="number" 
                            value={appData.settings.discountPercentage ?? ''} 
                            onChange={(e) => updateSettings({ ...appData.settings, discountPercentage: e.target.value === '' ? '' as any : parseInt(e.target.value) })}
                            className="w-full p-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-zinc-900 text-sm"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-zinc-700 mb-1">Seuil Remise Circuit (km)</label>
                          <DebouncedInput 
                            type="number" 
                            value={appData.settings.discountDistanceThreshold ?? ''} 
                            onChange={(e) => updateSettings({ ...appData.settings, discountDistanceThreshold: e.target.value === '' ? '' as any : parseInt(e.target.value) })}
                            className="w-full p-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-zinc-900 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-zinc-700 mb-1">Remise Circuit (%)</label>
                          <DebouncedInput 
                            type="number" 
                            value={appData.settings.tourDiscountPercentage ?? ''} 
                            onChange={(e) => updateSettings({ ...appData.settings, tourDiscountPercentage: e.target.value === '' ? '' as any : parseInt(e.target.value) })}
                            className="w-full p-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-zinc-900 text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-zinc-700 mb-1">Mentions Légales</label>
                        <textarea 
                          value={appData.settings.legalMentions || ''} 
                          onChange={(e) => updateSettings({ ...appData.settings, legalMentions: e.target.value })}
                          className="w-full p-3 bg-zinc-50 border border-zinc-200 rounded-xl h-32 text-sm text-zinc-900"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-zinc-700 mb-1">Conditions Générales de Vente (CGV)</label>
                        <textarea 
                          value={appData.settings.cgv || ''} 
                          onChange={(e) => updateSettings({ ...appData.settings, cgv: e.target.value })}
                          className="w-full p-3 bg-zinc-50 border border-zinc-200 rounded-xl h-32 text-sm text-zinc-900"
                        />
                      </div>
                      <h3 className="text-xl font-bold pt-4">Paramètres de Tarification</h3>
                      <div>
                        <label className="block text-sm font-medium text-zinc-700 mb-1">Tarif Kilométrique (€/km)</label>
                        <DebouncedInput 
                          type="number" 
                          value={appData.settings.kmRate === undefined || Number.isNaN(appData.settings.kmRate) ? '' : appData.settings.kmRate} 
                          onChange={(e) => updateSettings({ ...appData.settings, kmRate: e.target.value === '' ? '' as any : parseFloat(e.target.value) })}
                          className="w-full p-3 bg-zinc-50 border border-zinc-200 rounded-xl text-zinc-900"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-zinc-700">Inclure frais d'autoroute par défaut</label>
                        <input 
                          type="checkbox" 
                          checked={appData.settings.highwayTolls} 
                          onChange={(e) => updateSettings({ ...appData.settings, highwayTolls: e.target.checked })}
                          className="w-5 h-5 rounded border-zinc-300 text-emerald-600 focus:ring-emerald-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-zinc-700 mb-1">Distance Max Journalière (km)</label>
                        <DebouncedInput 
                          type="number" 
                          value={appData.settings.maxDailyKm ?? ''} 
                          onChange={(e) => updateSettings({ ...appData.settings, maxDailyKm: e.target.value === '' ? '' as any : parseInt(e.target.value) })}
                          className="w-full p-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-zinc-900 text-sm"
                        />
                      </div>

                      <div className="pt-8 border-t border-zinc-100 flex flex-col gap-4">
                        <button 
                          onClick={handleSaveSettings}
                          className="w-full py-6 bg-emerald-600 text-white rounded-2xl font-bold text-xl hover:bg-emerald-700 transition-all shadow-xl shadow-emerald-600/20 active:scale-[0.98] flex items-center justify-center gap-3"
                        >
                          <CheckCircle className="w-7 h-7" />
                          ENREGISTRER LES MODIFICATIONS
                        </button>
                      </div>

                      <div className="pt-8 border-t border-zinc-100 space-y-4">
                        <h4 className="text-sm font-bold text-zinc-400 uppercase tracking-wider flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-500" /> Maintenance
                        </h4>
                        <button 
                          onClick={() => {
                            if (confirm('Réinitialiser les circuits touristiques (étapes, sélections, propositions) ?')) {
                              resetTouristCircuits();
                              alert('Circuits réinitialisés.');
                            }
                          }}
                          className="w-full py-4 bg-amber-50 text-amber-700 rounded-2xl font-bold text-sm hover:bg-amber-100 transition-all flex items-center justify-center gap-2"
                        >
                          <RefreshCw className="w-4 h-4" />
                          Réinitialiser les Circuits Client
                        </button>
                      </div>
                    </div>
                  </section>
                </div>
                )}

                {adminTab === 'design' && (
                  <section className="bg-white p-8 rounded-[40px] border border-zinc-200 space-y-8">
                    <div className="flex items-center gap-4">
                      <div className="bg-emerald-100 p-3 rounded-2xl">
                        <Palette className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-display font-bold">Gestion de l'Image d'Accueil</h3>
                        <p className="text-zinc-500 text-sm">Optimisation automatique pour mobile (Portrait, PNG)</p>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                      <div className="space-y-4">
                        <div className="aspect-[9/16] bg-zinc-900 rounded-3xl overflow-hidden relative border-2 border-dashed border-zinc-800 group">
                          {heroImage && !heroImageError && (
                            <img 
                              src={heroImage} 
                              className="w-full h-full object-cover"
                              alt="Aperçu"
                              onError={() => setHeroImageError(true)}
                            />
                          )}
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <p className="text-white font-bold">Image Actuelle</p>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col justify-center space-y-6">
                        <div className="p-6 bg-zinc-50 rounded-3xl border border-zinc-100 space-y-4">
                          <h4 className="font-bold flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-emerald-600" />
                            Module d'Upload Intelligent
                          </h4>
                          <p className="text-sm text-zinc-600 leading-relaxed">
                            Téléchargez une image haute résolution. Notre système va automatiquement :
                          </p>
                          <ul className="text-xs text-zinc-500 space-y-2 list-disc pl-4">
                            <li>Redimensionner au format portrait (1080x1920)</li>
                            <li>Centrer l'image pour un rendu optimal</li>
                            <li>Enregistrer au format PNG (accueil-vtc.png)</li>
                            <li>Forcer le rafraîchissement de l'application</li>
                          </ul>
                        </div>

                        <div className="relative">
                          <input 
                            type="file" 
                            accept="image/*" 
                            onChange={handleImageUpload}
                            className="hidden" 
                            id="hero-upload"
                            disabled={isUploadingImage}
                          />
                          <label 
                            htmlFor="hero-upload"
                            className={cn(
                              "flex items-center justify-center gap-3 w-full py-6 rounded-2xl font-bold transition-all cursor-pointer shadow-lg",
                              isUploadingImage 
                                ? "bg-zinc-100 text-zinc-400 cursor-not-allowed" 
                                : "bg-emerald-600 text-white hover:bg-emerald-700 active:scale-95"
                            )}
                          >
                            <Upload className="w-6 h-6" />
                            {isUploadingImage ? "Optimisation en cours..." : "Télécharger la nouvelle image"}
                          </label>
                        </div>

                        {uploadDebugMessage && (
                          <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-2xl text-emerald-700 text-sm font-bold animate-pulse">
                            {uploadDebugMessage}
                          </div>
                        )}

                        {isUploadingImage && (
                          <div className="space-y-2">
                            <div className="flex justify-between text-xs font-bold text-zinc-500 uppercase tracking-wider">
                              <span>Progression</span>
                              <span>{uploadProgress}%</span>
                            </div>
                            <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                              <motion.div 
                                initial={{ width: 0 }}
                                animate={{ width: `${uploadProgress}%` }}
                                className="h-full bg-emerald-600"
                              />
                            </div>
                          </div>
                        )}

                        {uploadSuccess && (
                          <motion.div 
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="p-4 bg-emerald-50 text-emerald-700 rounded-2xl border border-emerald-100 flex items-center gap-3 font-bold text-sm"
                          >
                            <Check className="w-5 h-5" />
                            Image optimisée avec succès !
                          </motion.div>
                        )}

                        <div className="pt-8 border-t border-zinc-100">
                          <button 
                            onClick={handleSaveSettings}
                            className="w-full py-5 bg-emerald-600 text-white rounded-2xl font-bold text-lg hover:bg-emerald-700 transition-all shadow-xl shadow-emerald-600/20 active:scale-[0.98] flex items-center justify-center gap-3"
                          >
                            <CheckCircle className="w-6 h-6" />
                            ENREGISTRER LES MODIFICATIONS
                          </button>
                        </div>
                      </div>
                    </div>
                  </section>
                )}
                {adminTab === 'sites' && (
                  <section className="grid gap-4 sm:gap-8">
                    <div className="w-full bg-white p-3 sm:p-6 rounded-xl sm:rounded-3xl border border-zinc-200 shadow-sm space-y-4 sm:space-y-6 box-border overflow-hidden">
                      <div className="flex items-center gap-3 md:gap-4 px-1">
                        <div className="w-10 h-10 md:w-12 md:h-12 bg-emerald-100 rounded-xl md:rounded-2xl flex items-center justify-center shrink-0">
                          <Landmark className="w-5 h-5 md:w-6 md:h-6 text-emerald-600" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-base md:text-2xl font-display font-bold truncate">Gestion des Sites Touristiques</h3>
                          <p className="text-zinc-500 text-[10px] md:text-sm truncate">Ajoutez et gérez les points d'intérêt de votre région</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 w-full">
                        <div className="space-y-2 w-full">
                          <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider ml-1">Ville de recherche</label>
                          <PhotonAutocompleteInput 
                            placeholder="Ex: Montpellier, Lodève..."
                            defaultValue={adminSearchCity}
                            onPlaceSelect={(loc) => setAdminSearchCity(loc.address)}
                          />
                        </div>
                        <div className="space-y-2 w-full">
                          <div className="flex justify-between items-center ml-1">
                            <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Rayon d'action</label>
                            <span className="text-sm font-bold text-emerald-600">{adminSearchRadius} km</span>
                          </div>
                          <div className="flex items-center gap-3 bg-zinc-50 p-3 rounded-xl sm:rounded-2xl border border-zinc-200 w-full box-border">
                            <input 
                              type="range" 
                              min="0" 
                              max="100" 
                              value={adminSearchRadius ?? 0}
                              onChange={(e) => setAdminSearchRadius(e.target.value === '' ? 0 : parseInt(e.target.value))}
                              className="flex-1 h-2 bg-zinc-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                            />
                            <div className="w-10 md:w-12 text-right text-[10px] md:text-xs font-bold text-zinc-500">
                              {adminSearchRadius}km
                            </div>
                          </div>
                        </div>
                      </div>

                      <button 
                        onClick={handleSuggestSites}
                        disabled={isSuggesting || !adminSearchCity}
                        className="w-full py-4 bg-emerald-600 text-white rounded-2xl font-bold hover:bg-emerald-700 transition-all disabled:opacity-50 flex items-center justify-center gap-3 shadow-lg shadow-emerald-600/20"
                      >
                        <Compass className={cn("w-5 h-5", isSuggesting && "animate-spin")} />
                        {isSuggesting ? "RECHERCHE EN COURS..." : "LANCER LA RECHERCHE PAR IA"}
                      </button>
                    </div>

                    {/* Suggestions de l'IA */}
                    {suggestedSites.length > 0 && (
                      <div className="p-3 sm:p-6 bg-emerald-50 rounded-xl sm:rounded-3xl border border-emerald-100 space-y-4 sm:space-y-6 overflow-hidden">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex items-center gap-3">
                            <Sparkles className="w-5 h-5 text-emerald-600" />
                            <h4 className="text-sm font-bold text-emerald-800 uppercase tracking-widest">Suggestions IA trouvées ({suggestedSites.length})</h4>
                          </div>
                          <div className="flex items-center gap-4">
                            <button 
                              onClick={() => {
                                if (selectedSuggestedSites.length === suggestedSites.length) {
                                  setSelectedSuggestedSites([]);
                                } else {
                                  setSelectedSuggestedSites(suggestedSites.map(s => s.name));
                                }
                              }} 
                              className="text-emerald-600 text-xs font-bold hover:underline"
                            >
                              {selectedSuggestedSites.length === suggestedSites.length ? "Tout désélectionner" : "Tout sélectionner"}
                            </button>
                            <button onClick={() => setSuggestedSites([])} className="text-zinc-400 text-xs font-bold hover:underline">Tout effacer</button>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 sm:gap-4">
                          {suggestedSites.map(s => (
                            <div 
                              key={s.name} 
                              onClick={() => {
                                setSelectedSuggestedSites(prev => 
                                  prev.includes(s.name) ? prev.filter(id => id !== s.name) : [...prev, s.name]
                                );
                              }}
                              className={cn(
                                "flex items-center justify-between bg-white p-3 sm:p-4 md:p-5 rounded-xl sm:rounded-3xl border transition-all cursor-pointer",
                                selectedSuggestedSites.includes(s.name) ? "border-emerald-500 ring-2 ring-emerald-500/20 shadow-md" : "border-emerald-100 shadow-sm hover:shadow-md"
                              )}
                            >
                              <div className="flex items-center gap-3 md:gap-4 flex-1 min-w-0">
                                <div className={cn(
                                  "w-5 h-5 md:w-6 md:h-6 rounded-lg border-2 flex items-center justify-center transition-colors shrink-0",
                                  selectedSuggestedSites.includes(s.name) ? "bg-emerald-600 border-emerald-600" : "border-zinc-200"
                                )}>
                                  {selectedSuggestedSites.includes(s.name) && <Check className="w-3 h-3 md:w-4 md:h-4 text-white" />}
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className="text-sm font-bold truncate">{s.name}</p>
                                    {s.isHandicapAccessible && (
                                      <span className="text-[8px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded font-bold flex items-center gap-1 shrink-0">
                                        ♿ PMR
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-[10px] text-zinc-500 line-clamp-1 mt-0.5">{s.description}</p>
                                  <div className="flex gap-2 mt-2 flex-wrap">
                                    <span className="text-[9px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full font-bold uppercase border border-emerald-100">{(s.categories || []).join(', ')}</span>
                                    <span className="text-[9px] bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                                      <Star className="w-2.5 h-2.5 text-amber-500 fill-amber-500" /> {s.googleRating}/5 ({s.googleReviewsCount} avis)
                                    </span>
                                    {s.isPaid && <span className="text-[9px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-bold border border-amber-100">Payant</span>}
                                    {s.openingHours && <span className="text-[9px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-bold border border-blue-100">{s.openingHours}</span>}
                                  </div>
                                  {s.address && <p className="text-[9px] text-zinc-400 mt-1 truncate flex items-center gap-1"><MapPin className="w-2.5 h-2.5" /> {s.address}</p>}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        <div className="pt-4">
                          <button 
                            onClick={handleSaveSelectedSites}
                            disabled={isGenerating || selectedSuggestedSites.length === 0}
                            className="w-full py-4 bg-emerald-600 text-white rounded-2xl font-bold hover:bg-emerald-700 transition-all disabled:opacity-50 flex items-center justify-center gap-3 shadow-lg shadow-emerald-600/20 box-border"
                          >
                            {isGenerating ? (
                              <>
                                <Hourglass className="w-5 h-5 animate-spin" />
                                ENREGISTREMENT EN COURS...
                              </>
                            ) : (
                              <>
                                <Plus className="w-5 h-5" />
                                ENREGISTRER LES {selectedSuggestedSites.length} SITES SÉLECTIONNÉS
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Liste des Sites par Catégorie */}
                    <div className="space-y-6">
                      <div className="flex overflow-x-auto pb-4 gap-3 no-scrollbar">
                        {SITE_CATEGORIES.map(cat => (
                          <button
                            key={cat.id}
                            onClick={() => setAdminSiteCategory(cat.id as any)}
                            className={cn(
                              "flex items-center gap-2 px-6 py-3.5 rounded-2xl font-bold text-xs whitespace-nowrap transition-all border shadow-sm",
                              adminSiteCategory === cat.id 
                                ? "bg-emerald-600 text-white border-emerald-600 shadow-emerald-600/20" 
                                : "bg-white text-zinc-500 border-zinc-200 hover:border-emerald-300"
                            )}
                          >
                            <cat.icon className="w-4 h-4" />
                            {cat.label}
                          </button>
                        ))}
                      </div>

                      <div className="grid grid-cols-1 gap-6">
                        {appData.sites.filter(s => (s.categories || []).includes(adminSiteCategory)).map(site => (
                          <div key={site.id} className="group bg-white rounded-xl sm:rounded-[2rem] border border-zinc-200 overflow-hidden hover:shadow-2xl transition-all duration-500 flex flex-col lg:flex-row min-h-[320px]">
                            <div className="relative h-48 lg:h-auto lg:w-72 bg-zinc-100 overflow-hidden flex items-center justify-center shrink-0 border-b lg:border-b-0 lg:border-r border-zinc-100">
                              {site.image && site.image.length > 0 && !site.image.includes('maps.googleapis.com/maps/api/place/js/PhotoService.GetPhoto') ? (
                                <img 
                                  src={site.image} 
                                  alt={site.name} 
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                  referrerPolicy="no-referrer"
                                  loading="lazy"
                                />
                              ) : (
                                <div className="w-full h-full bg-emerald-50 flex items-center justify-center text-emerald-200">
                                  {(site.categories || []).includes('patrimoine') && <Landmark className="w-12 h-12" />}
                                  {(site.categories || []).includes('nature') && <Trees className="w-12 h-12" />}
                                  {(site.categories || []).includes('activites') && <Sparkles className="w-12 h-12" />}
                                  {(site.categories || []).includes('randonnee') && <Footprints className="w-12 h-12" />}
                                  {(site.categories || []).includes('points_de_vue') && <Mountain className="w-12 h-12" />}
                                  {(site.categories || []).includes('terroir') && <Coffee className="w-12 h-12" />}
                                  {(site.categories || []).includes('restauration') && <UtensilsCrossed className="w-12 h-12" />}
                                  {(site.categories || []).includes('insolite') && <Star className="w-12 h-12" />}
                                  {!(site.categories || []).some(c => ['patrimoine', 'nature', 'activites', 'randonnee', 'points_de_vue', 'terroir', 'restauration', 'insolite'].includes(c)) && <MapPin className="w-12 h-12" />}
                                </div>
                              )}
                              
                              <div className="absolute top-3 right-3 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                <button 
                                  onClick={() => setEditingSite(site)}
                                  className="p-2 bg-white shadow-lg text-zinc-600 rounded-xl hover:bg-emerald-600 hover:text-white transition-all border border-zinc-100"
                                  title="Modifier"
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button 
                                  onClick={() => {
                                    const updated = appData.sites.filter(s => s.id !== site.id);
                                    setAppData(prev => ({ ...prev, sites: updated }));
                                  }}
                                  className="p-2 bg-white shadow-lg text-zinc-600 rounded-xl hover:bg-red-600 hover:text-white transition-all border border-zinc-100"
                                  title="Supprimer"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>

                            <div className="flex-1 p-6 flex flex-col justify-between">
                              <div className="space-y-4">
                                <div className="flex items-start justify-between gap-4">
                                  <div className="space-y-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <h4 className="text-zinc-900 font-bold text-2xl tracking-tight">{site.name}</h4>
                                      {site.isHandicapAccessible && (
                                        <span className="bg-blue-100 text-blue-700 text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
                                          ♿ PMR
                                        </span>
                                      )}
                                      {site.isFree && (
                                        <span className="bg-emerald-100 text-emerald-700 text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
                                          Gratuit
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-4 text-xs text-zinc-400 font-medium">
                                      <div className="flex items-center gap-1.5">
                                        <MapPin className="w-3.5 h-3.5" />
                                        {site.sector}
                                      </div>
                                      {site.googleRating && (
                                        <div className="flex items-center gap-1.5">
                                          <Star className="w-3.5 h-3.5 text-yellow-400 fill-current" />
                                          <span className="text-zinc-700 font-bold">{site.googleRating}</span>
                                          <span className="text-zinc-400">({site.googleReviewsCount} avis)</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  
                                  <div className="flex gap-2">
                                    <label className="cursor-pointer group/upload">
                                      <div className="p-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-zinc-500 hover:bg-emerald-50 hover:border-emerald-200 hover:text-emerald-600 transition-all shadow-sm">
                                        <Upload className="w-4 h-4" />
                                      </div>
                                      <input 
                                        type="file" 
                                        className="hidden" 
                                        accept="image/*"
                                        onChange={(e) => handleSiteImageUpload(e, site.id)}
                                      />
                                    </label>
                                    <button 
                                      onClick={() => handleGenerateSiteImage(site.id, site.name)}
                                      className="p-2.5 bg-emerald-50 border border-emerald-100 rounded-xl text-emerald-600 hover:bg-emerald-600 hover:text-white transition-all shadow-sm"
                                      title="Générer par IA"
                                    >
                                      <Sparkles className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>

                                <p className="text-sm text-zinc-500 leading-relaxed max-w-3xl">{site.description}</p>
                                
                                <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-6 gap-4 py-4 border-y border-zinc-50">
                                  <div className="space-y-1">
                                    <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Tarifs</p>
                                    <div className="flex flex-wrap gap-x-3 gap-y-1">
                                      <span className="text-sm font-bold text-zinc-800">Adulte: {site.adultPrice || 0}€</span>
                                      <span className="text-xs text-zinc-500">Enfant: {site.child6_12Price || 0}€</span>
                                    </div>
                                  </div>
                                  <div className="space-y-1">
                                    <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Saison</p>
                                    <p className="text-sm font-bold text-zinc-800 truncate">{site.openingSeason || 'Non renseigné'}</p>
                                  </div>
                                  <div className="space-y-1">
                                    <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Horaires</p>
                                    <p className="text-sm font-bold text-zinc-800 truncate">{site.openingHours || 'Non renseigné'}</p>
                                  </div>
                                  <div className="space-y-1">
                                    <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Adresse</p>
                                    <p className="text-sm font-bold text-zinc-800 truncate">{site.address || 'Non renseigné'}</p>
                                  </div>
                                  <div className="space-y-1">
                                    <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Téléphone</p>
                                    <p className="text-sm font-bold text-zinc-800 truncate">{site.phone || 'Non renseigné'}</p>
                                  </div>
                                  <div className="space-y-1">
                                    <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Site Web</p>
                                    <p className="text-sm font-bold text-zinc-800 truncate">{site.website || 'Non renseigné'}</p>
                                  </div>
                                </div>
                              </div>

                              <div className="mt-6 flex items-center justify-between gap-4">
                                <div className="flex gap-2">
                                  {(site.categories || []).map(catId => {
                                    const cat = SITE_CATEGORIES.find(c => c.id === catId);
                                    if (!cat) return null;
                                    return (
                                      <span key={catId} className="px-3 py-1 bg-zinc-100 text-zinc-500 rounded-lg text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5">
                                        <cat.icon className="w-3 h-3" />
                                        {cat.label}
                                      </span>
                                    );
                                  })}
                                </div>
                                <button 
                                  onClick={() => setEditingSite(site)}
                                  className="px-6 py-2.5 bg-zinc-900 text-white rounded-xl text-xs font-bold hover:bg-emerald-600 transition-all flex items-center gap-2 shadow-lg shadow-black/5"
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                  MODIFIER LA FICHE
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}


                        {appData.sites.filter(s => (s.categories || []).includes(adminSiteCategory)).length === 0 && (
                          <div className="col-span-full py-24 text-center space-y-4 bg-zinc-50 rounded-[2.5rem] border-2 border-dashed border-zinc-200">
                            <div className="w-20 h-20 bg-zinc-100 rounded-full flex items-center justify-center mx-auto">
                              <MapPin className="w-10 h-10 text-zinc-300" />
                            </div>
                            <div className="max-w-xs mx-auto">
                              <p className="text-zinc-600 font-bold">Aucun site dans cette catégorie</p>
                              <p className="text-xs text-zinc-400 mt-1">Utilisez le module de recherche ci-dessus pour ajouter des sites touristiques à votre catalogue.</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="pt-8 border-t border-zinc-100 w-full">
                      <button 
                        onClick={handleSaveSettings}
                        className="w-full py-5 bg-emerald-600 text-white rounded-2xl font-bold text-base md:text-lg hover:bg-emerald-700 transition-all shadow-xl shadow-emerald-600/20 active:scale-[0.98] flex items-center justify-center gap-3 box-border"
                      >
                        <CheckCircle className="w-5 h-5 md:w-6 md:h-6" />
                        ENREGISTRER LES MODIFICATIONS
                      </button>
                    </div>
                  </section>
                )}

                {adminTab === 'customers' && (
                  <section className="bg-white p-4 sm:p-6 rounded-2xl sm:rounded-3xl border border-zinc-200 space-y-6 overflow-hidden">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <h3 className="text-xl font-bold">Fichier Clients</h3>
                        <div className="text-xs text-zinc-400 font-medium bg-zinc-50 px-3 py-1 rounded-full border border-zinc-100">
                          {appData.users.length} clients
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row w-full sm:w-auto gap-2">
                        <div className="relative w-full sm:w-48">
                          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                          <input 
                            type="text" 
                            placeholder="Nom de famille..." 
                            value={customerSearchLastName}
                            onChange={(e) => setCustomerSearchLastName(e.target.value)}
                            className="w-full pl-9 pr-4 py-2 bg-zinc-50 border border-zinc-200 rounded-xl text-sm text-zinc-900 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none"
                          />
                        </div>
                        <div className="relative w-full sm:w-48">
                          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                          <input 
                            type="text" 
                            placeholder="Prénom..." 
                            value={customerSearchFirstName}
                            onChange={(e) => setCustomerSearchFirstName(e.target.value)}
                            className="w-full pl-9 pr-4 py-2 bg-zinc-50 border border-zinc-200 rounded-xl text-sm text-zinc-900 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="overflow-x-auto -mx-4 sm:mx-0">
                      <div className="inline-block min-w-full align-middle px-4 sm:px-0">
                        <table className="w-full text-left text-sm border-collapse">
                          <thead>
                            <tr className="border-b border-zinc-100">
                              <th className="pb-4 font-bold text-zinc-400 uppercase tracking-wider text-[10px]">Client</th>
                              <th className="pb-4 font-bold text-zinc-400 uppercase tracking-wider text-[10px]">Contact</th>
                              <th className="pb-4 font-bold text-zinc-400 uppercase tracking-wider text-[10px] hidden sm:table-cell">Origine</th>
                              <th className="pb-4 font-bold text-zinc-400 uppercase tracking-wider text-[10px] hidden md:table-cell">Adresse</th>
                              <th className="pb-4 font-bold text-zinc-400 uppercase tracking-wider text-[10px] text-right">Action</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-zinc-50">
                            {appData.users
                              .filter(u => {
                                const searchLastLower = customerSearchLastName.toLowerCase();
                                const searchFirstLower = customerSearchFirstName.toLowerCase();
                                const matchLast = !searchLastLower || (u.lastName?.toLowerCase().includes(searchLastLower));
                                const matchFirst = !searchFirstLower || (u.firstName?.toLowerCase().includes(searchFirstLower));
                                return matchLast && matchFirst;
                              })
                              .sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''))
                              .map(u => (
                              <tr 
                                key={u.id} 
                                onClick={() => setSelectedCustomer(u)}
                                className="hover:bg-emerald-50/30 transition-colors cursor-pointer group"
                              >
                                <td className="py-4 pr-4">
                                  <div className="font-bold text-zinc-900 group-hover:text-emerald-700 transition-colors">{u.lastName} {u.firstName}</div>
                                  <div className="text-[10px] text-zinc-400 font-mono">#{u.id.slice(-6).toUpperCase()}</div>
                                </td>
                                <td className="py-4 pr-4">
                                  <div className="text-xs truncate max-w-[150px]">{u.email || 'Pas d\'email'}</div>
                                  <div className="font-medium text-emerald-600 text-xs">{u.phone || 'Pas de tel'}</div>
                                </td>
                                <td className="py-4 pr-4 hidden sm:table-cell">
                                  <span className={cn(
                                    "px-2 py-0.5 rounded-full text-[9px] font-bold uppercase border",
                                    u.provider === 'google' ? "bg-blue-50 text-blue-600 border-blue-100" : "bg-zinc-50 text-zinc-600 border-zinc-100"
                                  )}>
                                    {u.provider}
                                  </span>
                                </td>
                                <td className="py-4 pr-4 text-zinc-500 text-xs hidden md:table-cell max-w-[200px] truncate">
                                  {u.address || '-'}
                                </td>
                                <td className="py-4 text-right">
                                  <button className="p-2 hover:bg-emerald-100 rounded-lg text-emerald-600 transition-colors">
                                    <ChevronRight className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                            {appData.users.length === 0 && (
                              <tr>
                                <td colSpan={5} className="py-12 text-center text-zinc-400 italic">
                                  Aucun client enregistré pour le moment.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </section>
                )}

                {adminTab === 'orders' && (
                  <section className="bg-white p-6 rounded-3xl border border-zinc-200 space-y-6">
                    <h3 className="text-xl font-bold">Historique & Bons de Commande</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-sm">
                        <thead>
                          <tr className="border-b border-zinc-100">
                            <th className="pb-4 font-bold text-zinc-400 uppercase tracking-wider">Date</th>
                            <th className="pb-4 font-bold text-zinc-400 uppercase tracking-wider">Client</th>
                            <th className="pb-4 font-bold text-zinc-400 uppercase tracking-wider">Type</th>
                            <th className="pb-4 font-bold text-zinc-400 uppercase tracking-wider text-right">Montant</th>
                            <th className="pb-4 font-bold text-zinc-400 uppercase tracking-wider text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-50">
                          {completedBookings.map(b => {
                            const customer = appData.users.find(u => u.id === b.customerId);
                            return (
                              <tr key={b.id} className="hover:bg-zinc-50 transition-colors">
                                <td className="py-4">
                                  <div className="font-medium">{new Date(b.completedAt || b.timestamp).toLocaleDateString('fr-FR')}</div>
                                  <div className="text-[10px] text-zinc-400">{new Date(b.completedAt || b.timestamp).toLocaleTimeString('fr-FR')}</div>
                                </td>
                                <td className="py-4">
                                  <div className="font-bold">{customer ? `${customer.firstName} ${customer.lastName}` : 'Anonyme'}</div>
                                  <div className="text-xs text-zinc-400">{customer?.phone || '-'}</div>
                                </td>
                                <td className="py-4">
                                  <span className={cn(
                                    "px-2 py-1 rounded-full text-[10px] font-bold uppercase",
                                    b.type === 'vtc' ? "bg-zinc-100 text-zinc-600" : 
                                    b.type === 'tour' ? "bg-emerald-50 text-emerald-600" : 
                                    "bg-amber-50 text-amber-600"
                                  )}>
                                    {b.type === 'custom' ? 'Sur Mesure' : b.type}
                                  </span>
                                </td>
                                <td className="py-4 text-right font-bold">
                                  {(b.price || 0).toFixed(2)} €
                                </td>

                              </tr>
                            );
                          })}
                          {completedBookings.length === 0 && (
                            <tr>
                              <td colSpan={5} className="py-12 text-center text-zinc-400 italic">
                                Aucun historique de commande disponible.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}

                {adminTab === 'packs' && (
                  <div className="grid gap-4 sm:gap-8">
                    <section className="bg-white p-3 sm:p-6 rounded-xl sm:rounded-3xl border border-zinc-200 space-y-4 sm:space-y-6 overflow-hidden">
                      <div className="flex items-center justify-between px-1">
                        <h3 className="text-lg sm:text-xl font-bold">Packs et Formules</h3>
                        <button
                          onClick={() => setIsManualSiteModalOpen(true)}
                          className="px-4 py-2 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-all text-sm flex items-center gap-2"
                        >
                          <Plus className="w-4 h-4" /> Ajouter un site
                        </button>
                        <div className="flex items-center gap-2 bg-zinc-100 p-1 rounded-lg">
                          <button
                            onClick={() => setAdminPackViewMode('grid')}
                            className={cn("p-2 rounded-md transition-all", adminPackViewMode === 'grid' ? "bg-white shadow-sm text-emerald-600" : "text-zinc-400")}
                            title="Vue Grille"
                          >
                            <LayoutDashboard className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setAdminPackViewMode('detail')}
                            className={cn("p-2 rounded-md transition-all", adminPackViewMode === 'detail' ? "bg-white shadow-sm text-emerald-600" : "text-zinc-400")}
                            title="Vue Détaillée"
                          >
                            <FileText className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="space-y-4">
                        {/* Moteur de Recherche IA */}
                        <div className="p-3 sm:p-4 bg-emerald-50 border border-emerald-100 rounded-2xl space-y-4 relative overflow-hidden">
                          <div className="absolute top-0 right-0 p-8 opacity-5">
                            <Sparkles className="w-32 h-32 text-emerald-900" />
                          </div>
                          <div className="relative z-10 space-y-4">
                            <h4 className="text-sm font-bold text-emerald-800 flex items-center gap-2">
                              <Search className="w-4 h-4" /> Moteur de Recherche IA
                            </h4>
                            <p className="text-emerald-700 text-sm max-w-xl">
                              Sélectionnez un secteur et décrivez le circuit idéal (ex: "Un circuit gastronomique avec visite de cave"). L'IA sélectionnera les meilleurs sites pour vous.
                            </p>
                            
                            {/* Sélecteur de Secteur (Swipeable) pour la recherche */}
                            <div className="flex overflow-x-auto pb-2 gap-2 no-scrollbar scroll-smooth snap-x snap-mandatory">
                              {TOUR_SECTORS.map(sector => (
                                <button
                                  key={sector}
                                  onClick={() => setAiSearchSector(sector)}
                                  className={cn(
                                    "px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all border snap-start shrink-0",
                                    aiSearchSector === sector
                                      ? "bg-emerald-600 text-white border-emerald-600 shadow-md shadow-emerald-600/20"
                                      : "bg-white text-emerald-600 border-emerald-200 hover:border-emerald-300"
                                  )}
                                >
                                  {sector}
                                </button>
                              ))}
                            </div>

                            <AISearchInput 
                              initialPrompt={aiCircuitSearchPrompt}
                              initialLimit={aiSearchLimit}
                              sector={aiSearchSector}
                              isSearching={isAiSearchingCircuit}
                              onSearch={(prompt, limit) => handleSearchCircuitByAI(prompt, limit)}
                            />

                          {isManualSiteModalOpen && (
                            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[110] flex items-center justify-center p-4">
                              <div className="bg-white rounded-3xl p-8 max-w-md w-full space-y-6 shadow-2xl">
                                <h3 className="text-xl font-bold">Ajouter un site manuellement</h3>
                                <input
                                  type="text"
                                  value={manualSiteName}
                                  onChange={(e) => setManualSiteName(e.target.value)}
                                  placeholder="Nom du site"
                                  className="w-full p-3 bg-zinc-50 border border-zinc-200 rounded-xl text-zinc-900"
                                />
                                <div className="flex gap-4">
                                  <button
                                    onClick={() => setIsManualSiteModalOpen(false)}
                                    className="flex-1 py-3 bg-zinc-100 text-zinc-600 rounded-xl font-bold hover:bg-zinc-200"
                                  >
                                    Annuler
                                  </button>
                                  <button
                                    onClick={async () => {
                                      if (!manualSiteName.trim()) return;
                                      
                                      const newSite: TouristSite = {
                                        id: Date.now().toString(),
                                        name: manualSiteName,
                                        address: '',
                                        description: '',
                                        fullDescription: '',
                                        categories: [],
                                        isPaid: false,
                                        sector: aiSearchSector,
                                        location: { lat: 0, lng: 0 },
                                        isVisibleClient: false,
                                        tags: [],
                                        image: '',
                                        isEmblematic: false,
                                        sources: { name: 'manual' }
                                      };
                                      
                                      // Peuple les données
                                      const siteData = await fetchAIDataForSite(manualSiteName, '', []);
                                      if (siteData) {
                                          Object.assign(newSite, siteData);
                                      }
                                      
                                      setAppData(prev => ({ ...prev, sites: [...prev.sites, newSite] }));
                                      
                                      setIsManualSiteModalOpen(false);
                                      setManualSiteName('');
                                    }}
                                    className="flex-1 py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700"
                                  >
                                    Ajouter
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                          </div>

                          {aiSearchResult && (
                            <motion.div 
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="bg-white border border-emerald-200 p-3 sm:p-4 rounded-xl space-y-4 relative z-10 mt-4"
                            >
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                <div className="space-y-1">
                                  <label className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest">Titre du Secteur (IA)</label>
                                  <input 
                                    type="text"
                                    value={aiSearchResult.sectorTitle || ''}
                                    onChange={(e) => setAiSearchResult({...aiSearchResult, sectorTitle: e.target.value})}
                                    className="w-full p-2 bg-zinc-50 border border-zinc-200 rounded-lg text-black font-bold text-sm"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <label className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest">Description du Secteur (IA)</label>
                                  <textarea 
                                    value={aiSearchResult.sectorDescription || ''}
                                    onChange={(e) => setAiSearchResult({...aiSearchResult, sectorDescription: e.target.value})}
                                    className="w-full p-2 bg-zinc-50 border border-zinc-200 rounded-lg text-black text-xs h-16"
                                  />
                                </div>
                              </div>

                              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <h4 className="text-sm font-bold text-emerald-700">Propositions ({aiSearchResult?.propositions?.length || 0})</h4>
                                <button 
                                  onClick={() => {
                                    if (!aiSearchResult) return;
                                    // Apply AI results to current sector
                                    const sectorConfigs = { ...(appData.settings.sectorConfigs || {}) };
                                    sectorConfigs[aiSearchSector] = { 
                                      title: aiSearchResult.sectorTitle,
                                      description: aiSearchResult.sectorDescription
                                    };
                                    updateSettings({ ...appData.settings, sectorConfigs: sectorConfigs });
                                    
                                    // Handle propositions
                                    const selectedProps = (aiSearchResult.propositions || []).filter((p: any) => p.selected !== false);
                                    
                                    // We always merge new sites into existing ones, updating duplicates by name
                                    let updatedSites = [...appData.sites];

                                    // Update existing sites visibility and data
                                    updatedSites = updatedSites.map(s => {
                                      // Match by ID or by name in the same sector
                                      const aiProp = selectedProps.find((p: any) => 
                                        (p.isExisting && p.existingId === s.id) || 
                                        (s.sector === aiSearchSector && s.name.toLowerCase() === p.name.toLowerCase())
                                      );
                                      
                                      if (s.sector === aiSearchSector && aiProp) {
                                        return {
                                          ...s,
                                          isVisibleClient: true,
                                          description: aiProp.shortDescription || aiProp.description || aiProp.summary || s.description,
                                          fullDescription: aiProp.fullDescription || aiProp.summary || s.fullDescription,
                                          categories: aiProp.categories || s.categories,
                                          googleRating: aiProp.rating || aiProp.googleRating || s.googleRating,
                                          googleReviewsCount: aiProp.userRatingsTotal || aiProp.googleReviewsCount || s.googleReviewsCount,
                                          openingHours: aiProp.openingHours || aiProp.hours || s.openingHours,
                                          gps: aiProp.gps || (aiProp.location ? `${aiProp.location.lat}, ${aiProp.location.lng}` : s.gps),
                                          address: aiProp.address || s.address,
                                          phone: aiProp.phoneNumber || aiProp.phone || s.phone,
                                          website: aiProp.website || s.website,
                                          image: s.image || aiProp.photoUrl || '',
                                          conditions: aiProp.accessibility || aiProp.conditions || s.conditions,
                                          isHandicapAccessible: aiProp.isHandicapAccessible !== undefined ? aiProp.isHandicapAccessible : s.isHandicapAccessible,
                                          isAccommodation: (aiProp.categories || s.categories || []).includes('hebergement'),
                                          cateringType: (aiProp.categories || s.categories || []).includes('restauration') ? 'restaurant' : s.cateringType,
                                          adultPrice: aiProp.adultPrice !== undefined ? aiProp.adultPrice : s.adultPrice,
                                          child6_12Price: aiProp.childPrice !== undefined ? aiProp.childPrice : s.child6_12Price,
                                          child3_6Price: aiProp.childPrice !== undefined ? aiProp.childPrice : s.child3_6Price,
                                          isFree: aiProp.isFree !== undefined ? aiProp.isFree : s.isFree,
                                        };
                                      }
                                      return s;
                                    });

                                    // Create new sites for non-existing propositions
                                    const newSitesProps = selectedProps.filter((p: any) => {
                                      // If it was already in the library (even if AI didn't mark it as existing), don't add it as new
                                      const alreadyExists = appData.sites.some(s => 
                                        (p.isExisting && p.existingId === s.id) || 
                                        (s.sector === aiSearchSector && s.name.toLowerCase() === p.name.toLowerCase())
                                      );
                                      return !alreadyExists;
                                    });

                                    const newSites: TouristSite[] = newSitesProps.map((p: any) => ({
                                      id: `site-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                                      name: p.name,
                                      description: p.shortDescription || p.description || p.summary || '',
                                      fullDescription: p.fullDescription || p.summary || '',
                                      categories: p.categories || (p.category ? [p.category] : []),
                                      sector: aiSearchSector,
                                      isVisibleClient: true,
                                      isAccommodation: p.categories?.some((c: string) => c.toLowerCase().includes('hebergement')) || p.category?.toLowerCase().includes('hebergement') || false,
                                      isMarket: p.categories?.some((c: string) => c.toLowerCase().includes('marché')) || p.category?.toLowerCase().includes('marché') || false,
                                      cateringType: (p.categories?.some((c: string) => c.toLowerCase().includes('restauration') || c.toLowerCase().includes('resto') || c.toLowerCase().includes('restaurant')) || p.category?.toLowerCase().includes('restauration') || p.category?.toLowerCase().includes('resto') || p.category?.toLowerCase().includes('restaurant')) ? 'restaurant' : 'none',
                                      openingHours: p.openingHours || p.hours || "09:00 - 18:00",
                                      openingSeason: "Toute l'année",
                                      googleRating: p.rating || p.googleRating || 4.5,
                                      googleReviewsCount: p.userRatingsTotal || p.googleReviewsCount || 0,
                                      gps: p.gps || (p.location ? `${p.location.lat}, ${p.location.lng}` : ''),
                                      address: p.address || '',
                                      conditions: p.accessibility || p.conditions || '',
                                      isHandicapAccessible: p.isHandicapAccessible || false,
                                      adultPrice: p.adultPrice || 0,
                                      child6_12Price: p.childPrice || 0,
                                      child3_6Price: p.childPrice || 0,
                                      babyPrice: 0,
                                      isFree: p.isFree || false,
                                      isPaid: p.isPaid || false,
                                      phone: p.phoneNumber || p.phone || '',
                                      website: p.website || '',
                                      image: p.photoUrl || '',
                                      location: p.location || { lat: 43.9, lng: 3.8, address: p.address },
                                      tags: []
                                    }));

                                    let finalSites = [...updatedSites, ...newSites];

                                    // Final deduplication by name in the same sector (just in case)
                                    const seen = new Set();
                                    finalSites = finalSites.filter(s => {
                                      const key = `${s.sector}-${s.name.toLowerCase()}`;
                                      if (seen.has(key)) return false;
                                      seen.add(key);
                                      return true;
                                    });

                                    const finalConfigs = { ...(appData.settings.sectorConfigs || {}) };
                                    finalConfigs[aiSearchSector] = { 
                                      ...(finalConfigs[aiSearchSector] || {}), 
                                      title: aiSearchResult.sectorTitle || finalConfigs[aiSearchSector]?.title || '',
                                      description: aiSearchResult.sectorDescription || finalConfigs[aiSearchSector]?.description || ''
                                    };
                                    
                                    const finalSettings = { ...appData.settings, sectorConfigs: finalConfigs };
                                    const finalAppData = { ...appData, sites: finalSites, settings: finalSettings };
                                    saveAppData(finalAppData).then(savedData => setAppData(savedData));
                                    setAiSearchResult(null);
                                    setAiCircuitSearchPrompt('');
                                    
                                    // Switch to the library tab for this sector
                                    setAdminSelectedSector(aiSearchSector);
                                    alert(`${newSites.length} nouveaux sites ajoutés et ${selectedProps.length - newSites.length} sites existants mis à jour. Le titre et la description du pack ont été enregistrés.`);
                                  }}
                                  className="w-full md:w-auto px-4 py-2 bg-emerald-600 text-white rounded-lg font-bold hover:bg-emerald-700 transition-all text-xs"
                                >
                                  APPLIQUER LES MODIFICATIONS
                                </button>
                                </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                                {aiSearchResult?.propositions?.map((prop: any, idx: number) => (
                                  <div 
                                    key={idx}
                                    className={cn(
                                      "p-2 sm:p-3 rounded-xl border transition-all flex items-start gap-2",
                                      prop.selected !== false ? "bg-emerald-50 border-emerald-200" : "bg-zinc-50 border-zinc-200 opacity-50"
                                    )}
                                  >
                                    <input 
                                      type="checkbox"
                                      checked={prop.selected !== false}
                                      onChange={(e) => {
                                        const newProps = [...aiSearchResult.propositions];
                                        newProps[idx] = { ...newProps[idx], selected: e.target.checked };
                                        setAiSearchResult({ ...aiSearchResult, propositions: newProps });
                                      }}
                                      className="mt-0.5 w-3 h-3 rounded border-zinc-300 text-emerald-600 focus:ring-emerald-500 shrink-0"
                                    />
                                    <div className="flex-1 space-y-2">
                                      <div className="flex items-center justify-between">
                                        <div className="flex flex-wrap gap-1">
                                          {SITE_CATEGORIES.filter(c => c.id !== 'autre').map(cat => (
                                            <button
                                              key={cat.id}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                const currentCats = prop.categories || [];
                                                const newCats = currentCats.includes(cat.id)
                                                  ? currentCats.filter((c: string) => c !== cat.id)
                                                  : [...currentCats, cat.id];
                                                const newProps = [...aiSearchResult.propositions];
                                                newProps[idx] = { ...newProps[idx], categories: newCats };
                                                setAiSearchResult({ ...aiSearchResult, propositions: newProps });
                                              }}
                                              className={cn(
                                                "text-[7px] font-bold px-1.5 py-0.5 rounded-full transition-all border",
                                                (prop.categories || []).includes(cat.id)
                                                  ? "bg-emerald-600 text-white border-emerald-600"
                                                  : "bg-white text-zinc-400 border-zinc-200 hover:border-emerald-300"
                                              )}
                                            >
                                              {cat.label}
                                            </button>
                                          ))}
                                        </div>
                                        {prop.isExisting ? (
                                          <span className="text-[8px] font-bold text-sky-600 uppercase bg-sky-50 px-1 rounded">Existant</span>
                                        ) : (
                                          <span className="text-[8px] font-bold text-amber-600 uppercase bg-amber-50 px-1 rounded">Nouveau</span>
                                        )}
                                      </div>
                                      <h5 className="text-xs font-bold text-zinc-900 leading-tight">{prop.name}</h5>
                                      {prop.phoneNumber && (
                                        <p className="text-[8px] text-emerald-600 font-bold flex items-center gap-1">
                                          <Phone className="w-2 h-2" /> {prop.phoneNumber}
                                        </p>
                                      )}
                                      <div className="flex items-center gap-1 text-[10px] text-amber-500 font-medium">
                                        <Star className="w-3 h-3 fill-current" />
                                        <span>{prop.googleRating || 'N/A'}</span>
                                        <span className="text-zinc-400">({prop.googleReviewsCount || 0} avis)</span>
                                      </div>
                                      <p className="text-[9px] text-zinc-500 line-clamp-2">{prop.shortDescription || prop.description}</p>
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {prop.isFree ? (
                                          <span className="text-[8px] bg-emerald-100 text-emerald-700 px-1 rounded">Gratuit</span>
                                        ) : (
                                          <span className="text-[8px] bg-blue-100 text-blue-700 px-1 rounded">Payant</span>
                                        )}
                                        {prop.openingHours && <span className="text-[8px] bg-zinc-100 text-zinc-600 px-1 rounded truncate max-w-[100px]">{prop.openingHours}</span>}
                                        {prop.isHandicapAccessible && (
                                          <span className="text-[8px] bg-blue-500 text-white px-1 rounded font-bold flex items-center gap-0.5">
                                            ♿ PMR
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </div>

                        {/* Bibliothèque par Secteur */}
                        <div className="p-3 sm:p-4 bg-purple-50 border border-purple-100 rounded-2xl space-y-4">
                          <h4 className="text-sm font-bold text-purple-800 flex items-center gap-2">
                            <Package className="w-4 h-4" /> Bibliothèque par Secteur
                          </h4>


                          </div>

                          {/* Sélecteur de Secteur (Swipeable) */}
                          <div className="flex overflow-x-auto pb-2 gap-2 no-scrollbar scroll-smooth snap-x snap-mandatory">
                            {TOUR_SECTORS.map(sector => (
                              <button
                                key={sector}
                                onClick={() => setAdminSelectedSector(sector)}
                                className={cn(
                                  "px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all border snap-start shrink-0",
                                  adminSelectedSector === sector
                                    ? "bg-purple-600 text-white border-purple-600 shadow-md shadow-purple-600/20"
                                    : "bg-white text-purple-600 border-purple-200 hover:border-purple-300"
                                )}
                              >
                                {sector}
                              </button>
                            ))}
                          </div>

                          {/* Configuration du Pack pour le secteur sélectionné */}
                          <div className="space-y-4 pt-4 border-t border-purple-200/50">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                                  <label className="text-[10px] font-bold text-purple-700 uppercase tracking-wider ml-1">Titre du Pack (Client)</label>
                                  <div className="flex items-center gap-2 w-full md:w-auto">
                                    <input 
                                      type="text"
                                      value={aiPackPrompt || ''}
                                      onChange={(e) => setAiPackPrompt(e.target.value)}
                                      placeholder="Contexte pour l'IA..."
                                      className="text-[10px] p-1.5 bg-white border border-purple-200 rounded-lg flex-1 min-w-0 md:w-32 md:focus:w-48 transition-all outline-none text-black placeholder:text-zinc-400"
                                    />
                                    <button 
                                      onClick={() => handleGeneratePackContent(adminSelectedSector)}
                                      disabled={isAiGeneratingPack || isAiGeneratingAllPacks || !aiPackPrompt.trim()}
                                      className="text-[10px] font-bold text-purple-600 hover:text-purple-800 flex items-center gap-1 disabled:opacity-30 shrink-0"
                                    >
                                      {isAiGeneratingPack ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                                      GÉNÉRER PAR IA
                                    </button>
                                    <span className="text-purple-200">|</span>
                                    <button 
                                      onClick={() => {
                                        if (confirm("Voulez-vous générer les titres et descriptions pour TOUS les secteurs ? Cette opération peut prendre quelques minutes.")) {
                                          handleGenerateAllPacksContent();
                                        }
                                      }}
                                      disabled={isAiGeneratingPack || isAiGeneratingAllPacks || !aiPackPrompt.trim()}
                                      className="text-[10px] font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1 disabled:opacity-30 shrink-0"
                                      title="Générer les titres et descriptions pour TOUS les secteurs"
                                    >
                                      {isAiGeneratingAllPacks ? <RefreshCw className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                                      ACTUALISER TOUS LES PACKS
                                    </button>
                                  </div>
                                </div>
                                <input 
                                  type="text"
                                  value={appData.settings.sectorConfigs?.[adminSelectedSector]?.title || ''}
                                  onChange={(e) => {
                                    const configs = { ...(appData.settings.sectorConfigs || {}) };
                                    configs[adminSelectedSector] = { 
                                      ...(configs[adminSelectedSector] || { description: '' }), 
                                      title: e.target.value 
                                    };
                                    updateSettings({ ...appData.settings, sectorConfigs: configs });
                                  }}
                                  placeholder="Ex: L'Épopée Romaine"
                                  className="w-full p-3 bg-white border border-purple-200 rounded-xl font-bold focus:ring-2 focus:ring-purple-500/20 outline-none transition-all text-sm text-zinc-900 placeholder:text-zinc-400"
                                />
                              </div>
                              <div className="space-y-1">
                                <label className="text-[10px] font-bold text-purple-700 uppercase tracking-wider ml-1">Description Marketing</label>
                                <textarea 
                                  value={appData.settings.sectorConfigs?.[adminSelectedSector]?.description || ''}
                                  onChange={(e) => {
                                    const configs = { ...(appData.settings.sectorConfigs || {}) };
                                    configs[adminSelectedSector] = { 
                                      ...(configs[adminSelectedSector] || { title: '' }), 
                                      description: e.target.value 
                                    };
                                    updateSettings({ ...appData.settings, sectorConfigs: configs });
                                  }}
                                  placeholder="Décrivez l'expérience client pour ce secteur..."
                                  rows={4}
                                  className="w-full p-3 bg-white border border-purple-200 rounded-xl font-medium focus:ring-2 focus:ring-purple-500/20 outline-none transition-all resize-none text-sm text-zinc-900 placeholder:text-zinc-400"
                                />
                              </div>
                              <div className="space-y-1">
                                <label className="text-[10px] font-bold text-purple-700 uppercase tracking-wider ml-1">Image du Secteur</label>
                                <div className="flex items-center gap-4">
                                  <div className="w-24 h-16 bg-zinc-100 rounded-lg overflow-hidden border border-purple-100 flex-shrink-0">
                                    {appData.settings.sectorConfigs?.[adminSelectedSector]?.imageBase64 || appData.settings.sectorConfigs?.[adminSelectedSector]?.imageLastUpdate ? (
                                      <img 
                                        src={appData.settings.sectorConfigs?.[adminSelectedSector]?.imageBase64 || `/api/sector-image/${encodeURIComponent(adminSelectedSector)}?t=${appData.settings.sectorConfigs?.[adminSelectedSector]?.imageLastUpdate || Date.now()}`} 
                                        className="w-full h-full object-cover" 
                                        alt="Secteur" 
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center text-zinc-400">
                                        <ImageIcon className="w-6 h-6" />
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex-1">
                                    <input 
                                      type="file" 
                                      accept="image/*" 
                                      id={`sector-upload-${adminSelectedSector}`}
                                      className="hidden"
                                      onChange={(e) => handleSectorImageUpload(e, adminSelectedSector)}
                                    />
                                    <label 
                                      htmlFor={`sector-upload-${adminSelectedSector}`}
                                      className={cn(
                                        "inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold cursor-pointer transition-all",
                                        isUploadingSectorImage === adminSelectedSector
                                          ? "bg-zinc-100 text-zinc-400 cursor-not-allowed"
                                          : "bg-purple-100 text-purple-700 hover:bg-purple-200"
                                      )}
                                    >
                                      {isUploadingSectorImage === adminSelectedSector ? (
                                        <RefreshCw className="w-3 h-3 animate-spin" />
                                      ) : (
                                        <Upload className="w-3 h-3" />
                                      )}
                                      {isUploadingSectorImage === adminSelectedSector ? "Traitement..." : "Changer l'image"}
                                    </label>
                                    <p className="text-[10px] text-zinc-400 mt-1">Format paysage recommandé (1200x800)</p>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Liste des activités du secteur avec Checkboxes */}
                            <div className="space-y-3">
                              <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                                <h4 className="font-bold text-purple-900 text-sm">
                                  Sélection du Catalogue (Visible Client)
                                  <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-[10px] font-bold">
                                    {appData.sites
                                      .filter(s => s.sector === adminSelectedSector)
                                      .filter(s => s.name.toLowerCase().includes(adminSearchQuery.toLowerCase()))
                                      .length} fiches
                                  </span>
                                </h4>
                                <input
                                  type="text"
                                  placeholder="Rechercher une fiche..."
                                  value={adminSearchQuery}
                                  onChange={(e) => setAdminSearchQuery(e.target.value)}
                                  className="px-3 py-1.5 rounded-lg border border-purple-200 text-xs outline-none focus:border-purple-400 bg-white text-black placeholder:text-zinc-500"
                                />
                                  <div className="flex gap-2 items-center">
                                    <button 
                                      onClick={() => handleUpdateAllSites(adminSelectedSector)}
                                      disabled={isUpdatingAllSites}
                                      className="text-[10px] font-bold text-blue-600 hover:underline flex items-center gap-1 disabled:opacity-50"
                                    >
                                      {isUpdatingAllSites ? (
                                        <>
                                          <RefreshCw className="w-3 h-3 animate-spin" />
                                          MISE À JOUR {updateProgress ? `(${updateProgress.current}/${updateProgress.total})` : '...'}
                                        </>
                                      ) : (
                                        "ACTUALISER TOUT"
                                      )}
                                    </button>
                                  <span className="text-purple-300">|</span>
                                  <button 
                                    onClick={() => setShowDistanceMatrix(true)}
                                    className="text-[10px] font-bold text-blue-600 hover:underline flex items-center gap-1"
                                  >
                                    <Table className="w-3 h-3" />
                                    MATRICE DISTANCES
                                  </button>
                                  <span className="text-purple-300">|</span>
                                  <button 
                                    onClick={() => {
                                      const updated = appData.sites.map(s => s.sector === adminSelectedSector ? { ...s, isVisibleClient: true } : s);
                                      setAppData(prev => ({ ...prev, sites: updated }));
                                    }}
                                    className="text-[10px] font-bold text-purple-600 hover:underline"
                                  >
                                    TOUT COCHER
                                  </button>
                                  <span className="text-purple-300">|</span>
                                  <button 
                                    onClick={() => {
                                      const updated = appData.sites.map(s => s.sector === adminSelectedSector ? { ...s, isVisibleClient: false } : s);
                                      setAppData(prev => ({ ...prev, sites: updated }));
                                    }}
                                    className="text-[10px] font-bold text-purple-400 hover:underline"
                                  >
                                    TOUT DÉCOCHER
                                  </button>
                                </div>
                              </div>

                              <div className={cn(
                                "gap-4",
                                adminPackViewMode === 'grid' ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" : "flex flex-col"
                              )}>
                                {appData.sites
                                  .filter(s => s.sector === adminSelectedSector)
                                  .filter(s => s.name.toLowerCase().includes(adminSearchQuery.toLowerCase()))
                                  .sort((a, b) => a.name.localeCompare(b.name))
                                  .map(site => (
<SiteAdminCard 
  key={site.id} 
  site={site} 
  adminPackViewMode={adminPackViewMode}
  updatingSiteIds={updatingSiteIds}
  serverSiteIds={serverSiteIds}
  handleUpdateSingleSite={handleUpdateSingleSite}
  setSiteToDelete={setSiteToDelete}
  onSave={(updatedSite) => {
    handleUpdateSite(updatedSite, () => {
      alert(`Les modifications pour "${updatedSite.name}" ont été enregistrées.`);
    });
  }}
/>
                                ))}
                                {appData.sites.filter(s => s.sector === adminSelectedSector).length === 0 && (
                                  <div className="col-span-full py-8 text-center bg-white/50 rounded-xl border-2 border-dashed border-purple-200">
                                    <p className="text-purple-400 text-xs italic">Aucune activité enregistrée pour ce secteur.</p>
                                  </div>
                                )}
                              </div>

                              {/* Modal de confirmation de suppression */}
                              {siteToDelete && (
                                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
                                  <motion.div 
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 border border-red-100"
                                  >
                                    <div className="flex items-center gap-3 text-red-600 mb-4">
                                      <div className="p-2 bg-red-50 rounded-full">
                                        <AlertCircle className="w-6 h-6" />
                                      </div>
                                      <h3 className="text-lg font-bold">Confirmer la suppression</h3>
                                    </div>
                                    
                                    <p className="text-zinc-600 text-sm mb-6">
                                      Êtes-vous sûr de vouloir retirer <span className="font-bold text-zinc-900">"{appData.sites.find(s => s.id === siteToDelete)?.name}"</span> de ce secteur ? Cette action est irréversible.
                                    </p>
                                    
                                    <div className="flex gap-3 justify-end">
                                      <button 
                                        onClick={() => setSiteToDelete(null)}
                                        className="px-4 py-2 text-sm font-bold text-zinc-500 hover:bg-zinc-100 rounded-xl transition-colors"
                                      >
                                        ANNULER
                                      </button>
                                      <button 
                                        onClick={() => {
                                          const updated = appData.sites.map(s => s.id === siteToDelete ? { ...s, sector: undefined } : s);
                                          const finalAppData = { ...appData, sites: updated };
                                          setAppData(finalAppData);
                                          saveAppData(finalAppData).then(savedData => setAppData(savedData));
                                          setSiteToDelete(null);
                                        }}
                                        className="px-6 py-2 text-sm font-bold bg-red-600 text-white rounded-xl hover:bg-red-700 shadow-lg shadow-red-200 transition-all"
                                      >
                                        SUPPRIMER
                                      </button>
                                    </div>
                                  </motion.div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="pt-8 border-t border-zinc-100 flex flex-col gap-4">
                          <button 
                            onClick={handleSaveSettings}
                            disabled={isSavingSettings}
                            className="w-full py-6 bg-emerald-600 text-white rounded-2xl font-bold text-xl hover:bg-emerald-700 transition-all shadow-xl shadow-emerald-600/20 active:scale-[0.98] flex items-center justify-center gap-3 disabled:opacity-50"
                          >
                            {isSavingSettings ? (
                              <>
                                <RefreshCw className="w-7 h-7 animate-spin" />
                                ENREGISTREMENT EN COURS...
                              </>
                            ) : (
                              <>
                                <CheckCircle className="w-7 h-7" />
                                ENREGISTRER TOUS LES SECTEURS & LA BIBLIOTHÈQUE
                              </>
                            )}
                          </button>
                          <p className="text-center text-[10px] text-zinc-400 font-medium uppercase tracking-widest">
                            Ce bouton enregistre l'intégralité de la bibliothèque pour tous les secteurs
                          </p>
                        </div>
                    </section>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {view === 'contact' && (
            <motion.div 
              key="contact"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="max-w-4xl mx-auto space-y-12"
            >
              <div className="text-center space-y-4">
                <h2 className="text-5xl font-serif italic text-zinc-900">Contact & Informations</h2>
                <p className="text-zinc-500 max-w-lg mx-auto">Tout ce qu'il faut savoir sur votre partenaire de transport Cévenol.</p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {/* Company Info Card */}
                <div className="bg-white p-10 rounded-[40px] border border-zinc-100 shadow-xl space-y-8">
                  <div className="flex items-center gap-4">
                    <div className="bg-emerald-500 p-4 rounded-2xl shadow-lg shadow-emerald-100">
                      <Car className="text-white w-8 h-8" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-vtc font-bold text-zinc-900">{appData.settings.companyName}</h3>
                      <p className="text-emerald-600 font-medium">Transport Privé de Personnes</p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-zinc-50 p-2 rounded-xl mt-1">
                        <MapPin className="w-5 h-5 text-zinc-400" />
                      </div>
                      <div>
                        <p className="text-xs text-zinc-400 uppercase font-bold tracking-wider mb-1">Siège Social</p>
                        <p className="text-zinc-700 font-medium">{appData.settings.baseAddress}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="bg-zinc-50 p-2 rounded-xl mt-1">
                        <Phone className="w-5 h-5 text-zinc-400" />
                      </div>
                      <div>
                        <p className="text-xs text-zinc-400 uppercase font-bold tracking-wider mb-1">Téléphone</p>
                        <p className="text-zinc-700 font-medium">{appData.settings.phone}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="bg-zinc-50 p-2 rounded-xl mt-1">
                        <Mail className="w-5 h-5 text-zinc-400" />
                      </div>
                      <div>
                        <p className="text-xs text-zinc-400 uppercase font-bold tracking-wider mb-1">Email</p>
                        <p className="text-zinc-700 font-medium">{appData.settings.email}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="bg-zinc-50 p-2 rounded-xl mt-1">
                        <FileText className="w-5 h-5 text-zinc-400" />
                      </div>
                      <div>
                        <p className="text-xs text-zinc-400 uppercase font-bold tracking-wider mb-1">SIRET</p>
                        <p className="text-zinc-700 font-medium">{appData.settings.siret}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Vehicle & Driver Card */}
                <div className="bg-zinc-900 p-10 rounded-[40px] text-white shadow-2xl space-y-8 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl -mr-32 -mt-32" />
                  
                  <div className="relative z-10 space-y-8">
                    <div className="flex items-center gap-4">
                      <div className="bg-white/10 backdrop-blur p-4 rounded-2xl border border-white/10">
                        <UserIcon className="text-emerald-400 w-8 h-8" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-display font-bold">Votre Chauffeur</h3>
                        <p className="text-emerald-400/80 font-medium">Gerald</p>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div className="bg-white/5 p-6 rounded-3xl border border-white/5">
                        <div className="flex items-center gap-3 mb-3">
                          <Car className="w-5 h-5 text-emerald-400" />
                          <p className="text-xs text-white/40 uppercase font-bold tracking-wider">Véhicule</p>
                        </div>
                        <p className="text-lg font-medium">{appData.settings.vehicle}</p>
                        <p className="text-sm text-white/60 mt-1">1 chauffeur, 6 places passagers</p>
                      </div>

                      <div className="bg-white/5 p-6 rounded-3xl border border-white/5">
                        <div className="flex items-center gap-3 mb-3">
                          <ShieldCheck className="w-5 h-5 text-emerald-400" />
                          <p className="text-xs text-white/40 uppercase font-bold tracking-wider">Expérience</p>
                        </div>
                        <p className="text-lg font-medium">{appData.settings.driverExperience}</p>
                        <p className="text-sm text-white/60 mt-1">Conduite sécurisée et fluide</p>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4">
                      <button 
                        onClick={() => {
                          if (activeBooking) {
                            setShowChat(true);
                          } else {
                            // If no active booking, we can't open chat easily without a context
                            // But we can at least show a message or redirect to booking
                            setView('vtc');
                          }
                        }}
                        className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white py-4 rounded-2xl font-bold transition-all shadow-lg shadow-emerald-500/20 flex items-center justify-center gap-3"
                      >
                        <MessageSquare className="w-5 h-5" />
                        Contacter par Chat
                      </button>
                      <a 
                        href={`tel:${appData.settings.phone.replace(/\s/g, '')}`}
                        className="flex-1 bg-zinc-900 hover:bg-zinc-800 text-white py-4 rounded-2xl font-bold transition-all shadow-lg flex items-center justify-center gap-3"
                      >
                        <Phone className="w-5 h-5" />
                        Appeler le chauffeur
                      </a>
                    </div>
                    <button 
                      onClick={() => setView('vtc')}
                      className="w-full bg-white border-2 border-emerald-500 text-emerald-600 hover:bg-emerald-50 py-4 rounded-2xl font-bold transition-all"
                    >
                      Réserver maintenant
                    </button>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <NavButton 
                  onClick={() => setView('home')} 
                  type="secondary"
                  icon={ChevronLeft}
                  label="Retour à l'accueil"
                  className="mx-auto"
                />
              </div>
            </motion.div>
          )}

          {view === 'legal' && (
            <motion.div
              key="legal"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white p-8 rounded-3xl border border-zinc-200 space-y-6"
            >
              <h2 className="text-2xl font-bold">Mentions Légales</h2>
              <div className="prose prose-zinc max-w-none whitespace-pre-wrap text-zinc-600">
                {appData.settings.legalMentions}
              </div>
              <NavButton 
                onClick={() => setView('home')} 
                type="primary"
                label="Retour"
                className="w-full"
              />
            </motion.div>
          )}

          {view === 'cgv' && (
            <motion.div
              key="cgv"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white p-8 rounded-3xl border border-zinc-200 space-y-6"
            >
              <h2 className="text-2xl font-bold">Conditions Générales de Vente</h2>
              <div className="prose prose-zinc max-w-none whitespace-pre-wrap text-zinc-600">
                {appData.settings.cgv}
              </div>
              <NavButton 
                onClick={() => setView('home')} 
                type="primary"
                label="Retour"
                className="w-full"
              />
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Phone Number Modal for Google Users */}
      <AnimatePresence>
        {showPhoneModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="absolute inset-0 bg-zinc-900/60 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="relative bg-white rounded-3xl shadow-2xl p-8 max-w-sm w-full space-y-6"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold">Informations manquantes</h3>
                <p className="text-zinc-500 text-sm mt-2">Nous avons besoin de ces informations pour confirmer vos réservations.</p>
              </div>
              <form onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const phone = formData.get('phone') as string;
                const email = formData.get('email') as string;
                if (user) {
                  const updated = { ...user, phone: phone || user.phone, email: email || user.email };
                  await saveUser(updated);
                  setShowPhoneModal(false);
                }
              }} className="space-y-4">
                {!user?.phone && (
                  <input 
                    name="phone"
                    type="tel" 
                    placeholder="Téléphone (ex: 06...)"
                    required
                    className="w-full p-4 bg-zinc-50 border border-zinc-200 rounded-2xl text-center text-lg font-bold"
                  />
                )}
                {!user?.email && (
                  <input 
                    name="email"
                    type="email" 
                    placeholder="Email"
                    required
                    className="w-full p-4 bg-zinc-50 border border-zinc-200 rounded-2xl text-center text-lg font-bold"
                  />
                )}
                <button type="submit" className="w-full py-4 bg-emerald-600 text-white rounded-2xl font-bold hover:bg-emerald-700 transition-all">
                  Valider
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

        {showCancelConfirm && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-[32px] p-8 max-w-sm w-full shadow-2xl space-y-6"
            >
              <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <div className="text-center space-y-2">
                <h3 className="text-xl font-bold text-zinc-900">Annuler la course ?</h3>
                <p className="text-zinc-500 text-sm">
                  Êtes-vous sûr de vouloir annuler cette course ? Cette action est irréversible.
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <button 
                  onClick={() => {
                    handleCancelBooking();
                    setShowCancelConfirm(false);
                  }}
                  className="w-full py-4 bg-red-600 text-white rounded-2xl font-bold hover:bg-red-700 transition-all shadow-lg"
                >
                  Oui, annuler
                </button>
                <button 
                  onClick={() => setShowCancelConfirm(false)}
                  className="w-full py-4 bg-zinc-100 text-zinc-600 rounded-2xl font-bold hover:bg-zinc-200 transition-all"
                >
                  Non, garder la course
                </button>
              </div>
            </motion.div>
          </div>
        )}

      <AnimatePresence mode="wait">
        {viewingActivityDetail && (
          <ActivityDetailModal 
            activity={viewingActivityDetail} 
            onClose={() => setViewingActivityDetail(null)} 
          />
        )}
      </AnimatePresence>

      {activeBooking && activeBooking.type === 'custom' && activeBooking.proposal && (
        <div className="fixed bottom-24 right-6 z-[60] flex flex-col gap-2">
          <button 
            onClick={() => setView('status')}
            className="bg-emerald-600 text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-transform flex items-center gap-2"
          >
            <Navigation className="w-6 h-6" />
            <span className="font-bold pr-2">Suivre mon circuit</span>
          </button>
        </div>
      )}

      <AnimatePresence>
        {saveSettingsSuccess && (
          <motion.div 
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-emerald-600 text-white px-8 py-4 rounded-2xl shadow-2xl font-bold flex items-center gap-3 z-[200] border-2 border-white/20 backdrop-blur-md"
          >
            <CheckCircle className="w-6 h-6" />
            Modifications sauvegardées avec succès
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Détails Activité */}
      <AnimatePresence>
        {selectedActivityDetail && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedActivityDetail(null)}
              className="absolute inset-0 bg-zinc-900/60 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-lg bg-white rounded-[32px] overflow-hidden shadow-2xl"
            >
              <div className="h-64 relative">
                {selectedActivityDetail.image && (
                  <img 
                    src={selectedActivityDetail.image} 
                    alt={selectedActivityDetail.label}
                    className="w-full h-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                )}
                <button 
                  onClick={() => setSelectedActivityDetail(null)}
                  className="absolute top-4 right-4 w-10 h-10 bg-black/20 hover:bg-black/40 backdrop-blur-md text-white rounded-full flex items-center justify-center transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="flex items-center gap-2 mb-2">
                    {selectedActivityDetail.familyFriendly && (
                      <span className="px-3 py-1 bg-amber-500 text-white text-[10px] font-bold rounded-full uppercase tracking-wider">
                        Idéal Famille
                      </span>
                    )}
                    {selectedActivityDetail.googleRating && (
                      <div className="flex items-center gap-1 bg-white/20 backdrop-blur-md px-2 py-1 rounded-lg text-white">
                        <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                        <span className="text-xs font-bold">{selectedActivityDetail.googleRating}</span>
                      </div>
                    )}
                    {selectedActivityDetail.isHandicapAccessible && (
                      <span className="bg-blue-500 text-white text-[10px] px-2 py-1 rounded-full font-bold flex items-center gap-1 shadow-sm">
                        ♿ PMR
                      </span>
                    )}
                  </div>
                  <h3 className="text-2xl font-display font-bold text-white">{selectedActivityDetail.label}</h3>
                </div>
              </div>

              <div className="p-8 space-y-6 overflow-y-auto max-h-[60vh]">
                <div className="space-y-4">
                  <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-widest">Présentation</h4>
                  <p className="text-zinc-600 leading-relaxed">
                    {selectedActivityDetail.longDescription || selectedActivityDetail.description}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-widest">Tarifs indicatifs</h4>
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-zinc-500">Adulte</span>
                        <span className="font-bold text-zinc-900">{selectedActivityDetail.priceAdult}€</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-zinc-500">Enfant (-12 ans)</span>
                        <span className="font-bold text-zinc-900">{selectedActivityDetail.priceChild}€</span>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-widest">Horaires</h4>
                    <p className="text-sm text-zinc-600">
                      {selectedActivityDetail.openingHours || "Consulter le site officiel pour les horaires saisonniers."}
                    </p>
                  </div>
                </div>

                <div className="pt-6 border-t border-zinc-100">
                  <button 
                    onClick={() => setSelectedActivityDetail(null)}
                    className="w-full py-4 bg-emerald-600 text-white rounded-2xl font-bold hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-600/20"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>






      {/* Modal Détails Client */}
      <AnimatePresence>
        {selectedCustomer && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md overflow-y-auto">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-[40px] p-6 sm:p-10 max-w-2xl w-full shadow-2xl space-y-8 my-8 relative"
            >
              <button 
                onClick={() => setSelectedCustomer(null)}
                className="absolute top-6 right-6 p-3 hover:bg-zinc-100 rounded-2xl transition-colors border border-zinc-100"
              >
                <X className="w-6 h-6 text-zinc-400" />
              </button>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                <div className="w-20 h-20 rounded-3xl bg-emerald-100 flex items-center justify-center text-emerald-600 shrink-0">
                  <UserIcon className="w-10 h-10" />
                </div>
                <div>
                  <h3 className="text-3xl font-black text-zinc-900 leading-tight">
                    {selectedCustomer.firstName} {selectedCustomer.lastName}
                  </h3>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="px-3 py-1 bg-zinc-100 text-zinc-500 rounded-full text-[10px] font-bold uppercase tracking-wider border border-zinc-200">
                      ID: {selectedCustomer.id}
                    </span>
                    <span className={cn(
                      "px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border",
                      selectedCustomer.provider === 'google' ? "bg-blue-50 text-blue-600 border-blue-100" : "bg-zinc-50 text-zinc-600 border-zinc-100"
                    )}>
                      Compte {selectedCustomer.provider}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="space-y-2">
                    <h4 className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">Coordonnées</h4>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 text-zinc-600">
                        <Mail className="w-4 h-4 text-emerald-500" />
                        <span className="text-sm font-medium">{selectedCustomer.email || 'Non renseigné'}</span>
                      </div>
                      <div className="flex items-center gap-3 text-zinc-600">
                        <Phone className="w-4 h-4 text-emerald-500" />
                        <span className="text-sm font-medium font-mono">{selectedCustomer.phone || 'Non renseigné'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <h4 className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">Adresse de facturation</h4>
                    <div className="flex items-start gap-3 text-zinc-600">
                      <MapPin className="w-4 h-4 text-emerald-500 mt-1 shrink-0" />
                      <span className="text-sm leading-relaxed">{selectedCustomer.address || 'Aucune adresse enregistrée'}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">Historique d'achats</h4>
                  <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                    {appData.bookings.filter(b => b.customerId === selectedCustomer.id).length > 0 ? (
                      appData.bookings
                        .filter(b => b.customerId === selectedCustomer.id)
                        .sort((a, b) => new Date(b.completedAt || b.timestamp).getTime() - new Date(a.completedAt || a.timestamp).getTime())
                        .map(booking => (
                          <div key={booking.id} className="p-4 rounded-2xl bg-zinc-50 border border-zinc-100 space-y-2 hover:border-emerald-200 transition-colors">
                            <div className="flex justify-between items-start">
                              <span className="text-[10px] font-bold text-zinc-400 uppercase">
                                {new Date(booking.completedAt || booking.timestamp).toLocaleDateString('fr-FR')}
                              </span>
                              <span className={cn(
                                "px-2 py-0.5 rounded-full text-[8px] font-bold uppercase",
                                booking.status === 'completed' || booking.paymentStatus === 'paid' ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                              )}>
                                {booking.status === 'completed' || booking.paymentStatus === 'paid' ? 'Payé' : 'En attente'}
                              </span>
                            </div>
                            <div className="text-xs font-bold text-zinc-900 truncate">
                              {booking.type === 'tour' ? `Circuit: ${booking.tourDetails?.packTitle || 'Sans titre'}` : 
                               booking.type === 'vtc' ? `Trajet: ${booking.pickup?.name || 'VTC'}` : 
                               'Sur Mesure'}
                            </div>
                            <div className="flex justify-between items-center pt-1">
                              <span className="text-[10px] text-zinc-500">#{booking.id.slice(-6).toUpperCase()}</span>
                              <span className="text-sm font-black text-emerald-600">{(booking.price || 0).toFixed(2)} €</span>
                            </div>
                          </div>
                        ))
                    ) : (
                      <div className="py-8 text-center text-zinc-400 text-xs italic bg-zinc-50 rounded-2xl border border-dashed border-zinc-200">
                        Aucune commande trouvée.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="pt-8 border-t border-zinc-100 flex gap-4">
                <button 
                  onClick={() => setSelectedCustomer(null)}
                  className="flex-1 py-4 bg-zinc-900 text-white rounded-2xl font-bold hover:bg-zinc-800 transition-all shadow-xl shadow-zinc-900/10"
                >
                  FERMER LA FICHE
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal Matrice de Distance */}
      <AnimatePresence>
        {showDistanceMatrix && (
          <div className="fixed inset-0 z-[100] flex items-start justify-center p-4 bg-black/60 backdrop-blur-md overflow-y-auto">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="bg-white rounded-[40px] p-8 max-w-6xl w-full shadow-2xl space-y-6 my-8"
            >
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-2xl font-bold text-zinc-900">Matrice de Distance - {adminSelectedSector}</h3>
                  <p className="text-zinc-500 text-sm">Temps de trajet par la route (OSRM / OpenStreetMap) ou estimés (Algorithme)</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right hidden sm:block">
                    <p className="text-[10px] font-black text-blue-600 uppercase tracking-widest">Données Réelles</p>
                    <p className="text-[9px] text-zinc-400 italic">Via OSRM (Itinéraire routier)</p>
                  </div>
                  <button 
                    onClick={() => handleUpdateDistanceMatrix(adminSelectedSector)}
                    disabled={isUpdatingMatrix}
                    className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-2xl font-bold text-sm hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 disabled:opacity-50 active:scale-95"
                  >
                    {isUpdatingMatrix ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        CALCUL EN COURS...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4" />
                        METTRE À JOUR LA MATRICE (OSRM)
                      </>
                    )}
                  </button>
                  <button 
                    onClick={() => setShowDistanceMatrix(false)}
                    className="p-3 hover:bg-zinc-100 rounded-2xl transition-colors border border-zinc-100"
                  >
                    <X className="w-6 h-6 text-zinc-400" />
                  </button>
                </div>
              </div>

              <div className="bg-blue-50/50 border border-blue-100 rounded-2xl p-4 flex gap-3 items-start">
                <Info className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                <div className="text-xs text-blue-700 leading-relaxed">
                  <p className="font-bold mb-1">Comment ça marche ?</p>
                  <p>Le bouton <strong>"METTRE À JOUR LA MATRICE"</strong> interroge les serveurs Open Source (OSRM) pour obtenir les distances et temps de trajet réels par la route en une seule fois (très rapide et gratuit). Les valeurs en <span className="text-blue-600 font-bold underline">bleu</span> sont des données réelles enregistrées. Les valeurs en noir sont des estimations algorithmiques (utilisées si aucune donnée réelle n'est disponible).</p>
                </div>
              </div>

              <div className="overflow-auto max-h-[60vh] border border-zinc-100 rounded-[32px] shadow-inner bg-zinc-50/30">
                <table className="w-full text-left border-collapse">
                  <thead className="sticky top-0 z-20 shadow-sm">
                    <tr className="bg-zinc-50 border-b">
                      <th className="p-4 text-[10px] font-black text-zinc-400 uppercase tracking-widest sticky left-0 top-0 bg-zinc-50 z-30 border-r min-w-[150px]">Site A \ Site B</th>
                      {appData.sites.filter(s => s.sector === adminSelectedSector).map(site => (
                        <th key={site.id} className="p-4 text-[10px] font-black text-zinc-400 uppercase tracking-widest min-w-[120px] text-center border-r sticky top-0 bg-zinc-50 z-20">
                          {site.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {appData.sites.filter(s => s.sector === adminSelectedSector).map(siteA => (
                      <tr key={siteA.id} className="border-b hover:bg-zinc-50/50 transition-colors">
                        <td className="p-4 font-bold text-zinc-900 text-xs sticky left-0 bg-white z-10 border-r shadow-[2px_0_5px_rgba(0,0,0,0.02)]">
                          {siteA.name}
                        </td>
                        {appData.sites.filter(s => s.sector === adminSelectedSector).map(siteB => {
                          if (siteA.id === siteB.id) return <td key={siteB.id} className="p-4 bg-zinc-100/50 text-center text-zinc-300 font-bold border-r">-</td>;
                          
                          const pairKey = [siteA.id, siteB.id].sort().join('_');
                          const realData = appData.settings.distanceMatrices?.[pairKey];
                          
                          const dist = realData ? realData.distance : getDistance(siteA.location, siteB.location);
                          const time = realData ? realData.duration : getTravelTime(dist);
                          
                          return (
                            <td key={siteB.id} className="p-4 text-center border-r">
                              <div className="flex flex-col">
                                <span className={cn("text-xs font-bold", realData ? "text-blue-600" : "text-zinc-900")}>
                                  {time} min
                                </span>
                                <span className={cn("text-[10px]", realData ? "text-blue-400" : "text-zinc-400")}>{Math.round(dist)} km</span>
                                {realData && <span className="text-[8px] text-blue-400 font-black uppercase tracking-tighter">Réel</span>}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end pt-4 gap-4">
                <button 
                  onClick={async () => {
                    const savedData = await saveAppData(appData);
                    setAppData(savedData);
                    alert("Matrice enregistrée !");
                  }}
                  className="px-8 py-4 bg-emerald-600 text-white rounded-2xl font-bold hover:bg-emerald-700 transition-all shadow-xl"
                >
                  ENREGISTRER
                </button>
                <button 
                  onClick={() => setShowDistanceMatrix(false)}
                  className="px-8 py-4 bg-zinc-900 text-white rounded-2xl font-bold hover:bg-zinc-800 transition-all shadow-xl"
                >
                  FERMER
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal de Validation Image IA */}
      {pendingImageSiteId && siteImagePreview && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-[40px] p-8 max-w-lg w-full shadow-2xl space-y-6"
          >
            <div className="text-center space-y-2">
              <h3 className="text-2xl font-bold">Valider l'image générée ?</h3>
              <p className="text-zinc-500">Souhaitez-vous utiliser cette image pour le site touristique ?</p>
            </div>
            
            <div className="aspect-[4/3] rounded-3xl overflow-hidden border-4 border-emerald-100 shadow-inner">
              {siteImagePreview && <img src={siteImagePreview} alt="Preview" className="w-full h-full object-cover" />}
            </div>

            <div className="flex gap-4">
              <button 
                onClick={() => {
                  setPendingImageSiteId(null);
                  setSiteImagePreview(null);
                }}
                className="flex-1 py-4 bg-zinc-100 text-zinc-600 rounded-2xl font-bold hover:bg-zinc-200 transition-all"
              >
                ANNULER
              </button>
              <button 
                onClick={confirmSiteImage}
                className="flex-1 py-4 bg-emerald-600 text-white rounded-2xl font-bold hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-600/20"
              >
                VALIDER
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* {viewingWebsite && (
        <WebsiteViewerModal url={viewingWebsite} onClose={() => setViewingWebsite(null)} />
      )} */}

      {editingSite && (
        <SiteEditModal 
          site={editingSite} 
          serverSiteIds={serverSiteIds}
          onSave={handleUpdateSite} 
          onClose={() => setEditingSite(null)} 
        />
      )}

      {conflictData && (
        <ConflictModal 
          serverItem={conflictData.serverItem}
          localItem={conflictData.localItem}
          onResolve={conflictData.onResolve}
        />
      )}
    </div>
  </APIProvider>
);
}
